import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './index.css';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';
import logger from './utils/logger';
import AIAnalyticsPanel from './components/AIAnalyticsPanel';
import { TrendsChart, BreakdownChart } from './components/EnterpriseCharts';

// --- 1. FIREBASE IMPORTS ---
// Auth Functions
import { 
  signInWithEmailAndPassword, 
  onAuthStateChanged, 
  signOut,
  sendPasswordResetEmail 
} from "firebase/auth";

// Database Functions
import { 
  collection, 
  getDocs, 
  getDoc,
  setDoc,
  addDoc, 
  deleteDoc, 
  updateDoc, 
  doc,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  Timestamp
} from 'firebase/firestore';

// NEW: Storage Functions for PDF Uploads
import { 
  ref, 
  uploadBytes, 
  getDownloadURL,
  deleteObject,
  getBlob 
} from "firebase/storage";

// The Local Services (Auth + DB + Cloud Functions)
import { auth, db, storage, scanBillFunction, analyzeUtilityUsageFunction, getMonthlyStatsFunction, applyAutoCorrectionsFunction, runDuplicateAuditFunction, exportBillDataFunction, getAnalysisDataFunction, scanForCorrectionsFunction, rebuildAllSiteNamesFunction, removeFacilityFunction } from './firebase';

// --- UTILITIES ---
import { updateDuplicateFingerprint, removeDuplicateFingerprint } from './utils/duplicateFingerprints';
import { useSessionTracker } from './hooks/useSessionTracker';

// --- 2. ICONS (Merged all into one list) ---
import { 
  // Auth Icons
  Menu,
  Lock, 
  Mail, 
  LogOut,
  Settings,
  // App Icons
  Home,
  LayoutDashboard, 
  UploadCloud, 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  Zap, 
  Droplets, 
  Flame, 
  Edit2, 
  Save, 
  X, 
  ArrowLeft,
  ChevronRight, 
  Activity, 
  Search,
  Trash2,
  Code,
  ScanLine,
  Bot,
  Play,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Eye,
  ArchiveRestore,
  Building2,
  Filter,
  Calendar,
  TrendingUp,
  ArrowUpDown,
  Download,
  Moon,
  Sun,
  ChevronDown,
  Sparkles,
  Thermometer,
  MapPin,
  CloudRain,
} from 'lucide-react';

import LocationManager from './components/LocationManager';
import IgnoredCorrectionsManager from './components/IgnoredCorrectionsManager';
import ArchiveViewer from './components/ArchiveViewer';
import BentoReportExporter from './components/BentoReportExporter';
import InsightsSidebar from './components/InsightsSidebar';
import EnterpriseAnalytics from './components/EnterpriseAnalytics';
import { useOperationRateLimiter } from './hooks/useOperationRateLimiter';

// --- 3. CHARTS ---
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';


// --- APP VERSION ---
const APP_VERSION = '1.2.1';

// --- GEMINI AI INTEGRATION ---
// API key is now securely stored in Cloud Functions (GEMINI_API_KEY secret)
// Use scanBillFunction and analyzeUtilityUsageFunction for AI features

// --- HELPER: Generate search tokens for a bill ---
const generateSearchTokens = (bill) => {
  const tokens = new Set();
  // Add all words from site_name (not just first word)
  if (bill.site_name) {
    bill.site_name.toLowerCase().split(/\s+/).forEach(word => {
      if (word.length > 1) tokens.add(word);
    });
  }
  // Add site_address words for better searchability
  if (bill.site_address) {
    bill.site_address.toLowerCase().split(/[\s,]+/).forEach(word => {
      if (word.length > 1) tokens.add(word);
    });
  }
  if (bill.provider) tokens.add(bill.provider.toLowerCase().split(/\s+/)[0]);
  if (bill.uploaded_by) tokens.add(bill.uploaded_by.split('@')[0].toLowerCase());
  if (bill.type) tokens.add(bill.type.toLowerCase());
  if (bill.date) tokens.add(bill.date.split('-')[0]); // year
  return Array.from(tokens).filter(Boolean);
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result.split(',')[1]; // Remove metadata header
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

// --- FILE COMPRESSION FOR UPLOADS ---
const compressImageFile = async (file, maxWidth = 2000, maxHeight = 2000, quality = 0.85) => {
  if (!file.type.startsWith('image/')) {
    return file; // Not an image, return as is
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
    };
  });
};

// --- PDF COMPRESSION FUNCTION ---
const compressPDF = async (file) => {
  if (!file.type.includes('pdf')) {
    return file; // Not a PDF, return as is
  }

  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    
    // Get all pages and compress embedded images
    const pages = pdfDoc.getPages();
    for (const page of pages) {
      const resources = page.node.Resources;
      if (resources && resources.has('XObject')) {
        const xObjects = resources.get('XObject');
        if (xObjects) {
          const xObjectsDict = xObjects.asDict();
          for (const [_key, value] of xObjectsDict.entries()) {
            const xObj = value.asDict ? value.asDict() : value;
            // Mark for compression by reducing quality if it's an image
            if (xObj && xObj.has('Subtype') && xObj.get('Subtype').toString().includes('Image')) {
              // pdf-lib handles compression through the save options
            }
          }
        }
      }
    }

    // Save with compression enabled
    const compressedBytes = await pdfDoc.save({ useObjectStreams: true });
    const compressedBlob = new Blob([compressedBytes], { type: 'application/pdf' });
    
    // Log compression ratio
    const ratio = ((1 - compressedBlob.size / file.size) * 100).toFixed(1);
    logger.log(`PDF compressed: ${file.size} → ${compressedBlob.size} bytes (${ratio}% reduction)`);
    
    // Only use compressed version if it saved significant space
    if (compressedBlob.size < file.size * 0.95) {
      return new File([compressedBlob], file.name, { type: 'application/pdf' });
    }
    return file;
  } catch (error) {
    logger.warn('PDF compression failed, using original:', error);
    return file; // Fallback to original
  }
};

const performGenAIScan = async (file, customPrompt) => {
  // 1. Convert Image/PDF
  let base64Data = null;
  
  if (file.name.includes("sample") && file.type === 'application/pdf') {
     return mockOCRProcess(file, customPrompt);
  }

  try {
     base64Data = await fileToBase64(file);
  } catch (e) {
     throw new Error("Failed to read file data.");
  }

  // 2. Construct Prompt for MULTI-UTILITY
  // NOTE: systemInstruction kept for reference; actual prompt is sent via Cloud Function
  const _systemInstruction = `
    You are a specialized Utility Bill OCR API. 
    Analyze the document (Image or PDF). It may contain one or multiple utility services.
    
    CRITICAL RULE FOR GAS BILLS:
    The user requires all Natural Gas usage to be standardized to CCF (100 cubic feet).
    You must perform the conversion calculation based on the unit found on the bill:
    - If Therms: Value / 1.037 = CCF
    - If MCF: Value * 10 = CCF
    - If Dth (Dekatherms): Value * 9.64 = CCF
    - If CCF: Value * 1 = CCF
    
    REQUIRED JSON STRUCTURE:
    {
      "utilities": [
        {
          "type": "electric" | "gas" | "water" | "sewer" | "trash",
          "fields": {
            "facility_name": { "value": "string", "confidence": 0.0-1.0, "bbox": { ... } },
            "utility_provider": { "value": "string", "confidence": 0.0-1.0, "bbox": { ... } },
            "bill_date": { "value": "YYYY-MM-DD", "confidence": 0.0-1.0, "bbox": { ... } },
            "total_amount": { "value": "number", "confidence": 0.0-1.0, "bbox": { ... } },
            
            "usage_total": { 
               "value": "number (THE CALCULATED CCF VALUE if Gas)", 
               "unit": "string (Always 'CCF' if Gas)", 
               "confidence": 0.0-1.0, 
               "bbox": { ... } 
            },
            
            "site_address": { "value": "string", "confidence": 0.0-1.0, "bbox": { ... } },
            "invoice_number": { "value": "string", "confidence": 0.0-1.0, "bbox": { ... } }
          }
        }
      ]
    }

    Rules:
    1. "facility_name": Extract specific name (e.g., "Warehouse B").
    2. If separate charges exist for Gas/Electric, create TWO objects in "utilities".
    3. For 'bbox', estimate location (0-100).
    4. If Gas, ensure 'usage_total.value' is the converted CCF number and 'usage_total.unit' is "CCF".
    
    USER CUSTOM INSTRUCTION: ${customPrompt}
  `;

  // 3. Call Cloud Function (API key is secure on server)
  try {
    const result = await scanBillFunction({
      imageBase64: base64Data,
      mimeType: file.type,
      customPrompt: customPrompt
    });

    return {
      ...result.data,
      raw_text: "AI Analysis Complete",
      used_prompt: customPrompt
    };

  } catch (error) {
    logger.error("Scan failed", error);
    // Extract error message from Firebase callable error
    const errorMessage = error.message || error.details || 'Failed to scan bill';
    return {
      error: errorMessage,
      utilities: [],
      raw_text: `Error: ${errorMessage}`
    };
  }
};

// Mock for the "Sample" button
const mockOCRProcess = async (_file, _prompt) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Simulate a combined bill (Electric + Gas)
      resolve({
        utilities: [
          {
            type: "electric",
            fields: {
              facility_name: { value: "North Warehouse", confidence: 0.95, bbox: { x: 10, y: 15, w: 30, h: 5 } },
              utility_provider: { value: "Sample Energy Co", confidence: 0.99, bbox: { x: 10, y: 5, w: 30, h: 10 } },
              bill_date: { value: "2023-10-12", confidence: 0.95, bbox: { x: 70, y: 5, w: 20, h: 5 } },
              total_amount: { value: "320.50", confidence: 0.98, bbox: { x: 65, y: 85, w: 25, h: 10 } },
              usage_total: { value: "850", unit: "kWh", confidence: 0.88, bbox: { x: 10, y: 40, w: 40, h: 15 } },
              site_address: { value: "123 Sample St, NY", confidence: 0.90, bbox: { x: 10, y: 20, w: 40, h: 10 } },
              invoice_number: { value: "INV-998877", confidence: 0.92, bbox: { x: 70, y: 10, w: 20, h: 5 } }
            }
          },
          {
            type: "gas",
            fields: {
              facility_name: { value: "North Warehouse", confidence: 0.95, bbox: { x: 10, y: 15, w: 30, h: 5 } },
              utility_provider: { value: "Sample Energy Co", confidence: 0.99, bbox: { x: 10, y: 5, w: 30, h: 10 } },
              bill_date: { value: "2023-10-12", confidence: 0.95, bbox: { x: 70, y: 5, w: 20, h: 5 } },
              total_amount: { value: "45.10", confidence: 0.85, bbox: { x: 65, y: 90, w: 25, h: 10 } },
              usage_total: { value: "32", unit: "therms", confidence: 0.75, bbox: { x: 10, y: 60, w: 40, h: 15 } },
              site_address: { value: "123 Sample St, NY", confidence: 0.90, bbox: { x: 10, y: 20, w: 40, h: 10 } },
              invoice_number: { value: "INV-998877", confidence: 0.92, bbox: { x: 70, y: 10, w: 20, h: 5 } }
            }
          }
        ],
        raw_text: "Sample Data Loaded"
      });
    }, 1500);
  });
};


// --- HELPER FUNCTIONS ---

// Compress image for PDF embedding
const compressImage = (img, quality = 0.7) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  // Set canvas size to match image
  canvas.width = img.width;
  canvas.height = img.height;
  
  // Fill with white background and draw image
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  
  // Convert to data URL with compression
  return canvas.toDataURL('image/jpeg', quality);
};

// --- AI UTILITY ANOMALY ANALYSIS COMPONENT ---
const UtilityAnomalyAnalysis = ({ 
  isOpen, 
  onClose, 
  darkMode, 
  approvedLocations, 
  monthlyStats, 
  _bills,
  sessionTracker,
  appSettings
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisDataSummary, setAnalysisDataSummary] = useState(null);
  const [showDataSummary, setShowDataSummary] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [facilityNames, setFacilityNames] = useState([]);

  // Load facility names from server on open (lightweight)
  useEffect(() => {
    if (isOpen && facilityNames.length === 0) {
      // Use approvedLocations if already loaded, otherwise server will provide them
      const names = approvedLocations.filter(loc => loc.address && loc.name);
      setFacilityNames(names);
    }
  }, [isOpen, approvedLocations, facilityNames.length]);

  // Get facilities with addresses (from local approvedLocations or server-provided names)
  const facilitiesWithData = useMemo(() => {
    return facilityNames.length > 0 ? facilityNames : approvedLocations.filter(loc => loc.address && loc.name);
  }, [facilityNames, approvedLocations]);

  // --- Weather Helpers ---
  const getCoordinates = async (address) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`);
      const data = await response.json();
      if (data && data.length > 0) {
        return { lat: data[0].lat, lon: data[0].lon };
      }
    } catch (err) {
      logger.warn('Geocoding failed for address:', address, err);
    }
    return null;
  };

  const getHistoricalWeatherHDDCDD = async (lat, lon, startDate, endDate) => {
    try {
      const start = `${startDate}-01`;
      const end = `${endDate}-28`;
      const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${start}&end_date=${end}&daily=temperature_2m_mean&timezone=auto`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (!data.daily) return {};

      const monthlyData = {};
      data.daily.time.forEach((dateString, index) => {
        const tempF = (data.daily.temperature_2m_mean[index] * 9/5) + 32;
        const monthKey = dateString.substring(0, 7);
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { hdd: 0, cdd: 0, temps: [] };
        }
        
        monthlyData[monthKey].temps.push(tempF);
        if (tempF < 65) {
          monthlyData[monthKey].hdd += (65 - tempF);
        } else if (tempF > 65) {
          monthlyData[monthKey].cdd += (tempF - 65);
        }
      });
      
      Object.keys(monthlyData).forEach(key => {
        const avgTemp = monthlyData[key].temps.reduce((a, b) => a + b, 0) / monthlyData[key].temps.length;
        monthlyData[key].avgTemp = Math.round(avgTemp);
        monthlyData[key].hdd = Math.round(monthlyData[key].hdd);
        monthlyData[key].cdd = Math.round(monthlyData[key].cdd);
        delete monthlyData[key].temps;
      });

      return monthlyData;
    } catch (err) {
      logger.warn('Weather fetch failed:', err);
      return {};
    }
  };

  const runAnalysis = async () => {
    setIsAnalyzing(true);
    setError(null);
    setAnalysisResults(null);

    try {
      // Fetch prepared facility data from server (reads monthly_stats + approved_locations server-side)
      const analysisDataResult = await getAnalysisDataFunction({
        selectedFacility: selectedFacility
      });
      
      const { facilityData, facilityNames: serverFacilityNames } = analysisDataResult.data;
      
      // Track reads from the analysis data function
      const facilityCount = Object.keys(facilityData || {}).length;
      // Note: Can't track exact doc counts from Cloud Function but we log the call
      sessionTracker?.trackRead('UtilityAnomalyAnalysis.getAnalysisData', 'monthly_stats+approved_locations', facilityCount, { via: 'CloudFunction:getAnalysisData', selectedFacility });
      
      // Update facility names if server provided them
      if (serverFacilityNames && serverFacilityNames.length > 0) {
        setFacilityNames(serverFacilityNames);
      }
      
      const facilitiesToAnalyze = Object.values(facilityData);

      if (facilitiesToAnalyze.length === 0) {
        throw new Error('No facility data available for analysis');
      }

      // --- Weather Integration ---
      const weatherMap = {}; // name -> YYYY-MM -> {hdd, cdd}
      for (const f of facilitiesToAnalyze) {
        if (f.address && f.months && f.months.length > 0) {
          const startMonth = f.months[0].month;
          const endMonth = f.months[f.months.length - 1].month;
          try {
            const coords = await getCoordinates(f.address);
            if (coords) {
              weatherMap[f.name] = await getHistoricalWeatherHDDCDD(coords.lat, coords.lon, startMonth, endMonth);
            }
          } catch (e) {
            logger.warn('Weather processing failed for ', f.name, e);
          }
        }
      }

      // Build data summary for UI display
      const dataSummary = {
        analysisDate: new Date().toISOString(),
        selectedFacility: selectedFacility === 'all' ? 'All Facilities' : selectedFacility,
        facilitiesIncluded: facilitiesToAnalyze.map(f => {
          const months = f.months.slice(-24);
          const dateRange = months.length > 0
            ? `${months[0].month} to ${months[months.length - 1].month}`
            : 'No data';
          const utilities = new Set();
          months.forEach(m => m.utilities_uploaded.forEach(u => utilities.add(u)));
          return {
            name: f.name,
            address: f.address,
            squareFootage: f.squareFootage,
            officeSquareFootage: f.officeSquareFootage,
            warehouseSquareFootage: f.warehouseSquareFootage,
            buildingAge: f.buildingAge,
            notes: f.notes || null,
            dataRange: dateRange,
            monthsOfData: months.length,
            utilitiesTracked: [...utilities],
          };
        }),
        totalMonthsAnalyzed: facilitiesToAnalyze.reduce((sum, f) => sum + f.months.slice(-24).length, 0),
      };
      setAnalysisDataSummary(dataSummary);

      // Build the AI prompt using server-provided data
      const analysisPrompt = `You are an expert utility analyst. Analyze the following utility consumption data for anomalies.

FACILITY DATA:
${facilitiesToAnalyze.map(facility => {
  const metadata = [];
  if (facility.squareFootage) metadata.push(`Total Size: ${facility.squareFootage.toLocaleString()} sq ft`);
  if (facility.officeSquareFootage) metadata.push(`Office: ${facility.officeSquareFootage.toLocaleString()} sq ft`);
  if (facility.warehouseSquareFootage) metadata.push(`Warehouse/Shop: ${facility.warehouseSquareFootage.toLocaleString()} sq ft (high ceilings)`);
  if (facility.buildingAge) metadata.push(`Building Age: ${facility.buildingAge} years`);
  if (facility.notes) metadata.push(`Notes: ${facility.notes}`);
  
  const monthlyData = facility.months.slice(-24).map(m => {
    const parts = [];
    if (m.utilities_uploaded.includes('electric')) {
      parts.push(`Electric=${m.electric_kwh}kWh ($${m.electric_cost})`);
    } else {
      parts.push(`Electric=NO_BILL_UPLOADED`);
    }
    if (m.utilities_uploaded.includes('gas')) {
      parts.push(`Gas=${m.gas_ccf}CCF ($${m.gas_cost})`);
    } else {
      parts.push(`Gas=NO_BILL_UPLOADED`);
    }
    if (m.utilities_uploaded.includes('water')) {
      parts.push(`Water=${m.water_gal}gal ($${m.water_cost})`);
    } else {
      parts.push(`Water=NO_BILL_UPLOADED`);
    }

    // Inject Weather
    const weather = weatherMap[facility.name]?.[m.month];
    if (weather) {
      parts.push(`Weather=AvgTemp:${weather.avgTemp}°F, HDD:${weather.hdd}, CDD:${weather.cdd}`);
    }

    return `  ${m.month}: ${parts.join(', ')}`;
  }).join('\n');
  
  return `
FACILITY: ${facility.name}
ADDRESS: ${facility.address}${metadata.length > 0 ? `
FACILITY SPECS: ${metadata.join(' | ')}` : ''}
MONTHLY USAGE DATA (last 12-24 months):
${monthlyData}`;
}).join('\n---\n')}

CRITICAL INSTRUCTIONS:
- "NO_BILL_UPLOADED" means the bill has not been processed yet - IGNORE these completely
- DO NOT flag "NO_BILL_UPLOADED" as zero consumption or an anomaly
- Only analyze months where actual bill data exists

FACILITY CONTEXT FOR ANALYSIS:
- Use building age to assess typical construction standards and insulation efficiency
- Older buildings (30+ years) typically have less efficient HVAC and insulation
- Warehouse/shop spaces with high ceilings require significantly more heating/cooling
- Normalize usage by square footage when comparing facilities (kWh per sq ft, CCF per sq ft)
- Office space typically uses 10-15 kWh/sq ft/year; warehouse uses 5-8 kWh/sq ft/year
- Gas usage varies by climate and building age; older buildings may use 2-3x more for heating
- Pay close attention to the "Notes" field in FACILITY SPECS — these contain user-provided context about the facility (e.g., EV chargers, solar panels, cold storage, 24/7 operations, known equipment issues). Factor these into your analysis and adjust anomaly thresholds accordingly. For example, a facility with EV chargers should be expected to have higher electric usage, and a facility with solar panels may show reduced grid consumption.

ANALYSIS INSTRUCTIONS:
1. For each facility, analyze the utility consumption patterns over the provided timeline.
2. CALCULATE A HYBRID "EXPECTED USAGE": Combine both theoretical benchmarking and historical reality:
   - A) Theoretical Baseline: Derived from the building's age, square footage, space type (office vs warehouse), and Notes.
   - B) Historical Anchor: The facility's own proven consumption history for that specific month/season.
3. CRITICAL RULE FOR UNDER-ESTIMATIONS: If your theoretical baseline (A) is significantly lower than the facility's historical anchor (B), DO NOT force 'expected_usage' to an unrealistically low number. Heavily weight the 'expected_usage' towards their actual consumption history, assuming the facility has undocumented systemic demands. 
4. Identify ANY months with unusually HIGH usage compared strictly to your unified HYBRID baseline.
5. WEATHER LOGIC: Evaluate the provided Weather metrics (AvgTemp, HDD, CDD).
   - HDD (Heating Degree Days) strictly correlates with Gas usage.
   - CDD (Cooling Degree Days) strictly correlates with Electric usage.
   - In the "likely_cause" field, DO NOT simply cite HDD or CDD numbers as the explanation. Instead, translate these into human-readable descriptions of the weather impact (e.g., "Extremely cold winter temperatures", "Persistent heatwave", "Unseasonably warm spring"). You may mention the specific average temperature provided to add credibility.
   - If a month has an unusually high HDD/CDD, it helps justify the usage, but you should still flag it if the usage is disproportionately high even for those conditions.
6. Flag potential issues ONLY when:
   - Sudden spikes in consumption are >${appSettings?.anomalySensitivity || 30}% above the established HYBRID "expected_usage" for that time period.
   - Unusual seasonal patterns occur (e.g., severe gas usage spikes in mid-summer when heating shouldn't be active).
   - Water usage anomalously deviates from its historical norm (indicating a potential leak).
7. IGNORE any utility marked as "NO_BILL_UPLOADED" - do not mention it in your analysis

RESPOND IN THIS EXACT JSON FORMAT:
{
  "summary": "Brief overall summary of findings",
  "data_sources_used": {
    "climate_data": "Describe any climate/weather assumptions used (region, heating/cooling degree days, typical temperatures, etc.)",
    "industry_benchmarks": "List specific industry benchmarks referenced (e.g., ENERGY STAR, CBECS, ASHRAE standards)",
    "rate_assumptions": "Any utility rate or pricing assumptions made",
    "methodology": "Brief description of the analytical methodology (statistical approach, comparison basis, threshold definitions)",
    "facility_notes_considered": ["List which facility notes were factored into the analysis and how they influenced the results"]
  },
  "facilities": [
    {
      "name": "Facility Name",
      "address": "Address",
      "risk_level": "high|medium|low|none",
      "anomalies": [
        {
          "utility_type": "electric|gas|water",
          "month": "YYYY-MM",
          "actual_usage": 1234,
          "expected_usage": 800,
          "percent_over": 54,
          "severity": "high|medium|low",
          "likely_cause": "Brief explanation",
          "recommendation": "Action to take"
        }
      ],
      "patterns": "Description of normal vs abnormal patterns observed",
      "recommendations": ["List", "of", "recommendations"]
    }
  ],
  "overall_recommendations": ["System-wide recommendations"]
}`;

      // Call Cloud Function (API key is secure on server)
      const result = await analyzeUtilityUsageFunction({ analysisPrompt });
      sessionTracker?.trackRead('UtilityAnomalyAnalysis.analyzeUsage', 'gemini_ai', 1, { via: 'CloudFunction:analyzeUtilityUsage' });
      setAnalysisResults(result.data);

    } catch (err) {
      logger.error('Analysis failed:', err);
      // Extract error message from Firebase callable error
      const errorMessage = err.message || err.details || 'Failed to analyze utility data';
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (!isOpen) return null;

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return darkMode ? 'text-red-400 bg-red-900/30' : 'text-red-700 bg-red-100';
      case 'medium': return darkMode ? 'text-yellow-400 bg-yellow-900/30' : 'text-yellow-700 bg-yellow-100';
      case 'low': return darkMode ? 'text-blue-400 bg-blue-900/30' : 'text-blue-700 bg-blue-100';
      default: return darkMode ? 'text-green-400 bg-green-900/30' : 'text-green-700 bg-green-100';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'medium': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default: return <Activity className="h-4 w-4 text-blue-500" />;
    }
  };

  const getUtilityIcon = (type) => {
    switch (type) {
      case 'electric': return <Zap className="h-4 w-4 text-yellow-500" />;
      case 'gas': return <Flame className="h-4 w-4 text-orange-500" />;
      case 'water': return <Droplets className="h-4 w-4 text-blue-500" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  // --- PDF Export for Analysis Report ---
  const exportAnalysisPDF = () => {
    if (!analysisResults) return;
    
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = 20;

    const addText = (text, x, fontSize, style = 'normal', color = [0, 0, 0]) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', style);
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(text, pageWidth - x - 14);
      if (y + lines.length * (fontSize * 0.5) > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        y = 20;
      }
      pdf.text(lines, x, y);
      y += lines.length * (fontSize * 0.5) + 2;
      return lines.length;
    };

    const checkPageBreak = (needed) => {
      if (y + needed > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        y = 20;
      }
    };

    // Title
    addText('UtiliTrack AI Anomaly Analysis Report', 14, 18, 'bold', [9, 80, 160]);
    addText(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 14, 9, 'normal', [100, 100, 100]);
    y += 4;

    // Data Summary Section
    if (analysisDataSummary) {
      addText('DATA SUBMITTED FOR ANALYSIS', 14, 12, 'bold', [80, 80, 80]);
      y += 1;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(14, y, pageWidth - 14, y);
      y += 4;
      addText(`Scope: ${analysisDataSummary.selectedFacility}`, 14, 9, 'normal', [60, 60, 60]);
      addText(`Total Months Analyzed: ${analysisDataSummary.totalMonthsAnalyzed}`, 14, 9, 'normal', [60, 60, 60]);
      y += 2;

      const summaryHeaders = [['Facility', 'Address', 'Sq Ft', 'Age', 'Data Range', 'Utilities', 'Notes']];
      const summaryRows = analysisDataSummary.facilitiesIncluded.map(f => [
        f.name,
        f.address || 'N/A',
        f.squareFootage ? f.squareFootage.toLocaleString() : 'N/A',
        f.buildingAge ? `${f.buildingAge} yrs` : 'N/A',
        f.dataRange,
        f.utilitiesTracked.join(', '),
        f.notes ? (f.notes.length > 40 ? f.notes.substring(0, 40) + '...' : f.notes) : '-'
      ]);
      autoTable(pdf, {
        head: summaryHeaders,
        body: summaryRows,
        startY: y,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [9, 80, 160], textColor: 255 },
        columnStyles: { 6: { cellWidth: 35 } },
        margin: { left: 14, right: 14 },
      });
      y = pdf.lastAutoTable.finalY + 8;
    }

    // Data Sources Used by AI
    if (analysisResults.data_sources_used) {
      checkPageBreak(30);
      addText('DATA SOURCES & METHODOLOGY USED BY AI', 14, 12, 'bold', [80, 80, 80]);
      y += 1;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(14, y, pageWidth - 14, y);
      y += 4;
      
      const ds = analysisResults.data_sources_used;
      if (ds.climate_data) { addText(`Climate Data: ${ds.climate_data}`, 14, 8, 'normal', [60, 60, 60]); }
      if (ds.industry_benchmarks) { addText(`Industry Benchmarks: ${ds.industry_benchmarks}`, 14, 8, 'normal', [60, 60, 60]); }
      if (ds.rate_assumptions) { addText(`Rate Assumptions: ${ds.rate_assumptions}`, 14, 8, 'normal', [60, 60, 60]); }
      if (ds.methodology) { addText(`Methodology: ${ds.methodology}`, 14, 8, 'normal', [60, 60, 60]); }
      if (ds.facility_notes_considered?.length > 0) {
        addText('Facility Notes Considered:', 14, 8, 'bold', [60, 60, 60]);
        ds.facility_notes_considered.forEach(note => {
          addText(`  • ${note}`, 16, 8, 'normal', [60, 60, 60]);
        });
      }
      y += 4;
    }

    // Summary
    checkPageBreak(20);
    addText('ANALYSIS SUMMARY', 14, 12, 'bold', [80, 80, 80]);
    y += 1;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(14, y, pageWidth - 14, y);
    y += 4;
    addText(analysisResults.summary, 14, 9, 'normal', [40, 40, 40]);
    y += 4;

    // Facility Results
    analysisResults.facilities?.forEach((facility) => {
      checkPageBreak(25);
      addText(`${facility.name}`, 14, 11, 'bold', [30, 30, 30]);
      addText(`${facility.address} — Risk Level: ${(facility.risk_level || 'none').toUpperCase()}`, 14, 8, 'normal', [100, 100, 100]);
      y += 2;

      if (facility.anomalies?.length > 0) {
        const anomalyHeaders = [['Utility', 'Month', 'Actual', 'Expected', '% Over', 'Severity', 'Likely Cause', 'Recommendation']];
        const anomalyRows = facility.anomalies.map(a => [
          a.utility_type,
          a.month,
          a.actual_usage?.toLocaleString() || 'N/A',
          a.expected_usage?.toLocaleString() || 'N/A',
          `${a.percent_over}%`,
          a.severity,
          a.likely_cause || '',
          a.recommendation || ''
        ]);
        
        autoTable(pdf, {
          head: anomalyHeaders,
          body: anomalyRows,
          startY: y,
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [220, 53, 69], textColor: 255 },
          margin: { left: 14, right: 14 },
        });
        y = pdf.lastAutoTable.finalY + 4;
      } else {
        addText('No anomalies detected.', 16, 8, 'italic', [34, 139, 34]);
      }

      if (facility.patterns) {
        addText(`Patterns: ${facility.patterns}`, 14, 8, 'normal', [60, 60, 60]);
      }

      if (facility.recommendations?.length > 0) {
        addText('Recommendations:', 14, 8, 'bold', [60, 60, 60]);
        facility.recommendations.forEach(rec => {
          addText(`  • ${rec}`, 16, 8, 'normal', [60, 60, 60]);
        });
      }
      y += 6;
    });

    // Overall Recommendations
    if (analysisResults.overall_recommendations?.length > 0) {
      checkPageBreak(20);
      addText('OVERALL RECOMMENDATIONS', 14, 12, 'bold', [80, 80, 80]);
      y += 1;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(14, y, pageWidth - 14, y);
      y += 4;
      analysisResults.overall_recommendations.forEach(rec => {
        addText(`• ${rec}`, 14, 9, 'normal', [40, 40, 40]);
      });
    }

    // Footer on each page
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`UtiliTrack AI Analysis Report — Page ${i} of ${totalPages}`, 14, pdf.internal.pageSize.getHeight() - 10);
    }

    pdf.save(`UtiliTrack_AI_Analysis_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-colors duration-200 border ${
        darkMode ? 'bg-[#121212] border-cyan-500 shadow-[0_0_24px_rgba(14,165,233,0.2)]' : 'bg-white border-sky-500/40 shadow-[0_4px_20px_rgba(14,165,233,0.12)]'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 border-b p-6 flex items-center justify-between ${
          darkMode ? 'bg-[#121212] border-cyan-500/40' : 'bg-white border-sky-400/30'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${darkMode ? 'bg-cyan-500/10' : 'bg-sky-500/10'}`}>
              <Sparkles className="h-6 w-6 neon-icon-pulse" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                AI Utility Anomaly Analysis
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Detect unusual consumption patterns across your facilities
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              darkMode ? 'hover:bg-cyan-500/10 text-gray-400' : 'hover:bg-sky-50 text-gray-500'
            }`}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${darkMode ? 'bg-[#121212]' : 'bg-white'}`}>
          
          {/* Controls */}
          <div className={`p-4 rounded-lg border ${
            darkMode ? 'bg-[#1a1a2e] border-cyan-500/30' : 'bg-gray-50 border-sky-400/30'
          }`}>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Select Facility
                </label>
                <select
                  value={selectedFacility}
                  onChange={(e) => setSelectedFacility(e.target.value)}
                  className="input-dashboard w-full px-3 py-2"
                >
                  <option value="all">All Facilities</option>
                  {facilitiesWithData.map(loc => (
                    <option key={loc.id} value={loc.name}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={runAnalysis}
                disabled={isAnalyzing || monthlyStats.length === 0}
                className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                  isAnalyzing || monthlyStats.length === 0
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-[0_0_12px_rgba(14,165,233,0.4)]'
                }`}
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Run Analysis
                  </>
                )}
              </button>
            </div>
            
            {monthlyStats.length === 0 && (
              <p className={`mt-3 text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                ⚠️ No utility data available. Upload some bills first to run analysis.
              </p>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className={`p-4 rounded-lg border-l-4 border-red-500 ${
              darkMode ? 'bg-red-900/30' : 'bg-red-50'
            }`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                  Analysis Error
                </span>
              </div>
              <p className={`mt-1 text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                {error}
              </p>
            </div>
          )}

          {/* Results */}
          {analysisResults && (
            <div className="space-y-6">
              {/* Summary */}
              <div className={`p-4 rounded-lg border ${
                darkMode ? 'bg-cyan-500/5 border-cyan-500/30' : 'bg-sky-50 border-sky-400/30'
              }`}>
                <div className="flex items-center justify-between">
                  <h4 className={`font-semibold flex items-center gap-2 ${
                    darkMode ? 'text-cyan-300' : 'text-sky-800'
                  }`}>
                    <Activity className="h-5 w-5" />
                    Summary
                  </h4>
                  <button
                    onClick={exportAnalysisPDF}
                    className="btn-dashboard-accent px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Export PDF
                  </button>
                </div>
                <p className="mt-2 text-gray-700 dark:text-gray-300">
                  {analysisResults.summary}
                </p>
              </div>

              {/* Data Submitted for Analysis (Collapsible) */}
              {analysisDataSummary && (
                <div className={`rounded-lg border overflow-hidden ${
                  darkMode ? 'border-cyan-500/30' : 'border-sky-400/30'
                }`}>
                  <button
                    onClick={() => setShowDataSummary(prev => !prev)}
                    className={`w-full p-4 flex items-center justify-between text-left transition-colors ${
                      darkMode ? 'bg-[#1a1a2e] hover:bg-cyan-500/5' : 'bg-gray-50 hover:bg-sky-50'
                    }`}
                  >
                    <h4 className="font-semibold flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <FileText className="h-4 w-4" />
                      Data Submitted for Analysis
                      <span className={`text-xs font-normal px-2 py-0.5 rounded-full ${
                        darkMode ? 'bg-cyan-500/10 text-cyan-400' : 'bg-sky-100 text-sky-600'
                      }`}>
                        {analysisDataSummary.facilitiesIncluded.length} facilities, {analysisDataSummary.totalMonthsAnalyzed} months
                      </span>
                    </h4>
                    <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${
                      showDataSummary ? 'rotate-90' : ''
                    } ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                  {showDataSummary && (
                    <div className={`p-4 border-t ${darkMode ? 'border-cyan-500/20 bg-[#121212]' : 'border-sky-400/20 bg-white'}`}>
                      <div className="text-xs mb-3 text-gray-500 dark:text-gray-400">
                        Analysis Scope: <strong>{analysisDataSummary.selectedFacility}</strong> — {new Date(analysisDataSummary.analysisDate).toLocaleString()}
                      </div>
                      <div className="space-y-3">
                        {analysisDataSummary.facilitiesIncluded.map((f, idx) => (
                          <div key={idx} className={`p-3 rounded border ${
                            darkMode ? 'bg-[#1a1a2e] border-cyan-500/20' : 'bg-gray-50 border-sky-400/20'
                          }`}>
                            <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                              {f.name}
                            </div>
                            <div className="text-xs mt-1 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-gray-500 dark:text-gray-400">
                              <span>Address: {f.address || 'N/A'}</span>
                              <span>Data: {f.dataRange}</span>
                              <span>Months: {f.monthsOfData}</span>
                              <span>Utilities: {f.utilitiesTracked.join(', ')}</span>
                              {f.squareFootage && <span>Sq Ft: {f.squareFootage.toLocaleString()}</span>}
                              {f.officeSquareFootage && <span>Office: {f.officeSquareFootage.toLocaleString()} sq ft</span>}
                              {f.warehouseSquareFootage && <span>Warehouse: {f.warehouseSquareFootage.toLocaleString()} sq ft</span>}
                              {f.buildingAge && <span>Age: {f.buildingAge} years</span>}
                            </div>
                            {f.notes && (
                              <div className={`text-xs mt-2 px-2 py-1.5 rounded border-l-2 ${
                                darkMode ? 'bg-cyan-500/5 border-cyan-500 text-cyan-300' : 'bg-sky-50 border-sky-400 text-sky-700'
                              }`}>
                                <strong>Notes:</strong> {f.notes}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Data Sources & Methodology Used by AI */}
              {analysisResults.data_sources_used && (
                <div className={`p-4 rounded-lg border ${
                  darkMode ? 'bg-cyan-500/5 border-cyan-500/30' : 'bg-sky-50 border-sky-400/30'
                }`}>
                  <h4 className={`font-semibold flex items-center gap-2 mb-3 ${
                    darkMode ? 'text-cyan-300' : 'text-sky-800'
                  }`}>
                    <Thermometer className="h-5 w-5" />
                    Data Sources & Methodology Used by AI
                  </h4>
                  <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                    {analysisResults.data_sources_used.climate_data && (
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-cyan-400' : 'text-sky-700'}`}>Climate Data: </span>
                        {analysisResults.data_sources_used.climate_data}
                      </div>
                    )}
                    {analysisResults.data_sources_used.industry_benchmarks && (
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-cyan-400' : 'text-sky-700'}`}>Industry Benchmarks: </span>
                        {analysisResults.data_sources_used.industry_benchmarks}
                      </div>
                    )}
                    {analysisResults.data_sources_used.rate_assumptions && (
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-cyan-400' : 'text-sky-700'}`}>Rate Assumptions: </span>
                        {analysisResults.data_sources_used.rate_assumptions}
                      </div>
                    )}
                    {analysisResults.data_sources_used.methodology && (
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-cyan-400' : 'text-sky-700'}`}>Methodology: </span>
                        {analysisResults.data_sources_used.methodology}
                      </div>
                    )}
                    {analysisResults.data_sources_used.facility_notes_considered?.length > 0 && (
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-cyan-400' : 'text-sky-700'}`}>Facility Notes Considered:</span>
                        <ul className="mt-1 ml-4 space-y-1">
                          {analysisResults.data_sources_used.facility_notes_considered.map((note, idx) => (
                            <li key={idx} className="flex items-start gap-1.5">
                              <ChevronRight className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-cyan-500" />
                              <span>{note}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Facility Results */}
              {analysisResults.facilities?.map((facility, idx) => (
                <div 
                  key={idx}
                  className={`rounded-lg border overflow-hidden ${
                    darkMode ? 'border-cyan-500/30' : 'border-sky-400/30'
                  }`}
                >
                  {/* Facility Header */}
                  <div className={`p-4 border-b ${
                    darkMode ? 'bg-[#1a1a2e] border-cyan-500/20' : 'bg-gray-50 border-sky-400/20'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Building2 className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <div>
                          <h5 className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            {facility.name}
                          </h5>
                          <p className={`text-sm flex items-center gap-1 ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            <MapPin className="h-3 w-3" />
                            {facility.address}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase ${getRiskColor(facility.risk_level)}`}>
                        {facility.risk_level} risk
                      </span>
                    </div>
                  </div>

                  {/* Anomalies */}
                  <div className={`p-4 ${darkMode ? 'bg-[#121212]' : 'bg-white'}`}>
                    {facility.anomalies?.length > 0 ? (
                      <div className="space-y-3">
                        <h6 className={`text-sm font-semibold uppercase tracking-wider ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Anomalies Detected ({facility.anomalies.length})
                        </h6>
                        {facility.anomalies.map((anomaly, aIdx) => (
                          <div 
                            key={aIdx}
                            className={`p-3 rounded-lg border ${
                              darkMode ? 'bg-[#1a1a2e] border-cyan-500/20' : 'bg-gray-50 border-sky-400/20'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                {getSeverityIcon(anomaly.severity)}
                                {getUtilityIcon(anomaly.utility_type)}
                                <span className={`font-medium capitalize ${
                                  darkMode ? 'text-gray-100' : 'text-gray-900'
                                }`}>
                                  {anomaly.utility_type}
                                </span>
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  • {anomaly.month}
                                </span>
                              </div>
                              <span className={`text-sm font-bold ${
                                anomaly.percent_over > ((appSettings?.anomalySensitivity || 30) + 20)
                                  ? 'text-red-500' 
                                  : anomaly.percent_over > (appSettings?.anomalySensitivity || 30)
                                    ? 'text-yellow-500' 
                                    : 'text-blue-500'
                              }`}>
                                +{anomaly.percent_over}% over expected
                              </span>
                            </div>
                            <div className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <p>
                                <strong>Actual:</strong> {anomaly.actual_usage?.toLocaleString()} | 
                                <strong> Expected:</strong> ~{anomaly.expected_usage?.toLocaleString()}
                              </p>
                              <p className="mt-1">
                                <strong>Likely cause:</strong> {anomaly.likely_cause}
                              </p>
                              <p className={`mt-1 ${darkMode ? 'text-cyan-400' : 'text-sky-600'}`}>
                                <strong>Recommendation:</strong> {anomaly.recommendation}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={`text-sm flex items-center gap-2 ${
                        darkMode ? 'text-green-400' : 'text-green-600'
                      }`}>
                        <CheckCircle2 className="h-4 w-4" />
                        No anomalies detected for this facility
                      </p>
                    )}

                    {/* Patterns */}
                    {facility.patterns && (
                      <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-cyan-500/20' : 'border-sky-400/20'}`}>
                        <h6 className={`text-sm font-semibold mb-2 ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Observed Patterns
                        </h6>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {facility.patterns}
                        </p>
                      </div>
                    )}

                    {/* Recommendations */}
                    {facility.recommendations?.length > 0 && (
                      <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-cyan-500/20' : 'border-sky-400/20'}`}>
                        <h6 className={`text-sm font-semibold mb-2 ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Recommendations
                        </h6>
                        <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {facility.recommendations.map((rec, rIdx) => (
                            <li key={rIdx} className="flex items-start gap-2">
                              <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0 text-cyan-500" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Overall Recommendations */}
              {analysisResults.overall_recommendations?.length > 0 && (
                <div className={`p-4 rounded-lg border ${
                  darkMode ? 'bg-cyan-500/5 border-cyan-500/30' : 'bg-sky-50 border-sky-400/30'
                }`}>
                  <h4 className={`font-semibold mb-3 ${darkMode ? 'text-cyan-300' : 'text-sky-800'}`}>
                    Overall Recommendations
                  </h4>
                  <ul className={`space-y-2 ${darkMode ? 'text-gray-300' : 'text-sky-700'}`}>
                    {analysisResults.overall_recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!analysisResults && !isAnalyzing && !error && (
            <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Sparkles className={`h-16 w-16 mx-auto mb-4 neon-icon-pulse`} />
              <h4 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-cyan-300' : 'text-sky-700'}`}>
                Ready to Analyze
              </h4>
              <p className="max-w-md mx-auto">
                Select a facility (or all facilities) and click "Run Analysis" to detect 
                unusual utility consumption patterns using AI.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`border-t p-4 flex justify-between ${
          darkMode ? 'bg-[#121212] border-cyan-500/30' : 'bg-gray-50 border-sky-400/30'
        }`}>
          <div>
            {analysisResults && (
              <button
                onClick={exportAnalysisPDF}
                className="btn-dashboard-accent flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export Full Report as PDF
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="btn-dashboard-outline"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// --- COMPONENTS ---

// 0. MOBILE BOTTOM NAV
const MobileBottomNav = ({ activeTab, setActiveTab, darkMode, onShowAnomalyAnalysis, onShowEnterpriseAnalytics }) => {
  return (
    <nav className={`fixed bottom-0 left-0 right-0 z-50 md:hidden border-t transition-all duration-200 ${
      darkMode
        ? 'bg-[#121212] border-cyan-500/60 shadow-[0_-2px_15px_rgba(14,165,233,0.3)]'
        : 'bg-white border-sky-200 shadow-[0_-2px_10px_rgba(14,165,233,0.1)]'
    }`}>
      <div className="flex items-end justify-around px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {/* Home / Dashboard */}
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-lg transition-colors ${
            activeTab === 'dashboard'
              ? darkMode
                ? 'text-cyan-400'
                : 'text-sky-600'
              : darkMode
                ? 'text-gray-500 hover:text-cyan-300'
                : 'text-gray-400 hover:text-sky-500'
          }`}
        >
          <Home className="h-5 w-5" />
          <span className="text-[10px] font-medium">Home</span>
        </button>

        {/* Scan - Center, larger */}
        <button
          onClick={() => setActiveTab('scan')}
          className="relative flex flex-col items-center -mt-5"
        >
          <div className={`flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-200 ${
            activeTab === 'scan'
              ? darkMode
                ? 'bg-cyan-500 shadow-[0_0_20px_rgba(14,165,233,0.6)]'
                : 'bg-sky-500 shadow-[0_4px_15px_rgba(14,165,233,0.4)]'
              : darkMode
                ? 'bg-cyan-600/80 shadow-[0_0_12px_rgba(14,165,233,0.3)] hover:bg-cyan-500'
                : 'bg-sky-500/80 shadow-[0_4px_10px_rgba(14,165,233,0.25)] hover:bg-sky-500'
          }`}>
            <ScanLine className="h-7 w-7 text-white" />
          </div>
          <span className={`text-[10px] font-medium mt-1 transition-colors ${
            activeTab === 'scan'
              ? darkMode ? 'text-cyan-400' : 'text-sky-600'
              : darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>Scan</span>
        </button>

        {/* AI Analysis */}
        <button
          onClick={() => onShowAnomalyAnalysis?.()}
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-lg transition-colors ${
            darkMode
              ? 'text-gray-500 hover:text-cyan-300'
              : 'text-gray-400 hover:text-sky-500'
          }`}
        >
          <Sparkles className="h-5 w-5" />
          <span className="text-[10px] font-medium">AI Analysis</span>
        </button>

        {/* Analytics */}
        <button
          onClick={() => onShowEnterpriseAnalytics?.()}
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-lg transition-colors ${
            darkMode
              ? 'text-gray-500 hover:text-cyan-300'
              : 'text-gray-400 hover:text-sky-500'
          }`}
        >
          <TrendingUp className="h-5 w-5" />
          <span className="text-[10px] font-medium">Analytics</span>
        </button>
      </div>
    </nav>
  );
};

// --- SYSTEM SETTINGS MODAL ---
const SystemSettings = ({ isOpen, onClose, darkMode, appSettings, setAppSettings }) => {
  const [localSettings, setLocalSettings] = useState(appSettings || { anomalySensitivity: 30 });
  const [isSaving, setIsSaving] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);

  useEffect(() => {
    if (appSettings) setLocalSettings(appSettings);
  }, [appSettings]);

  const handleRebuildStats = async () => {
    if (!window.confirm("This will re-calculate statistics for all facilities based on existing bills to backfill missing data (like Carbon Emissions). Proceed?")) return;
    setIsRebuilding(true);
    try {
      await rebuildAllSiteNamesFunction();
      alert("System data successfully rebuilt with updated metrics.");
    } catch (error) {
      logger.error('Failed to rebuild stats:', error);
      alert('Data rebuild failed. Check console.');
    } finally {
      setIsRebuilding(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'app_settings', 'global'), localSettings, { merge: true });
      setAppSettings(localSettings);
      onClose();
    } catch (error) {
      logger.error('Failed to save settings:', error);
      alert('Failed to save settings. Check console.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`w-full max-w-lg rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}>
        <div className={`flex justify-between items-center p-4 sm:p-6 border-b ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${darkMode ? 'bg-cyan-900/30 text-cyan-400' : 'bg-cyan-100 text-cyan-600'}`}>
              <Settings className="h-5 w-5" />
            </div>
            <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>System Settings</h2>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          <div>
            <h3 className={`text-sm font-bold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>AI Anomaly Sensitivity Threshold</h3>
            <p className={`text-xs mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Sets the percentage variation above expected bounds required for the AI to flag an anomaly. Lower values are more sensitive.
            </p>
            <div className="flex items-center gap-4">
              <input 
                type="range" 
                min="10" 
                max="100" 
                step="5" 
                value={localSettings.anomalySensitivity || 30} 
                onChange={(e) => setLocalSettings({...localSettings, anomalySensitivity: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
              <span className={`text-sm font-bold w-12 text-center ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {localSettings.anomalySensitivity || 30}%
              </span>
            </div>
          </div>

          <div className={`pt-6 border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
            <h3 className={`text-sm font-bold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Data Management</h3>
            <p className={`text-xs mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Backfill missing historical data (e.g., carbon emissions) by re-calculating all aggregated monthly statistics.
            </p>
            <button
              onClick={handleRebuildStats}
              disabled={isRebuilding}
              className={`w-full py-2.5 px-4 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 border ${
                darkMode 
                  ? 'border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10' 
                  : 'border-cyan-200 text-cyan-700 hover:bg-cyan-50 shadow-sm'
              } ${isRebuilding ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isRebuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
              {isRebuilding ? 'Rebuilding System Data...' : 'Rebuild All Database Stats'}
            </button>
          </div>
        </div>

        <div className={`p-4 border-t flex justify-end gap-3 ${darkMode ? 'border-gray-800 bg-gray-900/50' : 'border-gray-50 bg-gray-50'}`}>
          <button onClick={onClose} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-200'}`}>
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            disabled={isSaving}
            className={`px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg shadow-sm transition-colors flex items-center gap-2 ${isSaving ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

// 1. HEADER
const Header = ({ activeTab, setActiveTab, darkMode, toggleDarkMode, onShowLocationManager, onShowAnomalyAnalysis, onShowEnterpriseAnalytics, onShowSettings }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navItems = ['dashboard', 'scan', 'bills', 'archive'];

  return (
    <div className="border-b sticky top-0 z-50 transition-all duration-200 bg-[#121212] border-cyan-500/60 shadow-[0_2px_15px_rgba(14,165,233,0.4)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          
          {/* LEFT SIDE: LOGO */}
          <div className="flex items-center gap-4">
            <img 
              src="/logo_multi.png" 
              alt="Lift EHS Logo" 
              className="h-full object-contain" 
            />
            <div className="h-8 w-px mx-2 bg-cyan-500/40"></div>
            <span className="font-bold text-xl tracking-tight text-white">
              UtiliTrack AI
            </span>
          </div>

          {/* RIGHT SIDE: DESKTOP NAVIGATION (Hidden on mobile) */}
          <nav className="hidden md:flex space-x-8 h-full items-center">
            {navItems.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full transition-colors ${
                  activeTab === tab
                    ? 'border-cyan-400 text-cyan-300'
                    : 'border-transparent text-gray-400 hover:text-cyan-300 hover:border-cyan-500/50'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
            
            <div className="relative group flex items-center h-full ml-4">
              <button className="px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 text-gray-400 group-hover:text-cyan-300 group-hover:bg-cyan-500/10">
                <FileText className="h-5 w-5" />
                <span className="hidden lg:inline">Reports</span>
                <ChevronDown className="h-4 w-4 hidden lg:inline opacity-70" />
              </button>
              <div className="absolute top-[calc(100%-10px)] left-0 w-56 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-[#121212] border border-cyan-500/60 rounded-md shadow-lg py-2">
                  <button 
                    onClick={() => onShowAnomalyAnalysis?.()}
                    className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/10 flex items-center gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    AI Analysis
                  </button>
                  <button 
                    onClick={() => onShowEnterpriseAnalytics?.()}
                    className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/10 flex items-center gap-2"
                  >
                    <TrendingUp className="h-4 w-4" />
                    Analytics
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group flex items-center h-full">
              <button className="px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 text-gray-400 group-hover:text-cyan-300 group-hover:bg-cyan-500/10">
                <Settings className="h-5 w-5" />
                <span className="hidden lg:inline">Settings</span>
                <ChevronDown className="h-4 w-4 hidden lg:inline opacity-70" />
              </button>
              <div className="absolute top-[calc(100%-10px)] right-0 w-56 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-[#121212] border border-cyan-500/60 rounded-md shadow-lg py-2">
                  <button 
                    onClick={() => onShowLocationManager?.()}
                    className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/10 flex items-center gap-2"
                  >
                    <Building2 className="h-4 w-4" />
                    Locations
                  </button>
                  <button 
                    onClick={() => onShowSettings?.()}
                    className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/10 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Global Settings
                  </button>
                </div>
              </div>
            </div>
            
            <button 
              onClick={toggleDarkMode}
              className="p-2 transition-colors flex items-center text-gray-400 hover:text-cyan-300"
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            
            <button 
              onClick={() => signOut(auth)}
              className="p-2 transition-colors flex items-center text-gray-400 hover:text-red-400"
              title="Sign Out"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </nav>

          {/* RIGHT SIDE: MOBILE HAMBURGER BUTTON (Visible only on mobile) */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md focus:outline-none transition-colors text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE MENU DROPDOWN (Only shows when isMenuOpen is true) */}
      {isMenuOpen && (
        <div className="md:hidden border-t shadow-lg absolute w-full left-0 animate-in slide-in-from-top-2 border-cyan-500/40 bg-[#121212]">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setIsMenuOpen(false); // Close menu after clicking
                }}
                className={`block w-full text-left px-3 py-3 rounded-md text-base font-medium ${
                  activeTab === tab
                    ? 'bg-cyan-500/10 text-cyan-300'
                    : 'text-gray-400 hover:bg-cyan-500/10 hover:text-white'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
            
            <div className="border-t my-2 border-cyan-500/40"></div>
            
            <button
              onClick={() => {
                toggleDarkMode();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left px-3 py-3 rounded-md text-base font-medium flex items-center gap-2 text-gray-400 hover:bg-cyan-500/10"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
            
            <div className="border-t mt-4 mb-2 border-cyan-500/40"></div>
            <div className="px-3 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Reports</div>
            
            <button
              onClick={() => {
                if (onShowAnomalyAnalysis) onShowAnomalyAnalysis();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left pl-8 pr-3 py-3 rounded-md text-base font-medium flex items-center gap-2 text-gray-400 hover:bg-cyan-500/10"
            >
              <Sparkles className="h-5 w-5" />
              AI Analysis
            </button>
            
            <button
              onClick={() => {
                if (onShowEnterpriseAnalytics) onShowEnterpriseAnalytics();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left pl-8 pr-3 py-3 rounded-md text-base font-medium flex items-center gap-2 text-gray-400 hover:bg-cyan-500/10"
            >
              <TrendingUp className="h-5 w-5" />
              Enterprise Analytics
            </button>

            <div className="border-t my-2 border-cyan-500/40"></div>
            <div className="px-3 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Settings</div>

            <button
              onClick={() => {
                if (onShowLocationManager) onShowLocationManager();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left pl-8 pr-3 py-3 rounded-md text-base font-medium flex items-center gap-2 text-gray-400 hover:bg-cyan-500/10"
            >
              <Building2 className="h-5 w-5" />
              Manage Locations
            </button>

            <button
              onClick={() => {
                if (onShowSettings) onShowSettings();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left pl-8 pr-3 py-3 rounded-md text-base font-medium flex items-center gap-2 text-gray-400 hover:bg-cyan-500/10"
            >
              <Settings className="h-5 w-5" />
              Global Settings
            </button>
            
            <button
              onClick={() => {
                signOut(auth);
                setIsMenuOpen(false);
              }}
              className="block w-full text-left px-3 py-3 rounded-md text-base font-medium flex items-center gap-2 text-gray-400 hover:bg-red-900/20 hover:text-red-400"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// 1.4 DASHBOARD EXPORT MODAL
const DashboardExportModal = ({ _bills, onClose, onExport, darkMode, exportProgress }) => {
  const [exportFormat, setExportFormat] = useState('bento_pdf');
  const [groupByFacility, setGroupByFacility] = useState(true);
  const [includeSummaryPages, setIncludeSummaryPages] = useState(true);
  const [includeComparisonCharts, setIncludeComparisonCharts] = useState(true);
  const [includeMonthlyComparison, setIncludeMonthlyComparison] = useState(true);
  const [includeDate, setIncludeDate] = useState(true);
  const [includeFacility, setIncludeFacility] = useState(true);
  const [includeType, setIncludeType] = useState(true);
  const [includeUsage, setIncludeUsage] = useState(true);
  const [includeCost, setIncludeCost] = useState(true);

  const isBento = exportFormat === 'bento_pdf';
  const isExporting = !!exportProgress;

  const handleExport = () => {
    onExport(exportFormat, {
      groupByFacility,
      includeSummaryPages,
      includeComparisonCharts,
      includeMonthlyComparison,
      includeDate,
      includeFacility,
      includeType,
      includeUsage,
      includeCost,
    });
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto transition-all duration-200 ${
        darkMode 
          ? 'bg-[#121212] border border-cyan-500 shadow-[0_0_20px_rgba(14,165,233,0.3),inset_0_0_10px_rgba(14,165,233,0.1)]' 
          : 'bg-white border border-sky-500/40 shadow-[0_4px_15px_rgba(14,165,233,0.125)]'
      }`}>
        
        {/* Header */}
        <div className={`border-b p-6 flex items-center justify-between sticky top-0 z-10 transition-all duration-200 ${
          darkMode ? 'border-cyan-500/40 bg-[#121212]' : 'border-sky-400/30 bg-white'
        }`}>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Export Dashboard Report</h3>
          <button
            onClick={onClose}
            disabled={isExporting}
            className={`text-2xl ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Progress indicator */}
          {isExporting && (
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-[#0950A0]" />
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>Generating Report...</p>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>{exportProgress}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Export Format Selection */}
          <div>
            <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Export Format</h4>
            <div className="space-y-2">
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`} style={{ borderColor: exportFormat === 'bento_pdf' ? '#0950A0' : (darkMode ? '#374151' : '#e5e7eb'), backgroundColor: exportFormat === 'bento_pdf' ? (darkMode ? '#1e3a8a20' : '#f0f4ff') : 'transparent' }}>
                <input
                  type="radio"
                  name="format"
                  value="bento_pdf"
                  checked={exportFormat === 'bento_pdf'}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-4 h-4"
                  disabled={isExporting}
                />
                <div className="ml-3 flex-1">
                  <span className={`font-medium ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Visual PDF Report</span>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Bento-style layout with charts & data cards</p>
                </div>
                <span className="ml-auto px-2 py-0.5 text-[10px] font-bold rounded-full bg-[#0950A0] text-white">NEW</span>
              </label>

              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`} style={{ borderColor: exportFormat === 'pdf' ? '#0950A0' : (darkMode ? '#374151' : '#e5e7eb'), backgroundColor: exportFormat === 'pdf' ? (darkMode ? '#1e3a8a20' : '#f0f4ff') : 'transparent' }}>
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={exportFormat === 'pdf'}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-4 h-4"
                  disabled={isExporting}
                />
                <div className="ml-3 flex-1">
                  <span className={`font-medium ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Table PDF Report</span>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Classic tabular format</p>
                </div>
              </label>
              
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`} style={{ borderColor: exportFormat === 'excel' ? '#0950A0' : (darkMode ? '#374151' : '#e5e7eb'), backgroundColor: exportFormat === 'excel' ? (darkMode ? '#1e3a8a20' : '#f0f4ff') : 'transparent' }}>
                <input
                  type="radio"
                  name="format"
                  value="excel"
                  checked={exportFormat === 'excel'}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-4 h-4"
                  disabled={isExporting}
                />
                <div className="ml-3 flex-1">
                  <span className={`font-medium ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Excel (CSV)</span>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Spreadsheet format</p>
                </div>
              </label>
            </div>
          </div>

          {/* Bento PDF Options */}
          {isBento && (
            <div>
              <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Visual Report Options</h4>
              <div className="space-y-2">
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={groupByFacility}
                    onChange={(e) => setGroupByFacility(e.target.checked)}
                    className="w-4 h-4 rounded"
                    disabled={isExporting}
                  />
                  <div className="ml-3">
                    <span className={`text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Group by Facility</span>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Separate section with divider for each location</p>
                  </div>
                </label>

                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeSummaryPages}
                    onChange={(e) => setIncludeSummaryPages(e.target.checked)}
                    className="w-4 h-4 rounded"
                    disabled={isExporting}
                  />
                  <div className="ml-3">
                    <span className={`text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Include Summary Pages</span>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Portfolio overview with KPIs and facility table</p>
                  </div>
                </label>

                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeComparisonCharts}
                    onChange={(e) => setIncludeComparisonCharts(e.target.checked)}
                    className="w-4 h-4 rounded"
                    disabled={isExporting || !includeSummaryPages}
                  />
                  <div className="ml-3">
                    <span className={`text-sm ${includeSummaryPages ? (darkMode ? 'text-gray-100' : 'text-[#000000]') : 'text-gray-400'}`}>Summary Comparison Charts</span>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cost by utility type & trend charts on summary</p>
                  </div>
                </label>

                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeMonthlyComparison}
                    onChange={(e) => setIncludeMonthlyComparison(e.target.checked)}
                    className="w-4 h-4 rounded"
                    disabled={isExporting}
                  />
                  <div className="ml-3">
                    <span className={`text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Month-to-Month Comparison</span>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Year-over-year cost & usage comparison charts</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Classic PDF Layout Options */}
          {exportFormat === 'pdf' && (
            <div>
              <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Report Layout</h4>
              <div className="space-y-2">
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={groupByFacility}
                    onChange={(e) => setGroupByFacility(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className={`ml-3 text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Group by Facility (sections)</span>
                </label>
                
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeSummaryPages}
                    onChange={(e) => setIncludeSummaryPages(e.target.checked)}
                    className="w-4 h-4 rounded"
                    disabled={!groupByFacility}
                  />
                  <span className={`ml-3 text-sm ${groupByFacility ? (darkMode ? 'text-gray-100' : 'text-[#000000]') : 'text-gray-400'}`}>Include Summary Pages per Facility</span>
                </label>
              </div>
            </div>
          )}

          {/* Column Options (classic PDF & CSV only) */}
          {!isBento && (
            <div>
              <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Include Columns</h4>
              <div className="space-y-2">
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeDate}
                    onChange={(e) => setIncludeDate(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className={`ml-3 text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Date</span>
                </label>
                
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeFacility}
                    onChange={(e) => setIncludeFacility(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className={`ml-3 text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Facility</span>
                </label>
                
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeType}
                    onChange={(e) => setIncludeType(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className={`ml-3 text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Utility Type</span>
                </label>
                
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeUsage}
                    onChange={(e) => setIncludeUsage(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className={`ml-3 text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Usage</span>
                </label>
                
                <label className={`flex items-center p-2 rounded cursor-pointer ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={includeCost}
                    onChange={(e) => setIncludeCost(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className={`ml-3 text-sm ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Cost</span>
                </label>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              disabled={isExporting}
              className={`flex-1 px-4 py-2 border rounded-lg font-medium transition-colors ${
                darkMode
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-[#000000] hover:bg-gray-50'
              } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`flex-1 px-4 py-2 bg-[#0950A0] text-white rounded-lg hover:bg-[#0950A0]/90 font-medium transition-colors flex items-center justify-center gap-2 ${
                isExporting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isExporting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <><Download className="h-4 w-4" /> Export</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 1.5 MISSING BILLS REPORT MODAL
const MissingBillsModal = ({ monthlyStats, facilities, approvedLocations, onClose, initialParams, darkMode }) => {
  const MISSING_FILTERS_KEY = 'utilitrack_missing_filters';
  const persistedMissing = (() => {
    try {
      const raw = localStorage.getItem(MISSING_FILTERS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const [reportTimeframe, setReportTimeframe] = useState(initialParams?.timeframe || persistedMissing?.timeframe || 'rolling_12');
  const [reportFacility, setReportFacility] = useState(initialParams?.facility || persistedMissing?.facility || 'All');
  const [reportUtilities, setReportUtilities] = useState(initialParams?.utilities || persistedMissing?.utilities || ['electric', 'gas', 'water']);
  const [customStartDate, setCustomStartDate] = useState(initialParams?.customStart || persistedMissing?.customStart || '');
  const [customEndDate, setCustomEndDate] = useState(initialParams?.customEnd || persistedMissing?.customEnd || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(initialParams?.includeInactive || persistedMissing?.includeInactive || false);
  const [reportData, setReportData] = useState(null);
  const [shareLink, setShareLink] = useState('');

  // Build a lookup of inactive facilities with their effective dates
  const inactiveFacilities = useMemo(() => {
    const map = {};
    (approvedLocations || []).forEach(loc => {
      if (loc.inactive) {
        map[loc.name] = loc.inactiveDate || null; // null = inactive forever
      }
    });
    return map;
  }, [approvedLocations]);

  // Filter facilities list based on includeInactive toggle
  const availableFacilities = useMemo(() => {
    if (includeInactive) return facilities;
    return facilities.filter(f => !inactiveFacilities[f]);
  }, [facilities, includeInactive, inactiveFacilities]);

  // Auto-run report if initial params are provided
  useEffect(() => {
    if (initialParams) {
      generateReportFromParams(
        initialParams.timeframe || 'rolling_12',
        initialParams.facility || 'All',
        initialParams.utilities || ['electric', 'gas', 'water'],
        initialParams.customStart || '',
        initialParams.customEnd || ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timeOptions = [
    { id: '3_months', label: 'Last 3 Months' },
    { id: 'rolling_12', label: 'Rolling 12 Months' },
    { id: 'ytd', label: 'Year to Date' },
    { id: '3_years', label: 'Last 3 Years' },
    { id: 'custom', label: 'Custom Date Range' },
  ];

  

  // Persist missing bills filters when they change
  useEffect(() => {
    try {
      localStorage.setItem(MISSING_FILTERS_KEY, JSON.stringify({
        timeframe: reportTimeframe,
        facility: reportFacility,
        utilities: reportUtilities,
        customStart: customStartDate,
        customEnd: customEndDate,
        includeInactive: includeInactive
      }));
    } catch { void 0; }
  }, [reportTimeframe, reportFacility, reportUtilities, customStartDate, customEndDate, includeInactive]);

  const generateReportFromParams = async (timeframe, facility, utilities, startOverride = '', endOverride = '') => {
    setIsGenerating(true);
    try {
    const now = new Date();

    // Get all months in the range
    const months = [];
    let startDate;
    let endDate = now;

    if (timeframe === 'custom' && startOverride && endOverride) {
      // Custom date range: parse YYYY-MM inputs
      const [startYear, startMonth] = startOverride.split('-').map(Number);
      const [endYear, endMonth] = endOverride.split('-').map(Number);
      startDate = new Date(startYear, startMonth - 1, 1);
      endDate = new Date(endYear, endMonth - 1, 1);
    } else {
      const getDaysInRange = () => {
        switch (timeframe) {
          case '3_months': return 90;
          case 'rolling_12': return 365;
          case 'ytd': return 365;
          case '3_years': return 1095;
          default: return 365;
        }
      };
      const days = getDaysInRange();
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - days);
    }

    for (let d = new Date(startDate); d <= endDate; d.setMonth(d.getMonth() + 1)) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      months.push(`${year}-${month}`);
    }

    // Get facilities to check
    const facilitiesToCheck = facility === 'All' ? (includeInactive ? facilities : availableFacilities) : [facility];
    const utilitiesToCheck = utilities.length > 0 ? utilities : ['electric', 'gas', 'water'];

    // --- TWO-SOURCE APPROACH ---
    // Source 1: monthly_stats (aggregated by Cloud Functions — fast but can miss entries
    //           if onBillCreated trigger was rejected by the event-age safety check)
    // Source 2: Direct Firestore bills query (definitive source of truth)
    // A month is only flagged as missing if NEITHER source has a matching entry.

    // Source 1: Fetch fresh stats from server
    let statsToUse = monthlyStats;
    try {
      const result = await getMonthlyStatsFunction({ timeRange: 'all' });
      statsToUse = result.data.stats || [];
      logger.log(`[MissingBillsReport] Fetched ${statsToUse.length} stats from server`);
    } catch (err) {
      logger.warn('[MissingBillsReport] Failed to fetch fresh stats, using cached monthlyStats:', err);
    }

    // Build a lookup set from monthly_stats: "facility_month_utility"
    const statsLookup = new Set(
      statsToUse.map(s => `${s.facility}_${s.month}_${s.utility}`)
    );

    // Source 2: Query actual bills directly from Firestore
    // This is the definitive check — covers placeholders and any bills whose
    // monthly_stats entry wasn't created (e.g., Cloud Function cold-start timeout)
    const billsLookup = new Set();
    try {
      const firstMonth = months[0];       // e.g., "2025-03"
      const lastMonth = months[months.length - 1]; // e.g., "2026-03"
      // Range query on billing_month only (no composite index needed)
      // Filter out archived bills client-side
      const billsQuery = query(
        collection(db, 'bills'),
        where('billing_month', '>=', firstMonth),
        where('billing_month', '<=', lastMonth)
      );
      const billsSnapshot = await getDocs(billsQuery);
      billsSnapshot.docs.forEach(d => {
        const b = d.data();
        if (b.archived) return; // Skip archived bills
        const bMonth = b.billing_month || (b.date ? b.date.substring(0, 7) : '');
        const bType = (b.type || '').toLowerCase();
        if (bMonth && b.site_name) {
          billsLookup.add(`${b.site_name}_${bMonth}_${bType}`);
        }
      });
      logger.log(`[MissingBillsReport] Queried ${billsSnapshot.size} bills directly from Firestore (${firstMonth} to ${lastMonth}), ${billsLookup.size} active entries`);
    } catch (err) {
      logger.warn('[MissingBillsReport] Direct bills query failed, relying on stats only:', err);
    }

    // Find missing bills — check BOTH sources
    const missingByFacility = {};
    facilitiesToCheck.forEach(fac => {
      missingByFacility[fac] = {};
      
      utilitiesToCheck.forEach(utility => {
        missingByFacility[fac][utility] = [];
        
        months.forEach(month => {
          // Skip months after the facility's inactive date (no bill expected)
          const facInactiveDate = inactiveFacilities[fac];
          if (!includeInactive && facInactiveDate && month >= facInactiveDate) {
            return; // Facility was inactive for this month, don't flag as missing
          }

          const key = `${fac}_${month}_${utility}`;

          // Check both monthly_stats AND direct bills
          if (!statsLookup.has(key) && !billsLookup.has(key)) {
            missingByFacility[fac][utility].push(month);
          }
        });
      });
    });

    setReportData(missingByFacility);
    let link = window.location.href.split('?')[0] + `?report=missing_bills&timeframe=${timeframe}&facility=${facility}&utilities=${utilities.join(',')}`;
    if (timeframe === 'custom' && startOverride && endOverride) {
      link += `&customStart=${startOverride}&customEnd=${endOverride}`;
    }
    setShareLink(link);
    } catch (err) {
      logger.error('[MissingBillsReport] Error generating report:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateReport = () => {
    generateReportFromParams(reportTimeframe, reportFacility, reportUtilities, customStartDate, customEndDate);
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    alert('Share link copied to clipboard!');
  };

  const exportToPDF = async () => {
    try {
      logger.log('Starting PDF export...');
      
      const doc = new jsPDF();
      
      // Add logo to top right with compression
      const logoImg = new Image();
      logoImg.src = '/logo_multi.png';
      
      await new Promise((resolve) => {
        logoImg.onload = () => {
          // Compress the logo image
          const compressedImageData = compressImage(logoImg, 0.8);
          
          // Position logo in top right (50% larger: width=45, height=22.5)
          doc.addImage(compressedImageData, 'JPEG', 155, 10, 45, 22.5);
          resolve();
        };
      });
      
      doc.setFontSize(16);
      doc.text('Missing Bills Report', 14, 15);
      
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 25);
      
      const tableColumn = ['Facility', 'Utility', 'Missing Months'];
      const tableRows = [];
      
      Object.entries(reportData).forEach(([facility, utilities]) => {
        Object.entries(utilities).forEach(([utility, missingMonths]) => {
          if (missingMonths.length > 0) {
            const monthsText = missingMonths.map(m => {
              const [year, month] = m.split('-');
              return new Date(year, parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            }).join(', ');
            tableRows.push([facility, utility, monthsText]);
          }
        });
      });
      
      logger.log('Table rows:', tableRows.length);
      
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 35,
        theme: 'grid',
        headStyles: {
          fillColor: '#14007a',
          textColor: '#ffffff',
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40 },
          2: { cellWidth: 80 }
        }
      });
      
      logger.log('Saving PDF...');
      doc.save(`missing_bills_report_${new Date().toISOString().split('T')[0]}.pdf`);
      logger.log('PDF saved successfully');
    } catch (error) {
      logger.error('Error exporting PDF:', error);
      alert('Failed to export PDF: ' + error.message);
    }
  };

  const exportToExcel = () => {
    const rows = [];
    rows.push(['Facility', 'Utility', 'Missing Months']);
    
    Object.entries(reportData).forEach(([facility, utilities]) => {
      Object.entries(utilities).forEach(([utility, missingMonths]) => {
        if (missingMonths.length > 0) {
          const monthsText = missingMonths.map(m => {
            const [year, month] = m.split('-');
            return new Date(year, parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          }).join(', ');
          rows.push([facility, utility, monthsText]);
        }
      });
    });
    
    const csvContent = rows.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `missing_bills_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-200 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        
        {/* Header */}
        <div className={`sticky top-0 border-b p-6 flex items-center justify-between transition-colors duration-200 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Missing Bills Report</h2>
          <button
            onClick={onClose}
            className={`text-2xl ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          
          {/* Filter Section */}
          {!reportData && (
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-lg transition-colors duration-200 ${
              darkMode ? 'bg-gray-700' : 'bg-gray-50'
            }`}>
              
              {/* Timeframe */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Timeframe</label>
                <select
                  value={reportTimeframe}
                  onChange={(e) => setReportTimeframe(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:border-[#0950A0] focus:ring-1 focus:ring-[#0950A0] outline-none text-sm transition-colors ${
                    darkMode ? 'bg-gray-600 border-gray-600 text-gray-100' : 'border-gray-300 text-gray-900'
                  }`}
                >
                  {timeOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Custom Date Range Pickers */}
              {reportTimeframe === 'custom' && (
                <div className="col-span-2">
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date Range</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="month"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:border-[#0950A0] focus:ring-1 focus:ring-[#0950A0] outline-none text-sm transition-colors ${
                        darkMode ? 'bg-gray-600 border-gray-600 text-gray-100' : 'border-gray-300 text-gray-900'
                      }`}
                    />
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>to</span>
                    <input
                      type="month"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:border-[#0950A0] focus:ring-1 focus:ring-[#0950A0] outline-none text-sm transition-colors ${
                        darkMode ? 'bg-gray-600 border-gray-600 text-gray-100' : 'border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  {customStartDate && customEndDate && customStartDate > customEndDate && (
                    <p className="text-xs text-red-500 mt-1">Start date must be before end date</p>
                  )}
                </div>
              )}

              {/* Facility */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Facility</label>
                <select
                  value={reportFacility}
                  onChange={(e) => setReportFacility(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:border-[#0950A0] focus:ring-1 focus:ring-[#0950A0] outline-none text-sm transition-colors ${
                    darkMode ? 'bg-gray-600 border-gray-600 text-gray-100' : 'border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="All">All Facilities</option>
                  {availableFacilities.map(f => (
                    <option key={f} value={f}>{f}{inactiveFacilities[f] !== undefined ? ' (Inactive)' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Utility Type */}
              <div className="col-span-3">
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Utility Types</label>
                <div className="flex gap-4">
                  {['electric', 'gas', 'water'].map(utility => (
                    <label key={utility} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reportUtilities.includes(utility)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReportUtilities([...reportUtilities, utility]);
                          } else {
                            setReportUtilities(reportUtilities.filter(u => u !== utility));
                          }
                        }}
                        className="rounded border-gray-300 text-[#0950A0] focus:ring-[#0950A0] cursor-pointer"
                      />
                      <span className={`text-sm font-medium capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{utility}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Include Inactive Locations */}
              {Object.keys(inactiveFacilities).length > 0 && (
                <div className="col-span-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeInactive}
                      onChange={(e) => {
                        setIncludeInactive(e.target.checked);
                        // Reset facility selection if current selection is inactive and we're hiding inactive
                        if (!e.target.checked && inactiveFacilities[reportFacility]) {
                          setReportFacility('All');
                        }
                      }}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
                    />
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Include inactive locations</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700'}`}>
                      {Object.keys(inactiveFacilities).length} inactive
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Run Report Button */}
          {!reportData && (
            <button
              onClick={generateReport}
              disabled={isGenerating || (reportTimeframe === 'custom' && (!customStartDate || !customEndDate || customStartDate > customEndDate))}
              className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                isGenerating || (reportTimeframe === 'custom' && (!customStartDate || !customEndDate || customStartDate > customEndDate))
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-[#0950A0] text-white hover:bg-[#0950A0]/90'
              }`}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading Stats...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" fill="currentColor" />
                  Run Report
                </>
              )}
            </button>
          )}

          {/* Report Results */}
          {reportData && (
            <div className="space-y-4">
              
              {/* Share and Export Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                />
                <button
                  onClick={copyShareLink}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Download className="h-4 w-4" />
                  Copy Link
                </button>
                <button
                  onClick={exportToPDF}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Download className="h-4 w-4" />
                  Export PDF
                </button>
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Download className="h-4 w-4" />
                  Export Excel
                </button>
              </div>

              {/* Results Table */}
              {Object.values(reportData).some(utilities => 
                Object.values(utilities).some(months => months.length > 0)
              ) ? (
                <div className={`border rounded-lg overflow-hidden transition-colors duration-200 ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <table className="w-full text-sm">
                    <thead className={`border-b transition-colors duration-200 ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'
                    }`}>
                      <tr>
                        <th className={`px-4 py-2 text-left font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Facility</th>
                        <th className={`px-4 py-2 text-left font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Utility</th>
                        <th className={`px-4 py-2 text-left font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Missing Months</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {Object.entries(reportData).flatMap(([facility, utilities]) =>
                        Object.entries(utilities)
                          .filter(([, months]) => months.length > 0)
                          .map(([utility, missingMonths]) => (
                            <tr key={`${facility}-${utility}`} className={`transition-colors ${
                              darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                            }`}>
                              <td className={`px-4 py-2 font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{facility}</td>
                              <td className="px-4 py-2">
                                <span className={`capitalize px-2 py-1 rounded text-xs font-semibold ${
                                  utility === 'electric' ? 'bg-yellow-100 text-yellow-800' :
                                  utility === 'gas' ? 'bg-orange-100 text-orange-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {utility}
                                </span>
                              </td>
                              <td className={`px-4 py-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {missingMonths.map(m => {
                                  const [year, month] = m.split('-');
                                  return new Date(year, parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                                }).join(' • ')}
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-900 font-semibold text-sm">All bills are up to date!</p>
                  <p className="text-green-700 text-xs mt-1">No missing utility bills found for the selected criteria.</p>
                </div>
              )}

              {/* Back Button */}
              <button
                onClick={() => setReportData(null)}
                className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm"
              >
                Modify Report
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 2. DASHBOARD
const UTILITY_CHART_COLORS = {
  electric: '#3B82F6', // electric blue
  gas: '#FB923C', // bright orange
  water: '#0EA5E9', // cyan
  sewer: '#3B82F6', // swapped from cost trends
  trash: '#FF2D95', // electric pink
  other: '#FF00FF', // hot magenta
};

const Dashboard = ({ bills, _generatePDF, showMissingBillsModal, setShowMissingBillsModal, reportParams, darkMode, monthlyStats, statsLoading, approvedLocations }) => {
  const DASHBOARD_FILTERS_KEY = 'utilitrack_dashboard_filters';
  const bentoExporterRef = useRef(null);
  const persisted = (() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_FILTERS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const [selectedFacility, setSelectedFacility] = useState(persisted?.selectedFacility || 'All Facilities');
  const [timeRange, setTimeRange] = useState(persisted?.timeRange || 'rolling_12');
  const [usageUtilityFilter, setUsageUtilityFilter] = useState(persisted?.usageUtilityFilter || 'all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUtilityFilterModal, setShowUtilityFilterModal] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
    return localStorage.getItem('utilitrack_dashboard_filters_collapsed') === 'true';
  });
  const [visibleCharts, setVisibleCharts] = useState(() => {
    const saved = localStorage.getItem('utilitrack_main_visible_charts');
    return saved ? JSON.parse(saved) : ['cost_trends', 'usage_trends', 'cost_breakdown'];
  });
  const [showDashboardCustomizer, setShowDashboardCustomizer] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);

  const [visibleUtilities, setVisibleUtilities] = useState(() => {
    const saved = localStorage.getItem('utilitrak_main_visible_utilities');
    return saved ? JSON.parse(saved) : ['electric', 'gas', 'water'];
  });

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem('utilitrack_dashboard_filters_collapsed', filtersCollapsed);
  }, [filtersCollapsed]);

  // Persist visible charts
  useEffect(() => {
    localStorage.setItem('utilitrack_main_visible_charts', JSON.stringify(visibleCharts));
  }, [visibleCharts]);

  // Persist visible utilities
  useEffect(() => {
    localStorage.setItem('utilitrak_main_visible_utilities', JSON.stringify(visibleUtilities));
  }, [visibleUtilities]);

  // Persist filters when they change
  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_FILTERS_KEY, JSON.stringify({
        selectedFacility,
        timeRange,
        usageUtilityFilter
      }));
    } catch { void 0; }
  }, [selectedFacility, timeRange, usageUtilityFilter]);

  // --- RESTORED MISSING DEFINITION ---
  const timeOptions = [
    { id: '3_months', label: 'Last 3 Months' },
    { id: 'rolling_12', label: 'Rolling 12 Months' },
    { id: 'ytd', label: 'Year to Date (1 Year)' },
    { id: '3_years', label: 'Last 3 Years' },
    { id: '5_years', label: 'Last 5 Years' },
  ];

  // --- DATE HELPER ---
  // Converts "2025-01-15" -> "Jan 25" for pretty labels
  const formatDateLabel = (dateString) => {
    // Check if valid date string
    if (!dateString) return "N/A";
    
    // Parse YYYY-MM format directly to avoid timezone issues
    if (dateString.length === 7) {
      const [year, month] = dateString.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const yearShort = year.slice(-2);
      return `${monthNames[parseInt(month) - 1]} ${yearShort}`;
    }
    
    // For full dates, parse similarly to avoid timezone shift
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const hexToRgb = (hex) => {
    const normalized = hex.replace('#', '');
    const full = normalized.length === 3
      ? normalized.split('').map((ch) => ch + ch).join('')
      : normalized;
    const intVal = parseInt(full, 16);
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  };

  const rgbaFromHex = (hex, alpha) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const chartGlowStyle = (hex, isDark) => {
    const outerStrong = rgbaFromHex(hex, isDark ? 0.225 : 0.11);
    const outerSoft = rgbaFromHex(hex, isDark ? 0.14 : 0.06);
    return {
      borderColor: isDark ? hex : rgbaFromHex(hex, 0.5),
      boxShadow: isDark
        ? `0 0 18px ${outerStrong}, 0 0 32px ${outerSoft}`
        : `0 6px 18px ${outerStrong}, 0 10px 28px ${outerSoft}`,
    };
  };

  // --- FILTERING LOGIC ---
  // With server-side stats transition, bills may not be fully loaded.
  // Use monthlyStats as primary data source for all KPIs and charts.
  const filteredBills = useMemo(() => bills.filter(bill => {
    // Only active (non-archived) bills should be considered
    if (bill.archived) return false;
    
    // 1. Utility Type Filter (skip if 'all' is selected)
    if (usageUtilityFilter !== 'all' && bill.type !== usageUtilityFilter) {
      return false;
    }
    
    // 2. Facility Filter
    if (selectedFacility !== 'All Facilities' && bill.site_name !== selectedFacility) {
      return false;
    }

    // 3. Time Range Filter
    // Parse date with time component to avoid timezone shifts
    const billDate = new Date(bill.date + 'T00:00:00');
    const now = new Date();
    const diffTime = now - billDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 

    switch (timeRange) {
      case '3_months': return diffDays >= -90 && diffDays <= 90;
      case 'rolling_12': return diffDays >= -30 && diffDays <= 365;
      case 'ytd': return billDate.getFullYear() === now.getFullYear();
      case '3_years': return diffDays >= -30 && diffDays <= (365 * 3);
      case '5_years': return diffDays >= -30 && diffDays <= (365 * 5);
      default: return true;
    }
  }), [bills, selectedFacility, timeRange, usageUtilityFilter]);

  // Extract unique facility names from monthlyStats (more reliable than bills which may not be fully loaded)
  const actualFacilities = useMemo(() => {
    const fromStats = [...new Set(monthlyStats.map(s => s.facility))];
    const fromBills = [...new Set(filteredBills.map(b => b.site_name))];
    return [...new Set([...fromStats, ...fromBills])].sort();
  }, [monthlyStats, filteredBills]);
  const facilityOptions = ['All Facilities', ...actualFacilities];

  // --- AGGREGATION FROM MONTHLY STATS (Efficient) ---
  // Filter monthly stats based on facility and time range
  const filteredStats = useMemo(() => {
    if (statsLoading || monthlyStats.length === 0) {
      // Fallback to bills-based calculation if stats not ready
      return null;
    }

    const now = new Date();
    return monthlyStats.filter(stat => {
      // Facility filter
      if (selectedFacility !== 'All Facilities' && stat.facility !== selectedFacility) {
        return false;
      }

      // Time range filter
      const statDate = new Date(stat.month + '-01T00:00:00');
      const diffTime = now - statDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      switch (timeRange) {
        case '3_months': return diffDays >= -90 && diffDays <= 90;
        case 'rolling_12': return diffDays >= -30 && diffDays <= 365;
        case 'ytd': return statDate.getFullYear() === now.getFullYear();
        case '3_years': return diffDays >= -30 && diffDays <= (365 * 3);
        case '5_years': return diffDays >= -30 && diffDays <= (365 * 5);
        default: return true;
      }
    });
  }, [statsLoading, monthlyStats, selectedFacility, timeRange]);

  // Use stats as primary data source for KPIs (bills may not be fully loaded)
  const totalCost = useMemo(() => {
    if (filteredStats && filteredStats.length > 0) {
      const utilityStats = usageUtilityFilter === 'all' ? filteredStats : filteredStats.filter(s => s.utility === usageUtilityFilter);
      return utilityStats.reduce((sum, stat) => sum + (stat.totalCost || 0), 0);
    }
    return filteredBills.reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0);
  }, [filteredStats, filteredBills, usageUtilityFilter]);
    
  const totalDocs = useMemo(() => {
    if (filteredStats && filteredStats.length > 0) {
      const utilityStats = usageUtilityFilter === 'all' ? filteredStats : filteredStats.filter(s => s.utility === usageUtilityFilter);
      return utilityStats.reduce((sum, stat) => sum + (stat.count || 0), 0);
    }
    return filteredBills.length;
  }, [filteredStats, filteredBills, usageUtilityFilter]);
  
  // --- CHART DATA FROM STATS ---
  
  // 1. Cost by Month Chart
  const costMap = {};
  if (filteredStats) {
    const utilityStats = usageUtilityFilter === 'all' ? filteredStats : filteredStats.filter(s => s.utility === usageUtilityFilter);
    utilityStats.forEach(stat => {
      const month = stat.month;
      if (!costMap[month]) costMap[month] = 0;
      costMap[month] += stat.totalCost || 0;
    });
  } else {
    const utilityBills = usageUtilityFilter === 'all' ? filteredBills : filteredBills.filter(b => b.type === usageUtilityFilter);
    utilityBills.forEach(bill => {
      const monthKey = (bill.date || "").substring(0, 7);
      if (monthKey.length === 7) {
        if (!costMap[monthKey]) costMap[monthKey] = 0;
        costMap[monthKey] += parseFloat(bill.amount || 0);
      }
    });
  }

  const costChartData = Object.keys(costMap).sort().map(key => ({
    name: formatDateLabel(key),
    sortKey: key,
    cost: costMap[key]
  }));

  // 2. Usage by Month Chart — broken out by utility type to avoid mixing units

  const perUtilityUsageData = useMemo(() => {
    // Get the utility types to chart
    const targetTypes = usageUtilityFilter === 'all'
      ? [...new Set((filteredStats || []).map(s => s.utility).concat(filteredBills.map(b => b.type)))].filter(Boolean).sort()
      : [usageUtilityFilter];

    return targetTypes.map(ut => {
      const usageMap = {};
      let unit = 'units';

      if (filteredStats) {
        filteredStats.filter(s => s.utility === ut).forEach(stat => {
          if (!usageMap[stat.month]) usageMap[stat.month] = 0;
          usageMap[stat.month] += stat.totalUsage || 0;
          unit = stat.unit || 'units';
        });
      } else {
        filteredBills.filter(b => b.type === ut).forEach(bill => {
          const monthKey = (bill.date || '').substring(0, 7);
          if (monthKey.length === 7) {
            if (!usageMap[monthKey]) usageMap[monthKey] = 0;
            usageMap[monthKey] += parseFloat(bill.usage || 0);
          }
        });
        const firstBill = filteredBills.find(b => b.type === ut);
        unit = firstBill?.unit || 'units';
      }

      const data = Object.keys(usageMap).sort().map(key => ({
        name: formatDateLabel(key),
        usage: usageMap[key],
      }));

      return { utility: ut, unit, data, color: UTILITY_CHART_COLORS[ut] || '#8B5CF6' };
    }).filter(entry => entry.data.length > 0 && visibleUtilities.includes(entry.utility));
  }, [filteredStats, filteredBills, usageUtilityFilter, visibleUtilities]);

  // Legacy single-chart data (used when a specific utility is selected)
  const _currentUnit = perUtilityUsageData.length === 1 ? perUtilityUsageData[0].unit : 'units';
  const _usageChartData = perUtilityUsageData.length === 1 ? perUtilityUsageData[0].data : [];

  // 3. Breakdown Chart Data (Portfolio Cost Contribution)
  const breakdownChartData = useMemo(() => {
    if (!filteredStats) return [];
    
    const aggregated = {};
    filteredStats.forEach(stat => {
      if (!aggregated[stat.month]) {
        aggregated[stat.month] = { month: stat.month, name: formatDateLabel(stat.month) };
      }
      const costKey = `${stat.facility}_cost`;
      aggregated[stat.month][costKey] = (aggregated[stat.month][costKey] || 0) + (stat.totalCost || 0);
    });
    
    return Object.values(aggregated).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredStats]);

  const dashboardChartOptions = [
    { id: 'cost_trends', label: 'Cost Trends', description: 'Monthly expenditure across selected sites.' },
    { id: 'usage_trends', label: 'Usage Trends', description: 'Volume consumption by utility type.' },
    { id: 'cost_breakdown', label: 'Portfolio Cost Contribution', description: 'Stacked breakdown of costs per facility.' }
  ];

  const siteColors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];
  const facilityNamesForChart = useMemo(() => actualFacilities.map(name => ({ name })), [actualFacilities]);

  // --- EXPORT HANDLER ---
  const handleExport = async (format, options) => {
    try {
      logger.log('Export handler called with format:', format, 'options:', options);
      if (format === 'bento_pdf') {
        // Use the new bento-style visual PDF exporter
        if (!bentoExporterRef.current) {
          alert('Report engine is loading. Please try again in a moment.');
          return;
        }
        setExportProgress('Initializing...');
        await bentoExporterRef.current.generateBentoPDF({
          groupByFacility: options.groupByFacility,
          includeSummaryPages: options.includeSummaryPages,
          includeComparisonCharts: options.includeComparisonCharts,
          includeMonthlyComparison: options.includeMonthlyComparison,
        });
        setExportProgress(null);
        setShowExportModal(false);
      } else if (format === 'pdf') {
        await exportToPDFWithOptions(options);
        setShowExportModal(false);
      } else {
        await exportToExcelWithOptions(options);
        setShowExportModal(false);
      }
    } catch (error) {
      logger.error('Error in handleExport:', error);
      setExportProgress(null);
      alert('Error exporting report: ' + error.message);
    }
  };

  const exportToPDFWithOptions = async (options) => {
    try {
      logger.log('Starting PDF export with options:', options);
      
      // Fetch bill data from server for export
      let exportBills = filteredBills;
      if (exportBills.length === 0) {
        const result = await exportBillDataFunction({
          facility: selectedFacility !== 'All Facilities' ? selectedFacility : undefined,
          type: usageUtilityFilter !== 'all' ? usageUtilityFilter : undefined,
          timeRange: timeRange
        });
        exportBills = result.data.bills || [];
      }
      
      const doc = new jsPDF();
      
      // Add logo
      const logoImg = new Image();
      logoImg.src = '/logo_multi.png';
      
      await new Promise((resolve) => {
        logoImg.onload = () => {
          const compressedImageData = compressImage(logoImg, 0.8);
          doc.addImage(compressedImageData, 'JPEG', 155, 10, 45, 22.5);
          resolve();
        };
      });
      
      doc.setFontSize(18);
      doc.text('Utility Bills Report', 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);
      
      let yPosition = 38;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 14;
      
      if (options.groupByFacility) {
        // Group bills by facility
        const billsByFacility = {};
        exportBills.forEach(bill => {
          if (!billsByFacility[bill.site_name]) {
            billsByFacility[bill.site_name] = [];
          }
          billsByFacility[bill.site_name].push(bill);
        });
        
        // Generate summary pages if requested
        if (options.includeSummaryPages) {
          // Add title
          doc.setFontSize(14);
          doc.setTextColor(9, 80, 160);
          doc.text('Facility Summary', margin, yPosition);
          doc.setTextColor(0, 0, 0);
          yPosition += 8;
          
          // Build summary data for table
          const summaryRows = [];
          Object.keys(billsByFacility).sort().forEach((facility) => {
            const facilityBills = billsByFacility[facility];
            const totalCost = facilityBills.reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0);
            const totalUsage = facilityBills.reduce((sum, bill) => sum + parseFloat(bill.usage || 0), 0);
            
            // Get utility types for this facility
            const utilityTypes = [...new Set(facilityBills.map(b => b.type))].sort().join(', ');
            
            summaryRows.push([
              facility,
              facilityBills.length.toString(),
              `$${totalCost.toFixed(2)}`,
              `${totalUsage.toFixed(2)}`,
              utilityTypes
            ]);
          });
          
          // Create summary table
          autoTable(doc, {
            head: [['Facility', 'Bill Count', 'Total Cost', 'Total Usage', 'Utility Types']],
            body: summaryRows,
            startY: yPosition,
            margin: margin,
            theme: 'grid',
            headStyles: {
              fillColor: '#0950A0',
              textColor: '#ffffff',
              fontStyle: 'bold',
              fontSize: 10
            },
            bodyStyles: {
              fontSize: 9
            },
            columnStyles: {
              0: { cellWidth: 50 },
              1: { cellWidth: 25 },
              2: { cellWidth: 30 },
              3: { cellWidth: 30 },
              4: { cellWidth: 45 }
            }
          });
          
          // Always add page break after summary table
          doc.addPage();
          yPosition = 20;
        }
        
        Object.keys(billsByFacility).sort().forEach((facility) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            doc.addPage();
            yPosition = 20;
          }
          
          // Facility header
          doc.setFontSize(12);
          doc.setTextColor(9, 80, 160);
          doc.text(facility, margin, yPosition);
          yPosition += 8;
          
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(10);
          
          // Group bills by utility type for this facility
          const billsByUtility = {};
          billsByFacility[facility].forEach(bill => {
            if (!billsByUtility[bill.type]) {
              billsByUtility[bill.type] = [];
            }
            billsByUtility[bill.type].push(bill);
          });
          
          // Create a table for each utility type
          Object.keys(billsByUtility).sort().forEach((utilityType) => {
            // Check if we need a new page
            if (yPosition > pageHeight - 40) {
              doc.addPage();
              yPosition = 20;
            }
            
            // Utility type subheader
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`${utilityType.charAt(0).toUpperCase() + utilityType.slice(1)}`, margin + 5, yPosition);
            yPosition += 6;
            
            doc.setTextColor(0, 0, 0);
            
            // Build table columns
            const columns = [];
            if (options.includeDate) columns.push('Date');
            if (options.includeUsage) columns.push('Usage');
            if (options.includeCost) columns.push('Cost ($)');
            
            const rows = billsByUtility[utilityType].map(bill => {
              const row = [];
              if (options.includeDate) row.push(bill.date);
              if (options.includeUsage) row.push(`${bill.usage} ${bill.unit}`);
              if (options.includeCost) row.push(parseFloat(bill.amount || 0).toFixed(2));
              return row;
            });
            
            autoTable(doc, {
              head: [columns],
              body: rows,
              startY: yPosition,
              margin: margin
            });
            
            yPosition = doc.lastAutoTable.finalY + 8;
          });
          
          yPosition += 2;
        });
      } else {
        // No grouping - all bills in one table
        const columns = [];
        if (options.includeDate) columns.push('Date');
        if (options.includeFacility) columns.push('Facility');
        if (options.includeType) columns.push('Type');
        if (options.includeUsage) columns.push('Usage');
        if (options.includeCost) columns.push('Cost ($)');
        
        const rows = exportBills.map(bill => {
          const row = [];
          if (options.includeDate) row.push(bill.date);
          if (options.includeFacility) row.push(bill.site_name);
          if (options.includeType) row.push(bill.type);
          if (options.includeUsage) row.push(`${bill.usage} ${bill.unit}`);
          if (options.includeCost) row.push(parseFloat(bill.amount || 0).toFixed(2));
          return row;
        });
        
        autoTable(doc, {
          head: [columns],
          body: rows,
          startY: yPosition,
          theme: 'grid',
          headStyles: {
            fillColor: '#0950A0',
            textColor: '#ffffff',
            fontStyle: 'bold'
          }
        });
      }
      
      doc.save(`UtiliTrack_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      logger.error('Error exporting PDF:', error);
      alert('Failed to export PDF: ' + error.message);
    }
  };

  const exportToExcelWithOptions = async (options) => {
    // Fetch bill data from server for export
    let exportBillsForCsv = filteredBills;
    if (exportBillsForCsv.length === 0) {
      const result = await exportBillDataFunction({
        facility: selectedFacility !== 'All Facilities' ? selectedFacility : undefined,
        type: usageUtilityFilter !== 'all' ? usageUtilityFilter : undefined,
        timeRange: timeRange
      });
      exportBillsForCsv = result.data.bills || [];
    }
    
    const rows = [];
    
    if (options.groupByFacility) {
      const billsByFacility = {};
      exportBillsForCsv.forEach(bill => {
        if (!billsByFacility[bill.site_name]) {
          billsByFacility[bill.site_name] = [];
        }
        billsByFacility[bill.site_name].push(bill);
      });
      
      // Add headers
      const headers = [];
      if (options.includeDate) headers.push('Date');
      if (options.includeType) headers.push('Type');
      if (options.includeUsage) headers.push('Usage');
      if (options.includeCost) headers.push('Cost ($)');
      
      Object.keys(billsByFacility).sort().forEach((facility) => {
        rows.push([facility]); // Section header
        rows.push(headers);
        
        billsByFacility[facility].forEach(bill => {
          const row = [];
          if (options.includeDate) row.push(bill.date);
          if (options.includeType) row.push(bill.type);
          if (options.includeUsage) row.push(`${bill.usage} ${bill.unit}`);
          if (options.includeCost) row.push(parseFloat(bill.amount || 0).toFixed(2));
          rows.push(row);
        });
        
        rows.push([]); // Empty row for spacing
      });
    } else {
      // No grouping
      const headers = [];
      if (options.includeDate) headers.push('Date');
      if (options.includeFacility) headers.push('Facility');
      if (options.includeType) headers.push('Type');
      if (options.includeUsage) headers.push('Usage');
      if (options.includeCost) headers.push('Cost ($)');
      
      rows.push(headers);
      
      exportBillsForCsv.forEach(bill => {
        const row = [];
        if (options.includeDate) row.push(bill.date);
        if (options.includeFacility) row.push(bill.site_name);
        if (options.includeType) row.push(bill.type);
        if (options.includeUsage) row.push(`${bill.usage} ${bill.unit}`);
        if (options.includeCost) row.push(parseFloat(bill.amount || 0).toFixed(2));
        rows.push(row);
      });
    }
    
    // Convert to CSV
    const csvContent = rows.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `UtiliTrack_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const costChartColor = '#10B981'; // emerald green
  const costGlowColor = '#047857'; // darker emerald for glow

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {/* HEADER SECTION */}
      <div className="card-dashboard-cyan flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Facility Overview</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Track trends across locations and time</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
              className={`p-2 rounded-lg transition-all ${filtersCollapsed ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              title={filtersCollapsed ? "Show Filters" : "Hide Filters"}
            >
              <Filter className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowDashboardCustomizer(!showDashboardCustomizer)}
              className={`p-2 rounded-lg transition-all ${showDashboardCustomizer ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              title="Dashboard Options"
            >
              <Settings className="h-5 w-5" />
            </button>
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="lg:hidden p-2 rounded-lg bg-cyan-500/10 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 transition-colors hover:bg-cyan-500/20 dark:hover:bg-cyan-500/30"
              aria-label="Toggle filters"
            >
              <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
        
        <div className={`flex-col sm:flex-row gap-3 lg:!flex ${filtersExpanded ? 'flex' : 'hidden'} ${filtersCollapsed ? 'lg:!hidden' : ''}`}>
          {/* EXPORT BUTTON */}
          <button
            onClick={() => setShowExportModal(true)}
            className="btn-dashboard-outline inline-flex items-center justify-center px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <Download className="mr-2 h-4 w-4" />
            Export Report
          </button>

          {/* MISSING BILLS REPORT BUTTON */}
          <button
            onClick={() => setShowMissingBillsModal(true)}
            className="btn-dashboard-accent inline-flex items-center justify-center px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <AlertCircle className="mr-2 h-4 w-4" />
            Missing Bills Report
          </button>

          {/* FACILITY FILTER */}
          <div className="relative min-w-[200px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </div>
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="input-dashboard block w-full pl-10 pr-8 py-2 text-sm appearance-none"
            >
              {facilityOptions.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <ChevronRight className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400" />
            </div>
          </div>

          {/* TIME RANGE FILTER */}
          <div className="relative min-w-[200px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="input-dashboard block w-full pl-10 pr-8 py-2 text-sm appearance-none"
            >
              {timeOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <ChevronRight className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400" />
            </div>
          </div>

          {/* UTILITY TYPE FILTER BUTTON */}
          <button
            onClick={() => setShowUtilityFilterModal(true)}
            className="btn-dashboard-outline inline-flex items-center justify-center px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <Filter className="mr-2 h-4 w-4" />
            Utilities
          </button>
        </div>
      </div>
      
      {/* MAIN CONTENT + INSIGHTS SIDEBAR */}
      <div className="flex gap-6 items-start">
        {/* Left: KPIs + Charts */}
        <div className="flex-1 min-w-0 space-y-6">
      {/* KPIS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card-dashboard-cyan p-6 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Spend</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white truncate">${totalCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
              <div className="text-xs mt-1 capitalize text-gray-400 dark:text-gray-500">{timeRange.replace('_', ' ')}</div>
            </div>
            <div className="p-3 bg-cyan-500/10 dark:bg-cyan-500/20 rounded-full shrink-0">
              <span className="text-cyan-600 dark:text-cyan-400 font-bold">$</span>
            </div>
          </div>
        </div>
        <div className="card-dashboard-cyan p-6 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Bills Analyzed</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalDocs}</p>
              <div className="text-xs mt-1 text-gray-400 dark:text-gray-500">In selected period</div>
            </div>
            <div className="p-3 bg-cyan-500/10 dark:bg-cyan-500/20 rounded-full shrink-0">
              <FileText className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        </div>
        <div className="card-dashboard-cyan p-6 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Facilities</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {selectedFacility === 'All Facilities' ? actualFacilities.length : 1}
              </p>
              <div className="text-xs mt-1 text-gray-400 dark:text-gray-500">Active locations</div>
            </div>
            <div className="p-3 bg-cyan-500/10 dark:bg-cyan-500/20 rounded-full shrink-0">
              <Building2 className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        </div>
      </div>

      {/* FACILITY INSIGHTS — mobile/tablet only (before charts) */}
      <div className="xl:hidden">
        <InsightsSidebar monthlyStats={monthlyStats} locations={approvedLocations} />
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
        {/* Cost Chart */}
        {visibleCharts.includes('cost_trends') && (
          <div className="card-dashboard p-6" style={chartGlowStyle(costGlowColor, darkMode)}>
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Cost Trends</h3>
            <div className="h-64 overflow-hidden relative" style={{ isolation: 'isolate', clipPath: 'inset(0)' }}>
               {costChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={costChartData}>
                      <CartesianGrid stroke={darkMode ? '#0ea5e920' : '#f0f0f0'} horizontal={true} vertical={false} />
                      <XAxis dataKey="name" stroke={darkMode ? '#67e8f9' : '#6b7280'} fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke={darkMode ? '#67e8f9' : '#6b7280'} fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#121212' : '#fff', 
                          borderRadius: '8px', 
                          border: `1px solid ${rgbaFromHex(costChartColor, darkMode ? 0.9 : 0.4)}`,
                          boxShadow: darkMode
                            ? `0 0 15px ${rgbaFromHex(costChartColor, 0.225)}`
                            : `0 4px 15px ${rgbaFromHex(costChartColor, 0.11)}`,
                          color: darkMode ? '#f3f4f6' : '#111827' 
                        }}
                        labelStyle={{ color: darkMode ? '#f3f4f6' : '#111827' }}
                        itemStyle={{ color: costChartColor }}
                        formatter={(value) => [`$${value.toFixed(2)}`, "Cost"]}
                      />
                      <Line type="monotone" dataKey="cost" stroke={costChartColor} strokeWidth={3} dot={{ fill: costChartColor, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
               ) : (
                 <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400 dark:text-gray-500">
                   <Search className="h-8 w-8 opacity-50" />
                   <p>No cost data for this period</p>
                 </div>
               )}
            </div>
          </div>
        )}


        {/* Usage Charts — one per utility type when showing all */}
        {visibleCharts.includes('usage_trends') && perUtilityUsageData.length > 0 ? (
          perUtilityUsageData.map((entry) => (
            <div key={entry.utility} className="card-dashboard p-6 relative" style={chartGlowStyle(entry.color, darkMode)}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {entry.utility.charAt(0).toUpperCase() + entry.utility.slice(1)} Usage
                  <span className="text-sm font-normal ml-2 text-gray-500 dark:text-gray-400">({entry.unit})</span>
                </h3>
              </div>
              
              <div className="h-64 overflow-hidden relative" style={{ isolation: 'isolate', clipPath: 'inset(0)' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={entry.data}>
                    <CartesianGrid stroke={darkMode ? '#0ea5e920' : '#f0f0f0'} horizontal={true} vertical={false} />
                    <XAxis dataKey="name" stroke={darkMode ? '#67e8f9' : '#6b7280'} fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke={darkMode ? '#67e8f9' : '#6b7280'} fontSize={12} tickLine={false} axisLine={false} label={{ value: entry.unit, angle: -90, position: 'insideLeft', style: { fill: darkMode ? '#67e8f9' : '#6b7280', fontSize: 10 } }} />
                    <Tooltip 
                      cursor={{ fill: rgbaFromHex(entry.color, darkMode ? 0.12 : 0.08) }} 
                      contentStyle={{ 
                        backgroundColor: darkMode ? '#121212' : '#fff', 
                        borderRadius: '8px', 
                        border: `1px solid ${rgbaFromHex(entry.color, darkMode ? 0.9 : 0.4)}`,
                        boxShadow: darkMode
                          ? `0 0 15px ${rgbaFromHex(entry.color, 0.225)}`
                          : `0 4px 15px ${rgbaFromHex(entry.color, 0.11)}`,
                        color: darkMode ? '#f3f4f6' : '#111827' 
                      }} 
                      labelStyle={{ color: darkMode ? '#f3f4f6' : '#111827' }}
                      itemStyle={{ color: entry.color }}
                    />
                    <Bar dataKey="usage" fill={entry.color} radius={[4, 4, 0, 0]} barSize={30} name={`${entry.utility.charAt(0).toUpperCase() + entry.utility.slice(1)} (${entry.unit})`} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))
        ) : (
          visibleCharts.includes('usage_trends') && (
            <div className="card-dashboard p-6 relative">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Volume</h3>
              </div>
              <div className="h-64 overflow-hidden relative" style={{ isolation: 'isolate', clipPath: 'inset(0)' }}>
                <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400 dark:text-gray-500">
                  <TrendingUp className="h-8 w-8 opacity-50" />
                  <p>No usage data for this period</p>
                </div>
              </div>
            </div>
          )
        )}
      </div>
        </div>

        {/* Right: Insights Sidebar — desktop only */}
        <div className="hidden xl:block w-80 shrink-0 sticky top-6">
          <InsightsSidebar monthlyStats={monthlyStats} locations={approvedLocations} />
        </div>
      </div>

      {/* Portfolio Cost Contribution Breakdown (Full Width, Hidden on Mobile) */}
      {visibleCharts.includes('cost_breakdown') && (
        <div className="hidden md:block w-full mt-6">
          <div className="card-dashboard p-6" style={chartGlowStyle('#8B5CF6', darkMode)}>
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center justify-between">
              Portfolio Cost Contribution
              <Bot className="h-4 w-4 text-indigo-400 opacity-50" />
            </h3>
            <div className="h-[400px] overflow-hidden relative" style={{ isolation: 'isolate', clipPath: 'inset(0)' }}>
              {breakdownChartData.length > 0 ? (
                <BreakdownChart 
                  data={breakdownChartData} 
                  darkMode={darkMode} 
                  visibleFacilities={actualFacilities} 
                  facilityNames={facilityNamesForChart} 
                  siteColors={siteColors} 
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400 dark:text-gray-500">
                  <Activity className="h-8 w-8 opacity-50" />
                  <p>Aggregating portfolio insights...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MISSING BILLS REPORT MODAL */}
      {showMissingBillsModal && (
        <MissingBillsModal 
          monthlyStats={monthlyStats}
          facilities={actualFacilities}
          approvedLocations={approvedLocations}
          onClose={() => setShowMissingBillsModal(false)}
          initialParams={reportParams}
          darkMode={darkMode}
        />
      )}

      {/* UTILITY TYPE FILTER MODAL */}
      {showUtilityFilterModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="card-dashboard-cyan max-w-sm w-full">
            <div className="px-6 py-4 border-b border-sky-400/30 dark:border-cyan-500/40">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Select Utilities
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Filter dashboard by utility type
              </p>
            </div>

            <div className="px-6 py-4 space-y-2">
              {['all', 'electric', 'gas', 'water'].map((utility) => (
                <button
                  key={utility}
                  onClick={() => {
                    setUsageUtilityFilter(utility);
                    setShowUtilityFilterModal(false);
                  }}
                  className={`w-full px-4 py-3 rounded-lg text-sm font-medium text-left transition-colors ${
                    usageUtilityFilter === utility
                      ? 'bg-cyan-500 text-white shadow-[0_0_12px_rgba(14,165,233,0.5)]'
                      : 'bg-gray-50 text-gray-700 hover:bg-sky-50 dark:bg-[#1a1a2e] dark:text-gray-300 dark:hover:bg-cyan-500/10'
                  }`}
                >
                  {utility === 'all' ? 'All Utilities' : utility.charAt(0).toUpperCase() + utility.slice(1)}
                </button>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-sky-400/30 dark:border-cyan-500/40">
              <button
                onClick={() => setShowUtilityFilterModal(false)}
                className="btn-dashboard-outline w-full px-4 py-2 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EXPORT MODAL */}
      {showExportModal && (
        <DashboardExportModal
          bills={bills}
          onClose={() => { if (!exportProgress) setShowExportModal(false); }}
          onExport={handleExport}
          darkMode={darkMode}
          exportProgress={exportProgress}
        />
      )}

      {/* BENTO PDF EXPORTER (offscreen chart renderer) */}
      {monthlyStats.length > 0 && (
        <BentoReportExporter
          ref={bentoExporterRef}
          monthlyStats={monthlyStats}
          onProgress={setExportProgress}
        />
      )}

      {/* DASHBOARD CUSTOMIZER MODAL */}
      {showDashboardCustomizer && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border animate-in zoom-in-95 duration-200 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`p-6 border-b flex justify-between items-center ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
              <div>
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dashboard Options</h3>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Customize your analytical view</p>
              </div>
              <button onClick={() => setShowDashboardCustomizer(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <h4 className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Visible Visuals</h4>
                {dashboardChartOptions.map(chart => (
                  <label key={chart.id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${visibleCharts.includes(chart.id) ? (darkMode ? 'bg-indigo-500/10 border-indigo-500/50' : 'bg-indigo-50 border-indigo-200') : (darkMode ? 'bg-gray-900/50 border-gray-700 opacity-60' : 'bg-gray-50 border-gray-100 opacity-60')}`}>
                    <input 
                      type="checkbox" 
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={visibleCharts.includes(chart.id)}
                      onChange={(e) => {
                        if (e.target.checked) setVisibleCharts([...visibleCharts, chart.id]);
                        else setVisibleCharts(visibleCharts.filter(id => id !== chart.id));
                      }}
                    />
                    <div>
                      <div className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{chart.label}</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{chart.description}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="space-y-3 pt-2">
                <h4 className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Visible Utilities</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {['electric', 'gas', 'water'].map(utility => (
                    <label key={utility} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${visibleUtilities.includes(utility) ? (darkMode ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-400' : 'bg-indigo-50 border-indigo-200 text-indigo-600') : (darkMode ? 'bg-gray-900/50 border-gray-700 opacity-60' : 'bg-gray-50 border-gray-100 opacity-60')}`}>
                      <input 
                        type="checkbox" 
                        className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={visibleUtilities.includes(utility)}
                        onChange={(e) => {
                          if (e.target.checked) setVisibleUtilities([...visibleUtilities, utility]);
                          else setVisibleUtilities(visibleUtilities.filter(u => u !== utility));
                        }}
                      />
                      <span className="text-xs font-bold capitalize">{utility}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className={`p-4 border-t flex justify-end ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <button 
                onClick={() => setShowDashboardCustomizer(false)}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-500/20 transition-all active:scale-95"
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI ANALYST PANEL */}
      <div className="fixed bottom-6 right-6 z-[120]">
        {!aiChatOpen && (
          <button 
            onClick={() => setAiChatOpen(true)}
            className="flex items-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-2xl hover:scale-105 active:scale-95 transition-all group"
          >
            <Sparkles className="h-5 w-5" />
            <span className="font-bold text-sm pr-1">Ask AI analyst</span>
          </button>
        )}
        <AIAnalyticsPanel 
          isOpen={aiChatOpen} 
          onClose={() => setAiChatOpen(false)} 
          darkMode={darkMode}
          selectedFacility={selectedFacility === 'All Facilities' ? 'all' : selectedFacility}
          visibleFacilities={actualFacilities}
          chartData={costChartData}
          allTargets={[]} // Could be extended to load targets from Firestore if needed
        />
      </div>
    </div>
  );
};

// === CITY-TO-FACILITY MAPPING & HELPERS ===
const CITY_FACILITY_MAP = {
  'chalfont': 'Pengate - Chalfont',
  'pittston': 'TLNE - Pittson',
  'cranberry': 'TLNE - Cranberry',
  'new castle': 'TLNE - New Castle',
  'hamburg': 'Pengate - Hamburg',
  'annapolis junction': 'TLNE - Annapolis Junction',
  'breinigsville': 'Pengate - Breinigsville',
  'albany': 'Pengate - Albany',
  'cinnaminson': 'TLNE - Cinnaminson',
  'victor': 'TLNE - Victor',
  'bethlehem': 'TLNE - Bethlehem',
  'syracuse': 'Pengate - Syracuse',
  'phoenixville': 'TLNE - Phoenixville',
  'mt pleasant': 'TLNE - Mt Pleasant',
  'mount pleasant': 'TLNE - Mt Pleasant'
};

// Street/location-specific facility mappings (checked before city mapping)
const LOCATION_FACILITY_MAP = {
  'interchange': 'Pengate - Interchange',
  'interchange pl': 'Pengate - Interchange',
  'interchange place': 'Pengate - Interchange',
  'corporate cir': 'Pengate - Syracuse',
  'corporate circle': 'Pengate - Syracuse',
  'sathers dr': 'Pengate - Scranton',
  'sathers drive': 'Pengate - Scranton',
  'petra ln': 'Pengate - Albany',
  'petra lane': 'Pengate - Albany',
  '4373 county': 'Pengate - Chalfont',
  '4373 county-line': 'Pengate - Chalfont'
};

const EXCEPTION_ADDRESS = '';

// Helper function to extract facility name from address by location/street
const _extractFacilityFromLocation = (address) => {
  if (!address) return null;
  const addressLower = address.toLowerCase();
  
  // Check against location-specific mappings first
  for (const [location, facility] of Object.entries(LOCATION_FACILITY_MAP)) {
    if (addressLower.includes(location)) {
      logger.log('📍 Found location-based facility:', { location, facility, address });
      return facility;
    }
  }
  
  return null;
};

// Helper function to extract city from address
const _extractCityFromAddress = (address) => {
  if (!address) return null;
  const parts = address.split(',');
  if (parts.length < 2) return null;
  const cityPart = parts[parts.length - 2].trim().toLowerCase();
  logger.log('🏙️ Extracted city from address:', { address, cityPart, parts });
  return cityPart;
};

// Helper function to normalize address for matching (handles common abbreviations)
const normalizeAddressForMatching = (address) => {
  if (!address) return '';
  return address.toLowerCase()
    .replace(/\bln\b/g, 'lane')
    .replace(/\bst\b/g, 'street')
    .replace(/\bave\b/g, 'avenue')
    .replace(/\bblvd\b/g, 'boulevard')
    .replace(/\bdr\b/g, 'drive')
    .replace(/\brd\b/g, 'road')
    .replace(/\bct\b/g, 'court')
    .replace(/\bpl\b/g, 'place')
    .replace(/\bcir\b/g, 'circle')
    .replace(/\bhwy\b/g, 'highway')
    .replace(/\bpkwy\b/g, 'parkway')
    .replace(/[.,#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Helper function to get facility name from approved locations using fuzzy matching
const getFacilityNameFromAddress = (address, approvedLocations = []) => {
  if (!address || approvedLocations.length === 0) return null;
  
  const addressLower = address.toLowerCase();
  const addressNormalized = normalizeAddressForMatching(address);
  let bestMatch = null;
  let bestScore = 0;
  
  for (const location of approvedLocations) {
    let score = 0;
    const locationAddressNormalized = normalizeAddressForMatching(location.address);
    
    // Exact match on main address gets highest score (check both original and normalized)
    if (addressLower.includes(location.address.toLowerCase())) {
      score = Math.max(score, 100);
    }
    
    // Normalized match (handles ln vs lane, etc.)
    if (addressNormalized.includes(locationAddressNormalized) || locationAddressNormalized.includes(addressNormalized.split(',')[0])) {
      score = Math.max(score, 95);
    }
    
    // Check aliases - ALWAYS check, not just when main address doesn't match
    if (location.aliases && location.aliases.length > 0) {
      for (const alias of location.aliases) {
        const aliasLower = alias.toLowerCase();
        const aliasNormalized = normalizeAddressForMatching(alias);
        
        // Check if alias appears in the address (exact)
        if (addressLower.includes(aliasLower)) {
          score = Math.max(score, 90);
          logger.log('🔍 Alias match (exact):', { alias, address, score: 90 });
        }
        // Check if normalized alias appears in normalized address
        else if (addressNormalized.includes(aliasNormalized)) {
          score = Math.max(score, 85);
          logger.log('🔍 Alias match (normalized):', { alias, address, score: 85 });
        }
        // Check if address contains the alias (partial match)
        else if (aliasLower.length >= 5 && addressLower.includes(aliasLower.split(' ')[0])) {
          // Partial match on first word of alias (e.g., "200 Shell" would match "200")
          const aliasFirstWord = aliasLower.split(' ')[0];
          const aliasSecondWord = aliasLower.split(' ')[1];
          if (aliasFirstWord && aliasSecondWord && 
              addressLower.includes(aliasFirstWord) && addressLower.includes(aliasSecondWord)) {
            score = Math.max(score, 80);
            logger.log('🔍 Alias match (partial):', { alias, address, score: 80 });
          }
        }
      }
    }
    
    // Check city match (lower priority)
    if (location.city && addressLower.includes(location.city.toLowerCase())) {
      score = Math.max(score, 70);
    }
    
    // Check state match (very low priority, used as tiebreaker)
    if (location.state && addressLower.includes(location.state.toLowerCase())) {
      score = Math.max(score, 50);
    }
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = location;
    }
  }
  
  if (bestMatch && bestScore >= 70) {
    logger.log('🎯 Matched address to approved location:', { 
      address, 
      matchedTo: bestMatch.name, 
      score: bestScore 
    });
    return bestMatch.name;
  }
  
  logger.log('⚠️ No approved location match found for address:', address);
  return null;
};

// Helper function to check if address is the exception
const isExceptionAddress = (address) => {
  if (!address) return false;
  if (!EXCEPTION_ADDRESS) return false;
  return address.toLowerCase().includes(EXCEPTION_ADDRESS);
};

// Auto-populate facility name when address is available
const autoPopulateFacilityName = (utilities, approvedLocations = []) => {
  return utilities.map(util => {
    const address = util.fields.site_address?.value;
    const currentFacilityName = util.fields.facility_name?.value;
    logger.log('📝 Processing utility:', { type: util.type, address, currentFacilityName });
    
    if (address && !isExceptionAddress(address)) {
      // Try to get facility name from full address (location or city based)
      const facilityName = getFacilityNameFromAddress(address, approvedLocations);
      
      if (facilityName && !currentFacilityName) {
        logger.log('✅ Auto-populated facility name:', { address, facilityName });
        return {
          ...util,
          fields: {
            ...util.fields,
            facility_name: { 
              value: facilityName, 
              confidence: 0.95, 
              bbox: util.fields.facility_name?.bbox || {} 
            }
          }
        };
      } else if (facilityName && currentFacilityName && currentFacilityName !== facilityName) {
        logger.log('⚠️ Facility name already set:', { current: currentFacilityName, recommended: facilityName });
      }
    } else if (address) {
      logger.log('⏭️ Skipping auto-population - exception address:', address);
    }
    return util;
  });
};

// Auto-correct all facility names based on address
const _autoCorrectAllFacilityNames = (utilities, approvedLocations = []) => {
  return utilities.map(util => {
    const address = util.fields.site_address?.value;
    if (address && !isExceptionAddress(address)) {
      const facilityName = getFacilityNameFromAddress(address, approvedLocations);
      if (facilityName) {
        logger.log('✏️ Auto-correcting facility name:', { address, facilityName });
        return {
          ...util,
          fields: {
            ...util.fields,
            facility_name: { 
              value: facilityName, 
              confidence: 0.95, 
              bbox: util.fields.facility_name?.bbox || {} 
            }
          }
        };
      }
    }
    return util;
  });
};

// 3. SCANNER & VERIFICATION
const Scanner = ({ onSave, initialEditData = null, onCancelEdit, darkMode, approvedLocations }) => {

  const [step, setStep] = useState('upload'); 
  const [file, setFile] = useState(null);
  const [scanResult, setScanResult] = useState(null); 
  
  const [devMode, setDevMode] = useState(false);
  const [_activeField, setActiveField] = useState(null);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [aiPrompt, setAiPrompt] = useState(`Extract all utilities (gas, electric, water) found on the bill.
Separate them into individual entries.`);
  const [isReScanning, setIsReScanning] = useState(false);
  
  // Bulk upload state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkProgress, setBulkProgress] = useState({ 
    current: 0, 
    total: 0, 
    currentFile: '', 
    stage: '', // 'preparing', 'uploading', 'scanning', 'storing'
    stageProgress: 0 // 0-100 for current stage
  });
  const [bulkResults, setBulkResults] = useState([]);
  
  // Manual entry state
  const [manualMode, setManualMode] = useState(false);
  const [manualEntryFile, setManualEntryFile] = useState(null);
  const [manualEntryPreview, setManualEntryPreview] = useState(null);
  const [manualEntry, setManualEntry] = useState({
    site_name: '',
    site_address: '',
    bill_date: new Date().toISOString().split('T')[0],
    type: 'electric',
    usage: '',
    unit: 'kWh',
    amount: '',
    provider: '',
    billing_month: '',
    is_placeholder: false,
    placeholder_note: ''
  });

  // --- INITIALIZATION FOR EDIT MODE ---
  useEffect(() => {
    if (initialEditData) {
      setStep('verify');
      setImagePreview(initialEditData.fileUrl || initialEditData.imageUrl);
      
      setScanResult({
        utilities: [{
          type: initialEditData.type,
          fields: {
            utility_provider: { value: initialEditData.provider, confidence: 1.0, bbox: {} },
            bill_date: { value: initialEditData.date, confidence: 1.0, bbox: {} },
            total_amount: { value: initialEditData.amount, confidence: 1.0, bbox: {} },
            usage_total: { value: initialEditData.usage, unit: initialEditData.unit, confidence: 1.0, bbox: {} },
            site_address: { value: initialEditData.site_address || "", confidence: 1.0, bbox: {} }, 
            invoice_number: { value: "N/A", confidence: 1.0, bbox: {} },
            billing_month: { value: initialEditData.billing_month || "", confidence: 1.0, bbox: {} }
          }
        }],
        used_prompt: "Loaded from Edit"
      });
    }
  }, [initialEditData]);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      // 10MB Limit Check
      if (selected.size > 10 * 1024 * 1024) {
        setError("File is too large. Please upload a file smaller than 10MB.");
        return;
      }
      
      // Compress image if it's an image file
      if (selected.type.startsWith('image/')) {
        compressImageFile(selected).then(compressedFile => {
          setFile(compressedFile);
          const objectUrl = URL.createObjectURL(compressedFile);
          setImagePreview(objectUrl);
          startScan(compressedFile);
        });
      } else if (selected.type.includes('pdf')) {
        // Compress PDF before OCR
        compressPDF(selected).then(compressedFile => {
          setFile(compressedFile);
          const objectUrl = URL.createObjectURL(compressedFile);
          setImagePreview(objectUrl);
          startScan(compressedFile);
        });
      } else {
        setFile(selected);
        const objectUrl = URL.createObjectURL(selected);
        setImagePreview(objectUrl);
        startScan(selected);
      }
    }
  };

  const handleBulkFileSelect = async (e) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    
    if (selectedFiles.length === 0) return;

    // Filter and compress files
    const validFiles = [];
    
    for (const file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        logger.warn(`Skipped ${file.name} - too large`);
        continue;
      }

      try {
        let processedFile = file;
        
        if (file.type.startsWith('image/')) {
          processedFile = await compressImageFile(file);
        } else if (file.type.includes('pdf')) {
          processedFile = await compressPDF(file);
        }
        
        validFiles.push({
          id: Date.now() + Math.random(),
          name: file.name,
          file: processedFile,
          status: 'pending', // pending, scanning, completed, failed
          result: null,
          error: null
        });
      } catch (err) {
        logger.error(`Failed to process ${file.name}:`, err);
      }
    }

    setBulkFiles(validFiles);
    if (validFiles.length > 0) {
      setStep('bulk-preview');
    }
  };

  const processBulkFiles = async () => {
    setStep('bulk-scanning');
    setBulkProgress({ 
      current: 0, 
      total: bulkFiles.length, 
      currentFile: '', 
      stage: 'preparing',
      stageProgress: 0 
    });
    setBulkResults([]);

    let _completedCount = 0;
    const results = [];

    for (let i = 0; i < bulkFiles.length; i++) {
      const bulkFile = bulkFiles[i];
      
      // Stage 1: Preparing
      setBulkProgress({ 
        current: i + 1, 
        total: bulkFiles.length, 
        currentFile: bulkFile.name,
        stage: 'preparing',
        stageProgress: 0
      });

      try {
        // Stage 2: Uploading to AI (simulated - starts when we begin the API call)
        setBulkProgress(prev => ({ 
          ...prev, 
          stage: 'uploading',
          stageProgress: 30
        }));

        // Stage 3: AI Scanning
        setBulkProgress(prev => ({ 
          ...prev, 
          stage: 'scanning',
          stageProgress: 50
        }));
        
        // Retry logic for transient Cloud Function / Vertex AI errors
        let scanData = null;
        let lastError = null;
        const maxRetries = 2;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            scanData = await performGenAIScan(bulkFile.file, aiPrompt);
            if (!scanData.error) break; // Success
            lastError = scanData.error;
            // If it's a rate limit error, wait longer before retrying
            if (scanData.error.includes('rate limit') || scanData.error.includes('Rate limit')) {
              await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)));
            } else if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 2000 * (attempt + 1)));
            }
          } catch (retryErr) {
            lastError = retryErr.message || 'Unknown error';
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 2000 * (attempt + 1)));
            }
          }
        }
        
        // Use last successful result or create error result
        if (!scanData || scanData.error) {
          scanData = { error: lastError || 'Failed after retries', utilities: [] };
        }
        
        // Stage 4: Processing results
        setBulkProgress(prev => ({ 
          ...prev, 
          stage: 'processing',
          stageProgress: 80
        }));
        
        if (scanData.error) {
          results.push({
            ...bulkFile,
            status: 'failed',
            error: scanData.error
          });
        } else if (!scanData.utilities || scanData.utilities.length === 0) {
          results.push({
            ...bulkFile,
            status: 'failed',
            error: 'No utility data found'
          });
        } else {
          // Auto-populate facility names
          const populatedUtilities = autoPopulateFacilityName(scanData.utilities, approvedLocations);
          
          results.push({
            ...bulkFile,
            status: 'completed',
            result: {
              utilities: populatedUtilities,
              used_prompt: scanData.used_prompt
            }
          });
          _completedCount++;
        }
        
        // Stage 5: Complete
        setBulkProgress(prev => ({ 
          ...prev, 
          stage: 'complete',
          stageProgress: 100
        }));
        
        // Small delay between files to avoid rate limiting on Cloud Functions
        await new Promise(resolve => setTimeout(resolve, 1500));
        
      } catch (err) {
        results.push({
          ...bulkFile,
          status: 'failed',
          error: err.message || 'Unknown error'
        });
      }
    }

    setBulkResults(results);
    setStep('bulk-results');
  };

  const startScan = async (fileObj, _isSample = false, customPrompt = aiPrompt) => {
    const targetFile = fileObj || file;
    if (!targetFile) return;
    
    setError(null);
    const isRescan = step === 'verify';
    
    if (isRescan) {
      setIsReScanning(true);
    } else {
      setStep('scanning');
    }

    try {
      const data = await performGenAIScan(targetFile, customPrompt);
      
      if (data.error) throw new Error(data.error);
      if (!data.utilities || data.utilities.length === 0) throw new Error("No utility data found in response.");

      data.utilities.forEach(u => {
        if (!u.fields.site_address) u.fields.site_address = { value: "", confidence: 1.0, bbox: {} };
        // Auto-derive billing_month from bill_date if not already set
        if (!u.fields.billing_month?.value && u.fields.bill_date?.value) {
          const dateStr = u.fields.bill_date.value; // "YYYY-MM-DD"
          const derived = dateStr.length >= 7 ? dateStr.substring(0, 7) : '';
          u.fields.billing_month = { value: derived, confidence: u.fields.bill_date.confidence || 0.9, bbox: {} };
        } else if (!u.fields.billing_month) {
          u.fields.billing_month = { value: "", confidence: 1.0, bbox: {} };
        }
      });

      // Auto-populate facility names based on city
      const populatedUtilities = autoPopulateFacilityName(data.utilities, approvedLocations);
      setScanResult({
        ...data,
        utilities: populatedUtilities
      });
      if (!isRescan) setStep('verify');
    } catch (err) {
      setError(err.message);
      if (!isRescan) setStep('upload');
    } finally {
      setIsReScanning(false);
    }
  };

  const handleSave = () => {
    if (!scanResult) return;
    
    const newBills = scanResult.utilities.map(util => ({
      ...util.fields,
      type: util.type,
      id: initialEditData ? initialEditData.id : Date.now() + Math.random(), 
      status: 'verified',
      rawFile: file,
      imageUrl: imagePreview 
    }));

    onSave(newBills);
    
    // Reset
    setStep('upload');
    setFile(null);
    setScanResult(null);
    setImagePreview(null);
    if (onCancelEdit) onCancelEdit();
  };
  
  const handleSaveManualEntry = () => {
    // Validation
    if (!manualEntry.site_name.trim()) {
      setError('Facility name is required');
      return;
    }
    if (!manualEntry.bill_date) {
      setError('Bill date is required');
      return;
    }
    if (!manualEntry.is_placeholder && !manualEntry.amount) {
      setError('Amount is required for non-placeholder bills');
      return;
    }
    
    // Convert manual entry format to match AI scan format
    const manualBill = {
      site_address: { value: manualEntry.site_address || manualEntry.site_name },
      bill_date: { value: manualEntry.bill_date },
      type: manualEntry.type,
      usage_total: { value: manualEntry.usage || '0', unit: manualEntry.unit },
      total_amount: { value: manualEntry.amount || '0' },
      utility_provider: { value: manualEntry.provider || 'Unknown' },
      billing_month: { value: manualEntry.billing_month || manualEntry.bill_date.substring(0, 7) },
      is_placeholder: manualEntry.is_placeholder,
      placeholder_note: manualEntry.placeholder_note || '',
      manual_entry: true,
      rawFile: manualEntryFile, // Include uploaded file if present
      imageUrl: manualEntryPreview
    };
    
    onSave([manualBill]);
    
    // Reset
    setManualMode(false);
    setManualEntryFile(null);
    setManualEntryPreview(null);
    setManualEntry({
      site_name: '',
      site_address: '',
      bill_date: new Date().toISOString().split('T')[0],
      type: 'electric',
      usage: '',
      unit: 'kWh',
      amount: '',
      provider: '',
      billing_month: '',
      is_placeholder: false,
      placeholder_note: ''
    });
    setStep('upload');
  };

  const handleSaveBulkResults = async () => {
    const allBills = [];
    
    bulkResults.forEach(result => {
      if (result.status === 'completed' && result.result) {
        result.result.utilities.forEach(util => {
          allBills.push({
            ...util.fields,
            type: util.type,
            id: Date.now() + Math.random(),
            status: 'verified',
            rawFile: result.file,
            fileName: result.name
          });
        });
      }
    });

    if (allBills.length > 0) {
      try {
        await onSave(allBills);
        
        // Reset bulk state only after save completes
        setBulkMode(false);
        setBulkFiles([]);
        setBulkResults([]);
        setStep('upload');
        setError(null);
      } catch (error) {
        logger.error('Bulk save failed:', error);
        setError('Failed to save bills. Check console.');
      }
    }
  };

  const updateField = (index, key, newValue) => {
    setScanResult(prev => {
      const newUtils = [...prev.utilities];
      const targetUtil = newUtils[index];
      
      newUtils[index] = {
        ...targetUtil,
        fields: {
          ...targetUtil.fields,
          [key]: {
            ...targetUtil.fields[key],
            value: newValue
          }
        }
      };
      
      return { ...prev, utilities: newUtils };
    });
  };

  const updateUtilityType = (index, newType) => {
    setScanResult(prev => {
      const newUtils = [...prev.utilities];
      newUtils[index] = {
        ...newUtils[index],
        type: newType
      };
      return { ...prev, utilities: newUtils };
    });
  };

  const getAverageConfidence = () => {
    if (!scanResult) return 0;
    let totalConf = 0;
    let count = 0;
    scanResult.utilities.forEach(util => {
        const values = Object.values(util.fields).map(f => f?.confidence || 0);
        totalConf += values.reduce((a, b) => a + b, 0);
        count += values.length;
    });
    if (count === 0) return 0;
    return (totalConf / count) * 100;
  };

  const isPdfPreview = () => {
    if (file && file.type === 'application/pdf') return true;
    if (typeof imagePreview === 'string' && imagePreview.toLowerCase().includes('pdf')) return true;
    return false;
  };

  if (step === 'upload') {
    return (
      <div className="max-w-2xl mx-auto mt-10">
        {error && (
          <div className={`mb-6 px-4 py-3 rounded-lg flex items-center gap-2 border ${
            darkMode
              ? 'bg-red-900/30 border-red-500/50 text-red-300'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}

        <div className="card-dashboard-cyan p-8 text-center space-y-8">
          {/* Mode Toggle */}
          <div className="flex justify-center gap-2">
            <button
              onClick={() => { setBulkMode(false); setManualMode(false); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                !bulkMode && !manualMode
                  ? 'bg-cyan-500 text-white shadow-[0_0_12px_rgba(14,165,233,0.5)]'
                  : darkMode
                  ? 'bg-[#1a1a2e] text-gray-300 hover:bg-cyan-500/10'
                  : 'bg-gray-100 text-gray-700 hover:bg-sky-50'
              }`}
            >
              Single File
            </button>
            <button
              onClick={() => { setBulkMode(true); setManualMode(false); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                bulkMode && !manualMode
                  ? 'bg-cyan-500 text-white shadow-[0_0_12px_rgba(14,165,233,0.5)]'
                  : darkMode
                  ? 'bg-[#1a1a2e] text-gray-300 hover:bg-cyan-500/10'
                  : 'bg-gray-100 text-gray-700 hover:bg-sky-50'
              }`}
            >
              Bulk Upload
            </button>
            <button
              onClick={() => { setManualMode(true); setBulkMode(false); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                manualMode
                  ? 'bg-cyan-500 text-white shadow-[0_0_12px_rgba(14,165,233,0.5)]'
                  : darkMode
                  ? 'bg-[#1a1a2e] text-gray-300 hover:bg-cyan-500/10'
                  : 'bg-gray-100 text-gray-700 hover:bg-sky-50'
              }`}
            >
              Manual Entry
            </button>
          </div>

          {manualMode ? (
            /* Manual Entry Form */
            <div className="space-y-6 text-left">
              <div>
                <div className="mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4 bg-cyan-500/10 dark:bg-cyan-500/20">
                  <Edit2 className="h-10 w-10 neon-icon-rotate" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
                  Manual Bill Entry
                </h2>
                <p className="mt-2 text-gray-500 dark:text-gray-400 text-center">
                  Enter bill information manually or create a placeholder for months without usage
                </p>
              </div>

              {/* File Upload (Optional) */}
              <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                darkMode ? 'border-gray-600 hover:border-cyan-500 bg-gray-800/50' : 'border-gray-300 hover:border-cyan-500 bg-gray-50'
              }`}>
                <input
                  type="file"
                  id="manual-file-upload"
                  className="hidden"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={(e) => {
                    const selected = e.target.files?.[0];
                    if (selected) {
                      if (selected.size > 10 * 1024 * 1024) {
                        setError('File is too large. Please upload a file smaller than 10MB.');
                        return;
                      }
                      setManualEntryFile(selected);
                      const objectUrl = URL.createObjectURL(selected);
                      setManualEntryPreview(objectUrl);
                    }
                  }}
                />
                <label htmlFor="manual-file-upload" className="cursor-pointer block">
                  {manualEntryFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-5 w-5 text-cyan-500" />
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {manualEntryFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setManualEntryFile(null);
                          setManualEntryPreview(null);
                        }}
                        className="text-red-500 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <UploadCloud className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Optional: Upload bill file
                      </p>
                      <p className="text-xs text-gray-500 mt-1">PDF, JPG or PNG</p>
                    </>
                  )}
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Facility Name *
                  </label>
                  <select
                    value={manualEntry.site_name}
                    onChange={(e) => {
                      const selectedFacility = approvedLocations.find(loc => loc.name === e.target.value);
                      setManualEntry(prev => ({
                        ...prev,
                        site_name: e.target.value,
                        site_address: selectedFacility?.address || ''
                      }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select a facility...</option>
                    {approvedLocations.map((loc) => (
                      <option key={loc.id} value={loc.name}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Facility Address
                  </label>
                  <input
                    type="text"
                    value={manualEntry.site_address}
                    readOnly
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    } opacity-75 cursor-not-allowed`}
                    placeholder="Auto-filled from facility selection"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Utility Type *
                  </label>
                  <select
                    value={manualEntry.type}
                    onChange={(e) => setManualEntry(prev => ({ 
                      ...prev, 
                      type: e.target.value,
                      unit: e.target.value === 'electric' ? 'kWh' : e.target.value === 'gas' ? 'CCF' : 'Gallons'
                    }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="electric">Electric</option>
                    <option value="gas">Gas</option>
                    <option value="water">Water</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Bill Date *
                  </label>
                  <input
                    type="date"
                    value={manualEntry.bill_date}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, bill_date: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Billing Month
                  </label>
                  <input
                    type="month"
                    value={manualEntry.billing_month || manualEntry.bill_date.substring(0, 7)}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, billing_month: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Provider
                  </label>
                  <input
                    type="text"
                    value={manualEntry.provider}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, provider: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="e.g., ConEd, National Grid"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Amount {!manualEntry.is_placeholder && '*'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualEntry.amount}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, amount: e.target.value }))}
                    disabled={manualEntry.is_placeholder}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    } ${manualEntry.is_placeholder ? 'opacity-50' : ''}`}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Usage
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={manualEntry.usage}
                      onChange={(e) => setManualEntry(prev => ({ ...prev, usage: e.target.value }))}
                      disabled={manualEntry.is_placeholder}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      } ${manualEntry.is_placeholder ? 'opacity-50' : ''}`}
                      placeholder="0"
                    />
                    <select
                      value={manualEntry.unit}
                      onChange={(e) => setManualEntry(prev => ({ ...prev, unit: e.target.value }))}
                      disabled={manualEntry.is_placeholder}
                      className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      } ${manualEntry.is_placeholder ? 'opacity-50' : ''}`}
                    >
                      <option value="kWh">kWh</option>
                      <option value="CCF">CCF</option>
                      <option value="Gallons">Gallons</option>
                      <option value="units">units</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Placeholder Checkbox */}
              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualEntry.is_placeholder}
                    onChange={(e) => setManualEntry(prev => ({ 
                      ...prev, 
                      is_placeholder: e.target.checked,
                      amount: e.target.checked ? '0' : prev.amount,
                      usage: e.target.checked ? '0' : prev.usage
                    }))}
                    className="mt-1 h-4 w-4 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500"
                  />
                  <div className="flex-1">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mark as Placeholder
                    </span>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Use this for months when no bill was received due to zero usage. Placeholders won't appear in the missing bills report.
                    </p>
                  </div>
                </label>

                {manualEntry.is_placeholder && (
                  <div className="mt-3">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Note
                    </label>
                    <textarea
                      value={manualEntry.placeholder_note}
                      onChange={(e) => setManualEntry(prev => ({ ...prev, placeholder_note: e.target.value }))}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 outline-none ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Optional note (e.g., 'Facility was closed', 'No usage this month')"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setManualMode(false);
                    setManualEntryFile(null);
                    setManualEntryPreview(null);
                    setManualEntry({
                      site_name: '',
                      site_address: '',
                      bill_date: new Date().toISOString().split('T')[0],
                      type: 'electric',
                      usage: '',
                      unit: 'kWh',
                      amount: '',
                      provider: '',
                      billing_month: '',
                      is_placeholder: false,
                      placeholder_note: ''
                    });
                  }}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveManualEntry}
                  className="flex-1 px-6 py-3 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-600 transition-colors shadow-[0_0_12px_rgba(14,165,233,0.5)]"
                >
                  Save {manualEntry.is_placeholder ? 'Placeholder' : 'Bill'}
                </button>
              </div>
            </div>
          ) : (
            /* File Upload UI */
            <>
              <div>
                <div className="mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4 bg-cyan-500/10 dark:bg-cyan-500/20">
                  <UploadCloud className="h-10 w-10 neon-icon-rotate" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {bulkMode ? 'Bulk Upload Utility Bills' : 'Upload Utility Bill'}
                </h2>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  {bulkMode 
                    ? 'Upload multiple PDF, JPG, or PNG files at once. AI will scan all files automatically.' 
                    : 'Upload a PDF, JPG, or PNG. AI will detect separate utilities (Gas, Electric) automatically.'
                  }
                </p>
              </div>

              <div className="neon-upload-border rounded-xl p-10 transition-colors group cursor-pointer">
                <input 
                  type="file" 
                  id={bulkMode ? "bulk-file-upload" : "file-upload"}
                  className="hidden" 
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={bulkMode ? handleBulkFileSelect : handleFileSelect}
                  multiple={bulkMode}
                />
                <label htmlFor={bulkMode ? "bulk-file-upload" : "file-upload"} className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                  <span className="font-medium transition-colors text-gray-500 dark:text-gray-300 group-hover:text-cyan-500">
                    Click to upload {bulkMode ? 'multiple files' : ''}
                  </span>
                  <span className="text-sm mt-1 text-gray-400 dark:text-gray-500">PDF, JPG or PNG</span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (step === 'scanning') {
    return (
      <div className="max-w-md mx-auto mt-20 text-center space-y-6">
        <div className="relative h-32 w-32 mx-auto">
          <div className={`absolute inset-0 border-4 rounded-full ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}></div>
          <div className="absolute inset-0 border-4 border-transparent rounded-full border-t-cyan-500 animate-spin" style={{ animation: 'spin 1s linear infinite, neon-spinner 6s linear infinite' }}></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Bot className="h-10 w-10 neon-icon-rotate animate-pulse" />
          </div>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">AI Processing</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Scanning for multiple utilities...</p>
        </div>
      </div>
    );
  }

  // Bulk Preview Step
  if (step === 'bulk-preview') {
    return (
      <div className="max-w-3xl mx-auto mt-10">
        <div className="card-dashboard-cyan p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
            Selected Files ({bulkFiles.length})
          </h2>

          <div className={`space-y-2 max-h-96 overflow-y-auto p-4 rounded-lg border ${
            darkMode ? 'bg-[#1a1a2e] border-cyan-500/30' : 'bg-gray-50 border-sky-400/30'
          }`}>
            {bulkFiles.map(bf => (
              <div key={bf.id} className={`p-3 rounded flex justify-between items-center ${
                darkMode ? 'bg-[#121212] border border-cyan-500/20' : 'bg-white border border-sky-400/20'
              }`}>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {bf.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {(bf.file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setStep('upload');
                setBulkFiles([]);
              }}
              className="btn-dashboard-outline flex-1 px-4 py-2 font-medium"
            >
              Back
            </button>
            <button
              onClick={processBulkFiles}
              className="btn-dashboard-accent flex-1 px-4 py-2 font-medium flex items-center justify-center gap-2"
            >
              <ScanLine className="h-4 w-4" />
              Start Scanning ({bulkFiles.length})
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Bulk Scanning Step
  if (step === 'bulk-scanning') {
    const currentFileProgress = bulkProgress.stageProgress || 0;
    const overallProgress = bulkProgress.total > 0 
      ? ((bulkProgress.current - 1) / bulkProgress.total * 100) + (currentFileProgress / bulkProgress.total)
      : 0;

    const getStageLabel = (stage) => {
      switch(stage) {
        case 'preparing': return 'Preparing file...';
        case 'uploading': return 'Uploading to AI...';
        case 'scanning': return 'AI Scanning...';
        case 'processing': return 'Processing results...';
        case 'complete': return 'Complete!';
        default: return 'Processing...';
      }
    };

    const getStageIcon = (stage) => {
      switch(stage) {
        case 'preparing': return <RefreshCw className="h-5 w-5 animate-spin" />;
        case 'uploading': return <UploadCloud className="h-5 w-5 animate-pulse" />;
        case 'scanning': return <Bot className="h-5 w-5 animate-pulse" />;
        case 'processing': return <Activity className="h-5 w-5 animate-pulse" />;
        case 'complete': return <CheckCircle2 className="h-5 w-5" />;
        default: return <Loader2 className="h-5 w-5 animate-spin" />;
      }
    };
    
    return (
      <div className="max-w-lg mx-auto mt-20">
        <div className="card-dashboard-cyan p-8 text-center space-y-6">
          <div className="relative h-32 w-32 mx-auto">
            <div className={`absolute inset-0 border-4 rounded-full ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}></div>
            <div className="absolute inset-0 border-4 border-transparent rounded-full border-t-cyan-500 animate-spin" style={{ animation: 'spin 1s linear infinite, neon-spinner 6s linear infinite' }}></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Bot className="h-10 w-10 neon-icon-rotate animate-pulse" />
            </div>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Processing File {bulkProgress.current} of {bulkProgress.total}
            </h3>
            <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              {bulkProgress.currentFile}
            </p>
            
            {/* Current Stage Indicator */}
            <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
              darkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-sky-500/10 text-sky-700'
            }`}>
              {getStageIcon(bulkProgress.stage)}
              {getStageLabel(bulkProgress.stage)}
            </div>
          </div>

          {/* Overall Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-medium">
              <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Overall Progress</span>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{Math.round(overallProgress)}%</span>
            </div>
            <div className={`w-full h-3 rounded-full overflow-hidden ${
              darkMode ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
              <div 
                className="h-full bg-cyan-500 transition-all duration-500"
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
          </div>

          {/* Current File Stage Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-medium">
              <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Current File</span>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{currentFileProgress}%</span>
            </div>
            <div className={`w-full h-2 rounded-full overflow-hidden ${
              darkMode ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
              <div 
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${currentFileProgress}%` }}
              ></div>
            </div>
          </div>

          {/* Stage Timeline */}
          <div className="pt-4">
            <div className="flex justify-between items-center relative">
              {/* Progress Line */}
              <div className={`absolute top-3 left-0 right-0 h-0.5 ${
                darkMode ? 'bg-gray-700' : 'bg-gray-300'
              }`}></div>
              <div 
                className="absolute top-3 left-0 h-0.5 bg-cyan-500 transition-all duration-500"
                style={{ width: `${currentFileProgress}%` }}
              ></div>
              
              {/* Stage Dots */}
              {['preparing', 'uploading', 'scanning', 'processing'].map((stage, idx) => {
                const stageNumber = idx * 33.33;
                const isActive = currentFileProgress >= stageNumber;
                const isCurrent = bulkProgress.stage === stage;
                
                return (
                  <div key={stage} className="relative z-10 flex flex-col items-center">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      isCurrent 
                        ? 'border-cyan-500 bg-cyan-500 scale-110 shadow-[0_0_8px_rgba(14,165,233,0.5)]' 
                        : isActive
                        ? 'border-cyan-500 bg-cyan-500'
                        : darkMode ? 'border-gray-600 bg-[#121212]' : 'border-gray-300 bg-white'
                    }`}>
                      {isActive && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span className={`text-[10px] mt-1 whitespace-nowrap ${
                      isCurrent 
                        ? 'font-bold text-cyan-500'
                        : darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {stage === 'preparing' ? 'Prep' : stage === 'uploading' ? 'Upload' : stage === 'scanning' ? 'Scan' : 'Process'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Bulk Results Step
  if (step === 'bulk-results') {
    const successCount = bulkResults.filter(r => r.status === 'completed').length;
    const failureCount = bulkResults.filter(r => r.status === 'failed').length;

    return (
      <div className="max-w-4xl mx-auto mt-10">
        <div className="card-dashboard-cyan p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
            Scan Complete
          </h2>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg text-center ${
              darkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'
            }`}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                {successCount}
              </p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                Successful
              </p>
            </div>
            <div className={`p-4 rounded-lg text-center ${
              darkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                {failureCount}
              </p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                Failed
              </p>
            </div>
            <div className={`p-4 rounded-lg text-center ${
              darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                {bulkResults.length}
              </p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                Total
              </p>
            </div>
          </div>

          {/* Results List */}
          <div className={`space-y-2 max-h-96 overflow-y-auto p-4 rounded-lg border ${
            darkMode ? 'bg-[#1a1a2e] border-cyan-500/30' : 'bg-gray-50 border-sky-400/30'
          }`}>
            {bulkResults.map(result => (
              <div key={result.id} className={`p-3 rounded flex items-center gap-3 ${
                result.status === 'completed'
                  ? darkMode ? 'bg-green-900/20' : 'bg-green-50'
                  : darkMode ? 'bg-red-900/20' : 'bg-red-50'
              }`}>
                {result.status === 'completed' ? (
                  <CheckCircle2 className={`h-5 w-5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                ) : (
                  <AlertCircle className={`h-5 w-5 flex-shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    {result.name}
                  </p>
                  {result.error && (
                    <p className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                      {result.error}
                    </p>
                  )}
                  {result.status === 'completed' && result.result && (
                    <p className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                      {result.result.utilities.length} utilities found
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setStep('upload');
                setBulkMode(false);
                setBulkFiles([]);
                setBulkResults([]);
              }}
              className="btn-dashboard-outline flex-1 px-4 py-2 font-medium"
            >
              Upload More
            </button>
            {successCount > 0 && (
              <button
                onClick={handleSaveBulkResults}
                className="btn-dashboard-accent flex-1 px-4 py-2 font-medium flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                Save {successCount} Bill{successCount !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const confidenceScore = getAverageConfidence();
  const isLowConfidence = confidenceScore < 90;
  const orderedKeys = ['utility_provider', 'invoice_number', 'bill_date', 'site_address'];
  
  // Generate month options for the past 24 months and next 3 months
  const _generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 24; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    for (let i = 1; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return months;
  };

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col lg:flex-row gap-6 p-4 pb-24 md:pb-4">
      
      {/* Left Col: Document Preview */}
      <div className="flex-1 bg-gray-900 rounded-xl overflow-hidden relative shadow-lg flex flex-col">
        <div className="p-3 bg-gray-800 flex justify-between items-center text-white text-xs font-medium">
          <span>Document Preview</span>
          {initialEditData && <span className="text-yellow-400">EDIT MODE</span>}
          <span className="bg-gray-700 px-2 py-1 rounded">{file?.name || "scanned_doc"}</span>
        </div>
        <div className="flex-1 relative bg-gray-800 flex items-center justify-center p-4 overflow-hidden group">
          <div className="relative w-full h-full bg-white shadow-2xl transition-transform duration-500 ease-out flex items-center justify-center">
            {imagePreview ? (
               isPdfPreview() ? (
                 <iframe src={imagePreview} className="w-full h-full border-none" title="PDF Preview" />
               ) : (
                <div className="relative w-full h-full flex items-center justify-center bg-gray-900">
                   <img src={imagePreview} alt="Preview" className="max-w-full max-h-full object-contain" />
                </div>
               )
            ) : (
              <div className="p-12 text-center text-[#58595B]">
                 <FileText className="h-16 w-16 mx-auto mb-4 text-[#58595B]/50" />
                 <p>Preview Not Available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Col: Verification Form */}
      <div className="w-full lg:w-[450px] flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className={`font-bold text-lg ${darkMode ? 'text-gray-100' : 'text-[#000000]'}`}>Verify Extraction</h3>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-[#58595B]'}`}>{devMode ? "Dev Mode" : "Standard"}</span>
            <button 
              onClick={() => setDevMode(!devMode)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${devMode ? 'bg-[#0950A0]' : 'bg-gray-200'}`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${devMode ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {isLowConfidence && (
          <div className={`border-l-4 border-red-500 p-4 rounded-r-md ${
            darkMode ? 'bg-red-900/30' : 'bg-red-50'
          }`}>
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${darkMode ? 'text-red-400' : 'text-red-800'}`}>Low Accuracy ({confidenceScore.toFixed(0)}%)</h3>
                <div className={`mt-1 text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>Please review fields carefully.</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto pr-2 space-y-6">
          
          {/* PROMPT ENGINEERING WORKBENCH */}
          {devMode && (
            <div className="bg-gray-900 rounded-lg border border-gray-700 shadow-inner flex flex-col overflow-hidden mb-4 transition-all">
              <div className="bg-gray-800 p-2 flex items-center justify-between border-b border-gray-700">
                <div className="flex items-center gap-2 text-[#0950A0] text-xs font-bold uppercase tracking-wider">
                   <Bot className="h-3 w-3" /><span>Live Gemini Prompting</span>
                </div>
                {isReScanning ? (
                   <div className="flex items-center gap-1 text-[#0950A0] text-[10px]"><Loader2 className="h-3 w-3 animate-spin" /> Processing...</div>
                ) : (<span className="text-[10px] text-gray-500">Idle</span>)}
              </div>
              <div className="p-3">
                <label className="text-[10px] text-gray-400 mb-1 block">Custom Extraction Instructions</label>
                <textarea 
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  className="w-full bg-gray-950 text-gray-300 text-xs font-mono p-2 rounded border border-gray-700 focus:border-[#0950A0] focus:ring-0 min-h-[80px]"
                />
                <div className="mt-2 flex items-center justify-between">
                  <button 
                    onClick={() => startScan(null, false, aiPrompt)}
                    disabled={isReScanning}
                    className="bg-[#0950A0] hover:bg-[#0950A0]/80 text-white text-xs px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Play className="h-3 w-3" fill="currentColor" /> Run AI
                  </button>
                </div>
              </div>
            </div>
          )}

          {scanResult?.utilities.map((util, idx) => (
            <div key={idx} className={`space-y-4 border-b pb-6 last:border-0 animate-in slide-in-from-bottom-4 duration-500 ${
              darkMode ? 'border-gray-700' : 'border-[#58595B]/10'
            }`}>
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                darkMode ? 'bg-gray-700' : 'bg-[#58595B]/5'
              }`}>
                  <div className={`p-1.5 rounded shadow-sm text-[#0950A0] ${
                    darkMode ? 'bg-gray-600' : 'bg-white'
                  }`}>
                    {util.type === 'electric' && <Zap className="h-4 w-4" />}
                    {util.type === 'gas' && <Flame className="h-4 w-4" />}
                    {util.type === 'water' && <Droplets className="h-4 w-4" />}
                    {util.type === 'other' && <Activity className="h-4 w-4" />}
                  </div>
                  <select
                    value={util.type}
                    onChange={(e) => updateUtilityType(idx, e.target.value)}
                    className={`font-bold text-sm rounded-md px-2 py-1 border focus:border-[#0950A0] focus:ring-1 focus:ring-[#0950A0] outline-none cursor-pointer transition-colors ${
                      darkMode
                        ? 'bg-gray-600 border-gray-500 text-gray-100'
                        : 'bg-white border-[#58595B]/20 text-[#000000]'
                    }`}
                  >
                    <option value="electric">Electric Service</option>
                    <option value="gas">Gas Service</option>
                    <option value="water">Water Service</option>
                    <option value="other">Other Service</option>
                  </select>
                  <span className={`ml-auto text-xs px-2 py-1 rounded border ${
                    darkMode ? 'text-gray-400 bg-gray-600 border-gray-500' : 'text-[#58595B] bg-white border-[#58595B]/10'
                  }`}>Entry #{idx + 1}</span>
              </div>

              <div className={`rounded-xl shadow-sm border overflow-hidden ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#58595B]/20'
              }`}>
                <div className="p-4 space-y-4">
                  {orderedKeys.map((key) => {
                    const field = util.fields[key];
                    if (!field) return null;
                    return (
                      <div key={`${idx}-${key}`}>
                        <label className={`block text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1 ${
                          darkMode ? 'text-gray-400' : 'text-[#58595B]'
                        }`}>

                          {key.replace(/_/g, ' ')}
                        </label>
                        <input
                          type="text"
                          value={field.value || ''}
                          onChange={(e) => updateField(idx, key, e.target.value)}
                          onFocus={() => setActiveField(`${idx}-${key}`)}
                          onBlur={() => setActiveField(null)}
                          className={`block w-full rounded-md shadow-sm focus:border-[#0950A0] focus:ring-[#0950A0] sm:text-sm p-2 border transition-colors ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                              : 'bg-white border-[#58595B]/30 text-gray-900'
                          }`}
                        />
                      </div>
                    );
                  })}
                  
                  {/* Billing Month - Special field for reports */}
                  <div className={`p-3 rounded-lg border-2 ${
                    darkMode ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-300'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded ${
                        darkMode ? 'bg-amber-700 text-amber-100' : 'bg-amber-400 text-amber-900'
                      }`}>
                        📊 For Reports
                      </span>
                    </div>
                    <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${
                      darkMode ? 'text-amber-400' : 'text-amber-800'
                    }`}>
                      Billing Month (Service Period)
                    </label>
                    <input
                      type="month"
                      value={util.fields.billing_month?.value || ''}
                      onChange={(e) => updateField(idx, 'billing_month', e.target.value)}
                      className={`block w-full rounded-md shadow-sm focus:border-amber-600 focus:ring-amber-600 sm:text-sm p-2 border transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-100' 
                          : 'bg-white border-amber-300 text-gray-900'
                      }`}
                    />
                    <p className={`text-[10px] mt-1 ${
                      darkMode ? 'text-amber-300' : 'text-amber-700'
                    }`}>
                      Select the month this bill covers for accurate reporting
                    </p>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl border overflow-hidden ${
                darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-[#0950A0]/5 border-[#0950A0]/20'
              }`}>
                  <div className="p-4 grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-semibold uppercase tracking-wider mb-1 ${
                        darkMode ? 'text-blue-400' : 'text-[#0950A0]'
                      }`}>
                        Usage ({util.fields.usage_total?.unit || 'units'})
                      </label>
                      <input
                        type="text"
                        value={util.fields.usage_total?.value || ''}
                        onChange={(e) => updateField(idx, 'usage_total', e.target.value)}
                        className={`block w-full rounded-md focus:border-[#0950A0] focus:ring-[#0950A0] sm:text-sm p-2 border font-mono font-medium ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-gray-100' 
                            : 'bg-white border-[#0950A0]/30 text-[#000000]'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-semibold uppercase tracking-wider mb-1 ${
                        darkMode ? 'text-blue-400' : 'text-[#0950A0]'
                      }`}>
                        Total Cost ($)
                      </label>
                      <input
                        type="text"
                        value={util.fields.total_amount?.value || ''}
                        onChange={(e) => updateField(idx, 'total_amount', e.target.value)}
                        className={`block w-full rounded-md focus:border-[#0950A0] focus:ring-[#0950A0] sm:text-sm p-2 border font-mono font-bold ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-gray-100' 
                            : 'bg-white border-[#0950A0]/30 text-[#000000]'
                        }`}
                      />
                    </div>
                </div>
              </div>
            </div>
          ))}

          <div className="flex gap-3 pt-4 pb-24 md:pb-8">
             <button 
                onClick={() => {
                  if (initialEditData && onCancelEdit) onCancelEdit();
                  else setStep('upload');
                }}
                className="btn-dashboard-outline flex-1 py-3 px-4 text-sm font-medium"
             >
                {initialEditData ? "Cancel Edit" : "Discard"}
             </button>
             <button 
                onClick={handleSave}
                className="flex-[2] py-3 px-4 bg-cyan-500 rounded-lg text-sm font-medium text-white hover:bg-cyan-600 shadow-[0_0_12px_rgba(14,165,233,0.4)] flex items-center justify-center gap-2 transition-colors"
             >
                <Save className="h-4 w-4" />
                {initialEditData ? "Save Changes" : "Confirm & Save All"}
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 4. BILL LIST (With Clean Filter Pop-up)
const BillList = ({ bills, onDeleteRequest, onArchiveRequest, onEditRequest, onBulkDelete, onBulkAutoCorrect, onSecureView, darkMode, isLoading, _fetchBills, allFacilities, _ignoredCorrections, _setIgnoredCorrections, _onShowIgnoredRulesManager, refreshKey, sessionTracker }) => {
  // --- STATE ---
  const BILLLIST_FILTERS_KEY = 'utilitrack_billlist_filters';
  const persisted = (() => {
    try {
      const raw = localStorage.getItem(BILLLIST_FILTERS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const [selectedIds, setSelectedIds] = useState([]); 
  const [sortConfig, setSortConfig] = useState(persisted?.sortConfig || { key: 'date', direction: 'desc' });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [dateMode, setDateMode] = useState(persisted?.dateMode || 'range'); 
  const [currentPage, setCurrentPage] = useState(persisted?.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(persisted?.itemsPerPage || 10);
  const [searchQuery, setSearchQuery] = useState(persisted?.searchQuery || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Debounce search input — wait 400ms after user stops typing before querying Firestore
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [filters, setFilters] = useState(persisted?.filters || {
    site_name: '',
    startDate: '', 
    endDate: '',   
    type: '',
    uploaded_by: '',
    amount: ''
  });

  // --- SERVER PAGINATION STATE ---
  const [pageBills, setPageBills] = useState([]);
  const [serverLoading, setServerLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageCursorsRef = useRef([]); // stores last doc for each page (index = page-1)
  const fetchInFlightRef = useRef(false); // Guard against StrictMode double-invoking fetchPage

  // --- HANDLERS ---
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      site_name: '',
      startDate: '',
      endDate: '',
      type: '',
      uploaded_by: '',
      amount: ''
    });
    setSearchQuery('');
    setDateMode('range');
    setCurrentPage(1);
    try { localStorage.removeItem(BILLLIST_FILTERS_KEY); } catch { void 0; }
  };

  // Count active filters for the badge
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // Get unique facility names from BOTH allFacilities AND bills
  // This ensures newly uploaded bills appear in the filter even before monthly_stats is rebuilt
  const uniqueFacilities = useMemo(() => {
    const fromStats = allFacilities || [];
    const fromBills = bills.map(b => b.site_name).filter(Boolean);
    return [...new Set([...fromStats, ...fromBills])].sort();
  }, [allFacilities, bills]);

  const handleSelectAll = (e, visibleBills) => {
    if (e.target.checked) {
      setSelectedIds(visibleBills.map(b => b.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // --- NORMALIZE SEARCH TOKEN (uses debounced value to avoid Firestore reads per keystroke) ---
  const normalizeSearchToken = useMemo(() => {
    const token = (debouncedSearch || '').trim().toLowerCase().split(/\s+/)[0] || '';
    return token;
  }, [debouncedSearch]);

  // Build date boundaries for YYYY-MM selections
  const startDateValue = useMemo(() => filters.startDate ? `${filters.startDate}-01` : null, [filters.startDate]);
  const endDateValue = useMemo(() => filters.endDate ? `${filters.endDate}-31` : null, [filters.endDate]);

  // Server-side fetch with Firestore pagination
  const fetchPage = useCallback(async (targetPage = 1, pageSizeOverride = null) => {
    const effectiveItemsPerPage = pageSizeOverride ?? itemsPerPage;
    // Guard: skip if a fetch is already in progress (prevents StrictMode double-fire)
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setServerLoading(true);
    try {
      const constraints = [where('archived', '==', false)];

      if (filters.site_name) constraints.push(where('site_name', '==', filters.site_name));
      if (filters.type) constraints.push(where('type', '==', filters.type));
      if (filters.uploaded_by) constraints.push(where('uploaded_by', '==', filters.uploaded_by));
      if (filters.amount) {
        const amt = parseFloat(filters.amount);
        if (!Number.isNaN(amt)) constraints.push(where('amount', '==', amt));
      }

      if (startDateValue) constraints.push(where('date', '>=', startDateValue));
      if (endDateValue) constraints.push(where('date', '<=', endDateValue));
      if (normalizeSearchToken) constraints.push(where('search_tokens', 'array-contains', normalizeSearchToken));

      logger.log('[fetchPage] Building query with constraints:', {
        site_name: filters.site_name || 'none',
        type: filters.type || 'none',
        searchToken: normalizeSearchToken || 'none',
        constraintCount: constraints.length
      });

      let billsQuery = query(
        collection(db, 'bills'),
        ...constraints,
        orderBy('date', 'desc'),
        limit(effectiveItemsPerPage)
      );

      if (targetPage > 1 && pageCursorsRef.current[targetPage - 2]) {
        billsQuery = query(
          collection(db, 'bills'),
          ...constraints,
          orderBy('date', 'desc'),
          startAfter(pageCursorsRef.current[targetPage - 2]),
          limit(effectiveItemsPerPage)
        );
      }

      const snapshot = await getDocs(billsQuery);
      const batchBills = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      logger.log(`[fetchPage] Query returned ${batchBills.length} bills`);
      sessionTracker?.trackRead('BillList.fetchPage', 'bills', batchBills.length, {
        page: targetPage,
        filters: Object.fromEntries(Object.entries(filters).filter(([,v]) => v)),
        hasSearch: !!normalizeSearchToken
      });

      // Store cursor for this page
      if (snapshot.docs.length > 0) {
        pageCursorsRef.current[targetPage - 1] = snapshot.docs[snapshot.docs.length - 1];
      }

      setPageBills(batchBills);
      setHasMore(snapshot.docs.length === effectiveItemsPerPage);
      setCurrentPage(targetPage);
      
      // If server returned 0 results with a filter but we have local bills matching, it might be a data sync issue
      if (batchBills.length === 0 && filters.site_name && bills.length > 0) {
        const localMatches = bills.filter(b => 
          !b.archived && 
          b.site_name && 
          b.site_name.trim().toLowerCase() === filters.site_name.trim().toLowerCase()
        );
        if (localMatches.length > 0) {
          logger.warn(`[fetchPage] Server returned 0 results but found ${localMatches.length} local matches. Possible Firestore index issue or data sync problem.`);
          // Use local data as fallback
          setPageBills(localMatches.slice(0, effectiveItemsPerPage));
        }
      }
    } catch (error) {
      logger.error('Error fetching paged bills:', error);
      // If query fails (e.g., missing index), show helpful message
      if (error.message?.includes('index')) {
        logger.error('⚠️ Firestore index required. Check Firebase console for index creation link.');
      }
      // Fallback to client-side filtering if server fails
      if (bills.length > 0) {
        const fallbackBills = bills.filter(b => {
          if (b.archived) return false;
          if (filters.site_name && b.site_name !== filters.site_name) return false;
          if (filters.type && b.type !== filters.type) return false;
          return true;
        }).slice(0, effectiveItemsPerPage);
        setPageBills(fallbackBills);
        logger.log('[fetchPage] Using client-side fallback:', fallbackBills.length, 'bills');
      } else {
        setPageBills([]);
      }
      setHasMore(false);
    } finally {
      setServerLoading(false);
      fetchInFlightRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.site_name, filters.type, filters.uploaded_by, filters.amount, startDateValue, endDateValue, normalizeSearchToken, itemsPerPage, refreshKey, sessionTracker]);

  // Kick off fetch on filter/search/page-size change or when refreshKey changes (new bill uploaded or delete)
  useEffect(() => {
    pageCursorsRef.current = [];
    setSelectedIds([]); // Clear selection on refresh (e.g. after bulk delete)
    fetchPage(1);
  }, [fetchPage]);

  // Derived view: sort within the current page for column sorting (page-level only)
  const processedBills = useMemo(() => {
    const sorted = [...pageBills].sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";
      const isNumber = !isNaN(parseFloat(aValue)) && isFinite(aValue);
      if (isNumber) {
        return sortConfig.direction === 'asc' ? parseFloat(aValue) - parseFloat(bValue) : parseFloat(bValue) - parseFloat(aValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [pageBills, sortConfig]);

  // Persist filters/search/sort/pagination
  useEffect(() => {
    try {
      localStorage.setItem(BILLLIST_FILTERS_KEY, JSON.stringify({
        filters,
        searchQuery,
        sortConfig,
        dateMode,
        currentPage,
        itemsPerPage
      }));
    } catch { void 0; }
  }, [filters, searchQuery, sortConfig, dateMode, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortConfig]);

  const _loading = isLoading || serverLoading;

  const SortIcon = ({ columnKey }) => (
    <span className="inline-block ml-1">
       <ArrowUpDown className={`h-3 w-3 ${sortConfig.key === columnKey ? 'text-blue-600' : 'text-gray-300'}`} />
    </span>
  );

  return (
    <div className="space-y-4 relative">
      
      {/* --- TOOLBAR ROW --- */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search bills..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className={`w-full px-4 py-2 pl-10 border rounded-lg outline-none text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
          <Search className={`absolute left-3 top-2.5 h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
        </div>

        {/* Action Buttons Row */}
        <div className="flex justify-between items-center">
        
          {/* Left: Bulk Actions */}
          <div className="h-10">
            {selectedIds.length > 0 && (
              <div className={`border px-3 py-2 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-left-1 transition-colors ${
                darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'
              }`}>
                <span className={`text-sm font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  {selectedIds.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => onBulkDelete(selectedIds)}
                  className={`flex items-center gap-1 text-sm font-medium ${
                    darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                  }`}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            )}
          </div>

          {/* Right: Filter Toggle Button */}
          <div className="flex gap-2 items-center">
            <button
              onClick={onBulkAutoCorrect}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                darkMode 
                  ? 'border-green-700 bg-green-900/30 text-green-400 hover:bg-green-900/50' 
                  : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
              }`}
              title="Auto-correct all facility names based on billing address"
            >
              <RefreshCw className="h-4 w-4" />
              Auto-Correct Locations
            </button>

          <div className="relative">
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                showFilterPanel || activeFilterCount > 0
                  ? 'bg-blue-600 border-blue-600 text-white' 
                  : darkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-blue-600 text-white text-[10px] px-1.5 rounded-full">
                  {activeFilterCount}
                </span>
            )}
          </button>

          {/* --- POP-UP FILTER PANEL --- */}
          {showFilterPanel && (
            <div className={`absolute right-0 top-12 z-20 w-80 sm:w-96 rounded-xl shadow-xl border p-4 animate-in fade-in zoom-in-95 duration-200 transition-colors ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Filter Records</h3>
                <button onClick={clearFilters} className={`text-xs underline ${
                  darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-600'
                }`}>
                  Clear All
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Facility Name Dropdown */}
                <div className="col-span-2">
                  <label className={`text-xs font-semibold uppercase ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>Facility Name</label>
                  <select 
                    className={`w-full mt-1 text-sm p-2 border rounded outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                    }`} 
                    value={filters.site_name} 
                    onChange={e => handleFilterChange('site_name', e.target.value)} 
                  >
                    <option value="">All Facilities</option>
                    {uniqueFacilities.map((facility) => (
                      <option key={facility} value={facility}>{facility}</option>
                    ))}
                  </select>
                </div>

                {/* Date Section (Full Width) */}
                <div className={`col-span-2 p-3 rounded-lg border transition-colors ${
                  darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'
                }`}>
                  <div className="flex justify-between items-center mb-2">
                    <label className={`text-xs font-semibold uppercase ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>Date Filter</label>
                    <div className={`flex rounded p-0.5 ${
                      darkMode ? 'bg-gray-600' : 'bg-gray-200'
                    }`}>
                      <button onClick={() => setDateMode('range')} className={`text-[10px] px-2 py-0.5 rounded ${
                        dateMode === 'range' 
                          ? (darkMode ? 'bg-gray-800 shadow text-gray-100' : 'bg-white shadow text-black') 
                          : (darkMode ? 'text-gray-400' : 'text-gray-500')
                      }`}>Range</button>
                      <button onClick={() => setDateMode('single')} className={`text-[10px] px-2 py-0.5 rounded ${
                        dateMode === 'single' 
                          ? (darkMode ? 'bg-gray-800 shadow text-gray-100' : 'bg-white shadow text-black') 
                          : (darkMode ? 'text-gray-400' : 'text-gray-500')
                      }`}>Single</button>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {dateMode === 'single' ? (
                       <input type="month" className={`w-full text-sm p-1.5 border rounded transition-colors ${
                         darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                       }`} value={filters.startDate} onChange={e => handleFilterChange('startDate', e.target.value)} />
                    ) : (
                      <>
                        <input type="month" placeholder="Start" className={`w-1/2 text-sm p-1.5 border rounded transition-colors ${
                          darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`} value={filters.startDate} onChange={e => handleFilterChange('startDate', e.target.value)} />
                        <span className={`self-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</span>
                        <input type="month" placeholder="End" className={`w-1/2 text-sm p-1.5 border rounded transition-colors ${
                          darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`} value={filters.endDate} onChange={e => handleFilterChange('endDate', e.target.value)} />
                      </>
                    )}
                  </div>
                </div>

                {/* Type */}
                <div>
                   <label className={`text-xs font-semibold uppercase ${
                     darkMode ? 'text-gray-400' : 'text-gray-500'
                   }`}>Utility Type</label>
                   <select className={`w-full mt-1 text-sm p-2 border rounded outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                     darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                   }`} value={filters.type} onChange={e => handleFilterChange('type', e.target.value)}>
                    <option value="">All Types</option>
                    <option value="electric">Electric</option>
                    <option value="gas">Gas</option>
                    <option value="water">Water</option>
                  </select>
                </div>

                {/* Amount */}
                <div>
                   <label className={`text-xs font-semibold uppercase ${
                     darkMode ? 'text-gray-400' : 'text-gray-500'
                   }`}>Min Cost</label>
                   <input placeholder="$0.00" className={`w-full mt-1 text-sm p-2 border rounded outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                     darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'
                   }`} value={filters.amount} onChange={e => handleFilterChange('amount', e.target.value)} />
                </div>
                
                {/* User */}
                <div className="col-span-2">
                   <label className={`text-xs font-semibold uppercase ${
                     darkMode ? 'text-gray-400' : 'text-gray-500'
                   }`}>Uploaded By</label>
                   <input placeholder="User email..." className={`w-full mt-1 text-sm p-2 border rounded outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                     darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'
                   }`} value={filters.uploaded_by} onChange={e => handleFilterChange('uploaded_by', e.target.value)} />
                </div>
              </div>
            </div>
          )}
            </div>
        </div>
      </div>
      </div>

      {/* --- TABLE --- */}
      <div className={`rounded-xl shadow-sm border overflow-hidden z-0 transition-colors duration-200 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="overflow-x-auto min-h-[400px]">
          <table className={`min-w-full divide-y text-sm ${
            darkMode ? 'divide-gray-700' : 'divide-gray-200'
          }`}>
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="px-2 py-2 text-left">
                  <input 
                    type="checkbox"
                    className="rounded border-gray-300 cursor-pointer"
                    checked={processedBills.length > 0 && processedBills.every(b => selectedIds.includes(b.id))}
                    onChange={(e) => handleSelectAll(e, processedBills)}
                  />
                </th>
                <th onClick={() => handleSort('site_name')} className="px-4 py-3 text-left font-semibold cursor-pointer">
                  Facility <SortIcon columnKey="site_name" />
                </th>
                <th onClick={() => handleSort('date')} className="px-4 py-3 text-left font-semibold cursor-pointer">
                  Date <SortIcon columnKey="date" />
                </th>
                <th onClick={() => handleSort('billing_month')} className="px-4 py-3 text-left font-semibold cursor-pointer">
                  Bill Mo. <SortIcon columnKey="billing_month" />
                </th>
                <th onClick={() => handleSort('type')} className="px-4 py-3 text-left font-semibold cursor-pointer">
                  Type <SortIcon columnKey="type" />
                </th>
                <th onClick={() => handleSort('amount')} className="px-4 py-3 text-right font-semibold cursor-pointer">
                  Cost <SortIcon columnKey="amount" />
                </th>
                <th className="px-4 py-3 text-center font-semibold">PDF</th>
                <th className="px-4 py-3 text-center font-semibold">Act.</th>
              </tr>
              {/* NOTE: We removed the ugly 2nd TR row here! */}
            </thead>

            <tbody className={`divide-y ${
              darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'
            }`}>
              {pageBills.length === 0 ? (
                <tr><td colSpan="8" className={`px-4 py-3 text-center text-sm ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {processedBills.length === 0 ? 'No matching bills found.' : 'No bills on this page.'}
                </td></tr>
              ) : (
                processedBills.map((bill) => (
                  <tr key={bill.id} onClick={() => onEditRequest(bill)} className={`transition-colors group cursor-pointer ${
                    selectedIds.includes(bill.id) 
                      ? (darkMode ? 'bg-blue-900/30' : 'bg-blue-50') 
                      : (darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50')
                  }`}>
                    <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                      <input 
                        type="checkbox"
                        className="rounded border-gray-300 cursor-pointer"
                        checked={selectedIds.includes(bill.id)}
                        onChange={() => handleSelectRow(bill.id)}
                      />
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      <div className="truncate flex items-center gap-2">
                        <span>{bill.site_name}</span>
                        {bill.is_placeholder && (
                          <span 
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" 
                            title={bill.placeholder_note || 'Placeholder for month with no usage'}
                          >
                            PLACEHOLDER
                          </span>
                        )}
                        {bill.manual_entry && !bill.is_placeholder && (
                          <span 
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" 
                            title="Manually entered"
                          >
                            MANUAL
                          </span>
                        )}
                      </div>
                      {bill.site_address && <div className={`text-[10px] font-normal truncate ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>{bill.site_address.split(',')[0]}</div>}
                      {bill.is_placeholder && bill.placeholder_note && (
                        <div className={`text-[10px] font-normal truncate mt-0.5 italic ${
                          darkMode ? 'text-purple-400/70' : 'text-purple-600/70'
                        }`} title={bill.placeholder_note}>
                          <span className="not-italic font-medium">Note:</span> {bill.placeholder_note}
                        </div>
                      )}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>{bill.date}</td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {bill.billing_month || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium capitalize
                        ${bill.type === 'electric' ? 'bg-yellow-100 text-yellow-800' : ''}
                        ${bill.type === 'gas' ? 'bg-orange-100 text-orange-800' : ''}
                        ${bill.type === 'water' ? 'bg-blue-100 text-blue-800' : ''}
                      `}>{bill.type}</span>
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-semibold text-right ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                       ${parseFloat(bill.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center" onClick={e => e.stopPropagation()}>
                      {bill.fileUrl ? (
                       <button 
                        onClick={() => onSecureView(bill.fileUrl)}
                        className="inline-flex items-center justify-center p-1 text-blue-600 bg-blue-100 rounded hover:bg-blue-600 hover:text-white transition-all" 
                        title="Open Secure PDF"
                      >
                        <FileText className="h-3.5 w-3.5" />
                      </button>
                      ) : <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-2 justify-center">
                        <button type="button" onClick={() => onEditRequest(bill)} className={`p-1 hover:bg-blue-100 dark:hover:bg-blue-900 rounded transition-colors ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`} title="View/Edit"><Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" /></button>
                        <button type="button" onClick={() => onArchiveRequest(bill)} className={`p-1 hover:bg-orange-100 dark:hover:bg-orange-900 rounded transition-colors ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`} title="Archive"><ArchiveRestore className="h-4 w-4 text-orange-600 dark:text-orange-400" /></button>
                        <button type="button" onClick={() => onDeleteRequest(bill)} className={`p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded transition-colors ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`} title="Delete"><Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {processedBills.length > 0 && (
          <div className={`border-t px-6 py-4 transition-colors duration-200 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              {/* Left: Results info */}
              <div className="flex items-center gap-4">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Page <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{currentPage}</span>{' '}
                  • Showing <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{processedBills.length}</span>{' '}
                  results (page size {itemsPerPage})
                </p>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    const selectedPageSize = Number(e.target.value);
                    setItemsPerPage(selectedPageSize);
                    setCurrentPage(1);
                    pageCursorsRef.current = [];
                    fetchPage(1, selectedPageSize);
                  }}
                  className={`text-sm border rounded px-2 py-1 outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>

              {/* Right: Page navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 text-sm border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>

                <button
                  onClick={() => fetchPage(currentPage + 1)}
                  disabled={!hasMore}
                  className={`px-3 py-1 text-sm border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
// 5. LOGIN PAGE
// --- LOGIN PAGE COMPONENT ---
const LoginPage = ({ darkMode, toggleDarkMode }) => {
  // Modes: 'login' | 'reset' | 'request'
  const [authMode, setAuthMode] = useState('login');
  
  // Form States
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(''); // New for Request
  const [company, setCompany] = useState('');   // New for Request
  
  // Feedback
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
    setFullName('');
    setCompany('');
  };

  // 1. Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError("Failed to sign in. Check credentials.");
      setIsLoading(false);
    }
  };

  // 2. Handle Password Reset
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccessMessage("Reset link sent! Check your inbox.");
    } catch (err) {
      setError("Failed to send reset email.");
    } finally {
      setIsLoading(false);
    }
  };

  // 3. Handle Access Request (The New Feature)
  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      // Save request to Firestore "access_requests" collection
      await addDoc(collection(db, "access_requests"), {
        full_name: fullName,
        email: email,
        company: company,
        request_date: new Date().toISOString(),
        status: "pending"
      });

      setSuccessMessage("Request sent! An admin will review your account shortly.");
      // Optional: Clear form
      setFullName('');
      setCompany('');
    } catch (err) {
      logger.error(err);
      setError("Failed to submit request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors duration-200 ${
      darkMode ? 'bg-[#121212]' : 'bg-gray-50'
    }`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        
        {/* Dark Mode Toggle */}
        <div className="flex justify-end mb-4 px-4">
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg transition-colors ${
              darkMode 
                ? 'bg-[#1a1a2e] text-gray-300 hover:text-cyan-400 border border-cyan-500/30' 
                : 'bg-white text-gray-600 hover:text-cyan-600 shadow-sm border border-sky-400/30'
            }`}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>
        
        {/* LOGO */}
        <div className="flex justify-center mb-4">
           <img 
             src="/logo_multi.png" 
             alt="Lift EHS Logo" 
             className="h-40 w-auto object-contain" 
           />
        </div>

        <h2 className={`mt-2 text-center text-3xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {authMode === 'login' && "Sign in to UtiliTrack"}
          {authMode === 'reset' && "Reset Password"}
          {authMode === 'request' && "Request Access"}
        </h2>
        
        <p className={`mt-2 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {authMode === 'request' && "Fill out the form below to apply for an account."}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`py-8 px-4 sm:rounded-lg sm:px-10 transition-all duration-200 ${
          darkMode 
            ? 'bg-[#121212] border border-cyan-500 shadow-[0_0_20px_rgba(14,165,233,0.3),inset_0_0_10px_rgba(14,165,233,0.1)]' 
            : 'bg-white border border-sky-500/40 shadow-[0_4px_15px_rgba(14,165,233,0.125)]'
        }`}>
          
          {/* FEEDBACK MESSAGES */}
          {error && <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 text-sm text-red-700">{error}</div>}
          {successMessage && <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4 text-sm text-green-700">{successMessage}</div>}

          {/* === FORM START === */}
          <form className="space-y-6" onSubmit={
            authMode === 'login' ? handleLogin : 
            authMode === 'reset' ? handlePasswordReset : 
            handleRequestAccess
          }>
            
            {/* NAME & COMPANY (Request Mode Only) */}
            {authMode === 'request' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                  <input 
                    type="text" 
                    required 
                    value={fullName} 
                    onChange={(e) => setFullName(e.target.value)} 
                    className="input-dashboard mt-1 block w-full shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company / Facility</label>
                  <input 
                    type="text" 
                    required 
                    value={company} 
                    onChange={(e) => setCompany(e.target.value)} 
                    className="input-dashboard mt-1 block w-full shadow-sm p-2"
                  />
                </div>
              </>
            )}

            {/* EMAIL (All Modes) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
              <input 
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                className="input-dashboard mt-1 block w-full shadow-sm p-2"
              />
            </div>

            {/* PASSWORD (Login Mode Only) */}
            {authMode === 'login' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                <input 
                  type="password" 
                  required 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="input-dashboard mt-1 block w-full shadow-sm p-2"
                />
              </div>
            )}

            {/* BUTTON */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 dark:shadow-[0_0_12px_rgba(14,165,233,0.25)] focus:outline-none disabled:opacity-50 transition-all"
            >
              {isLoading ? "Processing..." : (
                authMode === 'login' ? "Sign in" :
                authMode === 'reset' ? "Send Reset Link" :
                "Submit Request"
              )}
            </button>
          </form>

          {/* TOGGLE LINKS */}
          <div className="mt-6 space-y-2">
            
            {/* Toggle Reset / Login */}
            {authMode === 'login' && (
              <div className="flex justify-center">
                <button 
                  onClick={() => { setAuthMode('reset'); resetForm(); }} 
                  className={`text-sm ${darkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'}`}
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {/* Toggle Request / Login */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${darkMode ? 'bg-[#121212] text-gray-400' : 'bg-white text-gray-500'}`}>OR</span>
              </div>
            </div>

            <div className="flex justify-center">
              {authMode === 'login' ? (
                <button 
                  onClick={() => { setAuthMode('request'); resetForm(); }} 
                  className={`text-sm font-medium ${
                    darkMode 
                      ? 'text-gray-400 hover:text-cyan-400' 
                      : 'text-gray-600 hover:text-cyan-600'
                  }`}
                >
                  Don't have an account? <span className={darkMode ? 'text-cyan-400' : 'text-cyan-600'}>Request Access</span>
                </button>
              ) : (
                <button 
                  onClick={() => { setAuthMode('login'); resetForm(); }} 
                  className={`text-sm font-medium flex items-center gap-1 ${
                    darkMode 
                      ? 'text-cyan-400 hover:text-cyan-300' 
                      : 'text-cyan-600 hover:text-cyan-700'
                  }`}
                >
                  <ArrowLeft className="h-4 w-4" /> Back to Sign In
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// --- EXPORT BILLS MODAL COMPONENT ---
const ExportBillsModal = ({ _bills, onClose, onExport, darkMode, allFacilities, exportProgress }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedUtility, setSelectedUtility] = useState('');
  const [selectedBills, setSelectedBills] = useState([]);
  const [serverBills, setServerBills] = useState([]);
  const [_exportLoading, setExportLoading] = useState(false);

  const isExporting = exportProgress !== null && exportProgress !== undefined;

  // Fetch bills from server when filters change
  useEffect(() => {
    const fetchExportBills = async () => {
      setExportLoading(true);
      try {
        const result = await exportBillDataFunction({
          facility: selectedFacility || undefined,
          type: selectedUtility || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined
        });
        setServerBills((result.data.bills || []).filter(b => b.fileUrl));
      } catch (error) {
        logger.error('Error fetching export bills:', error);
        setServerBills([]);
      } finally {
        setExportLoading(false);
      }
    };
    fetchExportBills();
  }, [startDate, endDate, selectedFacility, selectedUtility]);

  const facilities = allFacilities || [];
  const utilities = ['electric', 'gas', 'water', 'sewer', 'trash'];

  const filteredBills = serverBills;

  const handleSelectBill = (billId) => {
    setSelectedBills(prev => 
      prev.includes(billId) ? prev.filter(id => id !== billId) : [...prev, billId]
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredBills.map(b => b.id);
    if (selectedBills.length === filteredIds.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(filteredIds);
    }
  };

  const selectedBillObjects = serverBills.filter(b => selectedBills.includes(b.id));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-200 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 border-b p-6 flex items-center justify-between transition-colors duration-200 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-xl font-bold flex items-center gap-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            <Download className="h-6 w-6 text-[#0950A0]" />
            Export Bills as ZIP
          </h3>
          <button onClick={onClose} className={`p-2 rounded-full transition-colors ${
            darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}>
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className={`p-6 space-y-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date Range Row */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[#0950A0] ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[#0950A0] ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            {/* Facility & Utility Row */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Facility
              </label>
              <select 
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[#0950A0] ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">All Facilities</option>
                {facilities.map(facility => (
                  <option key={facility} value={facility}>{facility}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Utility
              </label>
              <select 
                value={selectedUtility}
                onChange={(e) => setSelectedUtility(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[#0950A0] ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">All Utilities</option>
                {utilities.map(utility => (
                  <option key={utility} value={utility}>
                    {utility.charAt(0).toUpperCase() + utility.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bills List */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                Select Bills ({selectedBills.length} of {filteredBills.length})
              </h4>
              {filteredBills.length > 0 && (
                <button
                  onClick={handleSelectAllFiltered}
                  className={`text-sm px-3 py-1 rounded transition-colors ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  {selectedBills.length === filteredBills.length && filteredBills.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            <div className={`border rounded-lg overflow-hidden ${
              darkMode ? 'border-gray-700' : 'border-gray-300'
            }`}>
              <div className={`max-h-96 overflow-y-auto ${
                darkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                {filteredBills.length === 0 ? (
                  <div className={`p-4 text-center text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    No bills match your selection criteria
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <tbody className={`divide-y ${
                      darkMode ? 'divide-gray-600' : 'divide-gray-200'
                    }`}>
                      {filteredBills.map(bill => (
                        <tr key={bill.id} className={`transition-colors ${
                          darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                        }`}>
                          <td className="px-4 py-3 w-10">
                            <input 
                              type="checkbox"
                              checked={selectedBills.includes(bill.id)}
                              onChange={() => handleSelectBill(bill.id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className={`px-4 py-3 ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {bill.date}
                          </td>
                          <td className={`px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {bill.site_name}
                          </td>
                          <td className={`px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span className={`capitalize px-2 py-1 rounded text-xs font-semibold ${
                              bill.type === 'electric' ? 'bg-yellow-100 text-yellow-800' :
                              bill.type === 'gas' ? 'bg-orange-100 text-orange-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {bill.type}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Export Progress */}
          {isExporting && (
            <div className={`p-4 rounded-lg border ${
              darkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <Loader2 className="h-5 w-5 animate-spin text-[#0950A0]" />
                <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-blue-900'}`}>
                  {exportProgress.stage === 'downloading' 
                    ? `Downloading files... (${exportProgress.current} of ${exportProgress.total})` 
                    : 'Compressing ZIP...'}
                </span>
              </div>
              {/* Progress Bar */}
              <div className={`w-full h-3 rounded-full overflow-hidden ${
                darkMode ? 'bg-gray-600' : 'bg-blue-200'
              }`}>
                <div 
                  className="h-full bg-[#0950A0] rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${exportProgress.percent}%` }}
                />
              </div>
              <p className={`text-xs mt-1 text-right font-medium ${
                darkMode ? 'text-gray-400' : 'text-blue-700'
              }`}>
                {Math.round(exportProgress.percent)}%
              </p>
            </div>
          )}

          {/* Folder Structure Preview */}
          <div className={`p-4 rounded-lg border ${
            darkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'
          }`}>
            <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-blue-900'}`}>
              📁 Export Structure: Year &gt; Facility &gt; Utility
            </p>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-blue-700'}`}>
              Files will be organized as: <code className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>2025/Building A/electric/2025-01-15.pdf</code>
            </p>
          </div>
        </div>

        {/* Footer / Actions */}
        <div className={`sticky bottom-0 border-t p-6 flex justify-end gap-3 transition-colors duration-200 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <button 
            onClick={onClose}
            disabled={isExporting}
            className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
              darkMode 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
            } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Cancel
          </button>
          <button 
            onClick={() => onExport(selectedBillObjects)}
            disabled={selectedBills.length === 0 || isExporting}
            className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              selectedBills.length === 0 || isExporting
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-[#0950A0] text-white hover:bg-[#0950A0]/90'
            }`}
          >
            {isExporting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Exporting...</>
            ) : (
              <><Download className="h-4 w-4" /> Export {selectedBills.length > 0 ? `(${selectedBills.length})` : ''}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- MAIN APP ---
// --- PDF VIEWER MODAL COMPONENT ---
const PdfViewer = ({ fileUrl, onClose, darkMode }) => {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200 p-4">
      
      {/* Modal Container */}
      <div className={`rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col relative transition-colors duration-200 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}>
        
        {/* Header */}
        <div className={`flex justify-between items-center p-4 border-b rounded-t-xl transition-colors duration-200 ${
          darkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <h3 className={`font-bold flex items-center gap-2 ${
            darkMode ? 'text-gray-100' : 'text-gray-700'
          }`}>
            <FileText className="h-5 w-5 text-[#0950A0]" />
            Secure Document Viewer
          </h3>
          <button 
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              darkMode
                ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200'
                : 'hover:bg-gray-200 text-gray-500'
            }`}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* The PDF Frame */}
        <div className={`flex-1 p-0 overflow-hidden relative transition-colors duration-200 ${
          darkMode ? 'bg-gray-950' : 'bg-gray-100'
        }`}>
          <iframe 
            src={fileUrl} 
            className="w-full h-full border-none" 
            title="Bill PDF"
          />
        </div>
      </div>
    </div>
  );
};
export default function App() {
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('utilitrack_active_tab');
    return ['dashboard', 'scan', 'bills', 'archive'].includes(saved) ? saved : 'dashboard';
  });

  // Persist active tab so page refresh returns to the same tab
  useEffect(() => {
    localStorage.setItem('utilitrack_active_tab', activeTab);
  }, [activeTab]);
  
  // --- 1. AUTH STATE ---
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // --- 2. DATA STATE ---
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false); // Saving Overlay
  
  // --- PAGINATION STATE (for display only - all bills loaded on startup) ---
  const BILLS_FETCH_SIZE = 100; // Fetch 100 bills per batch from Firestore
  
  // --- MONTHLY STATS (shared across app - fetched once) ---
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // --- FACILITY LIST (derived from monthlyStats) ---
  const allFacilities = useMemo(() => {
    if (monthlyStats.length === 0) return [];
    return [...new Set(monthlyStats.map(s => s.facility))].sort();
  }, [monthlyStats]);
  
  // --- APPROVED LOCATIONS (for auto-matching) ---
  const [approvedLocations, setApprovedLocations] = useState([]);
  const [_approvedLocationsLoading, setApprovedLocationsLoading] = useState(false);
  const [showLocationManager, setShowLocationManager] = useState(false);

  // --- 3. UI STATE (Modals) ---
  const [deleteItem, setDeleteItem] = useState(null);
  const [bulkDeleteIds, setBulkDeleteIds] = useState(null); // IDs pending bulk delete confirmation
  const [_archivingItem, setArchivingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null); // PDF Modal
  const [duplicateGroups, setDuplicateGroups] = useState(null); // Audit Modal
  const [showMissingBillsModal, setShowMissingBillsModal] = useState(false);
  const [reportParams, setReportParams] = useState(null);
  const [autoCorrectPreview, setAutoCorrectPreview] = useState(null); // Auto-correct preview modal
  const [markedIncorrect, setMarkedIncorrect] = useState({}); // Track which corrections user marked as incorrect
  const [showIgnoredRulesManager, setShowIgnoredRulesManager] = useState(false);
  const [comparingDuplicate, setComparingDuplicate] = useState(null); // Side-by-side comparison
  const [ignoredDuplicates, setIgnoredDuplicates] = useState(() => {
    const saved = localStorage.getItem('ignoredDuplicates');
    return saved ? JSON.parse(saved) : [];
  });
  const [ignoredCorrections, setIgnoredCorrections] = useState(() => {
    const saved = localStorage.getItem('ignoredCorrections');
    return saved ? JSON.parse(saved) : [];
  });
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    const isDark = saved ? JSON.parse(saved) : false;
    // Sync <html> class on initial load
    if (isDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    return isDark;
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [zipExportProgress, setZipExportProgress] = useState(null); // { stage, current, total, percent }
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [archiveRefreshNonce, setArchiveRefreshNonce] = useState(0);
  const [billListRefreshKey, setBillListRefreshKey] = useState(0); // Triggers BillList refresh after new uploads
  const [showAnomalyAnalysis, setShowAnomalyAnalysis] = useState(false); // AI Utility Anomaly Analysis modal
  const [showEnterpriseAnalytics, setShowEnterpriseAnalytics] = useState(false); // Enterprise Analytics modal
  const [showSettings, setShowSettings] = useState(false);
  const [appSettings, setAppSettings] = useState({ anomalySensitivity: 30 });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const snap = await getDoc(doc(db, 'app_settings', 'global'));
        if (snap.exists()) setAppSettings(snap.data());
      } catch (err) { logger.warn('Failed to load settings:', err); }
    };
    loadSettings();
  }, []);

  // --- 4. RATE LIMITER ---
  const rateLimiter = useOperationRateLimiter();

  // --- SESSION TRACKER (tracks all Firestore reads for analysis) ---
  const sessionTracker = useSessionTracker(user?.email);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newValue));
      // Sync <html> class for Tailwind dark: variants
      if (newValue) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
      return newValue;
    });
  };

  // Export bills as ZIP (uses Firebase Storage SDK to avoid CORS issues)
  const handleExportBills = async (selectedBills) => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to export');
      return;
    }

    try {
      const zip = new JSZip();
      let successCount = 0;
      let failCount = 0;
      const total = selectedBills.length;

      // --- Phase 1: Download files ---
      for (let i = 0; i < selectedBills.length; i++) {
        const bill = selectedBills[i];
        setZipExportProgress({ 
          stage: 'downloading', 
          current: i + 1, 
          total, 
          percent: Math.round(((i) / total) * 80) // downloads = 0–80%
        });

        if (!bill.fileUrl) {
          failCount++;
          continue;
        }

        try {
          // Get the year from the bill date
          const year = bill.date ? bill.date.split('-')[0] : 'unknown';
          const facility = bill.site_name || 'unknown';
          const utility = bill.type || 'unknown';
          
          // Create folder structure: year/facility/utility
          const folderPath = `${year}/${facility}/${utility}`;
          
          // Use Firebase Storage SDK getBlob to avoid CORS issues
          const fileRef = ref(storage, bill.fileUrl);
          const blob = await getBlob(fileRef);
          
          // Generate filename
          const filename = bill.date || 'bill';
          const ext = blob.type.includes('pdf') ? '.pdf' : '.jpg';
          const fullPath = `${folderPath}/${filename}${ext}`;
          
          // Add to ZIP
          zip.file(fullPath, blob);
          successCount++;
        } catch (err) {
          logger.error(`Failed to add ${bill.id} to ZIP:`, err);
          failCount++;
        }
      }

      // --- Phase 2: Generate ZIP ---
      setZipExportProgress({ stage: 'compressing', current: total, total, percent: 82 });

      const zipBlob = await zip.generateAsync(
        { type: 'blob' },
        (metadata) => {
          // JSZip progress callback: metadata.percent goes from 0-100 for ZIP generation
          const overallPercent = 80 + Math.round(metadata.percent * 0.2); // 80–100%
          setZipExportProgress({ stage: 'compressing', current: total, total, percent: overallPercent });
        }
      );

      // --- Phase 3: Trigger download ---
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bills_export_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setZipExportProgress(null);
      setShowExportModal(false);
      setToastMessage(`Export complete! ${successCount} bills exported${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setShowToast(true);
    } catch (error) {
      logger.error('Export error:', error);
      setZipExportProgress(null);
      alert('Failed to export bills: ' + error.message);
    }
  };

  // --- 4. AUTH LISTENER ---
  // Clear all filters/persisted state when user logs out
  const clearAllUserData = useCallback(() => {
    // Flush session tracking data before clearing
    sessionTracker.flushSession();
    localStorage.removeItem('utilitrack_billlist_filters');
    localStorage.removeItem('utilitrack_dashboard_filters');
    localStorage.removeItem('utilitrack_missing_filters');
    localStorage.removeItem('utilitrack_archive_filters');
    localStorage.removeItem('utilitrack_monthly_stats_cache');
    localStorage.removeItem('utilitrack_monthly_stats_ts');
    localStorage.removeItem('utilitrack_approved_locations_cache');
    localStorage.removeItem('utilitrack_approved_locations_ts');
    // Reset bills and data
    setBills([]);
  }, [sessionTracker]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        // User logged out - clear all filters and data
        clearAllUserData();
      }
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [clearAllUserData]);

  // --- 5. FETCH BILLS ---
  // Full startup load removed — BillList uses paginated fetchPage() with limit()/startAfter().
  // Heavy operations (duplicate audit, auto-correct, export, analysis) now use Cloud Functions.
  // The `bills` state is still maintained for local updates after save/delete/archive operations.
  const fetchBills = useCallback(async () => {
    if (!user) return;
    // No longer loads all bills on startup — data is fetched on-demand via
    // paginated queries (fetchPage) and server-side Cloud Functions.
    setIsLoading(false);
    logger.log('[fetchBills] Startup bill load skipped — using paginated queries and Cloud Functions');
  }, [user]);

  useEffect(() => {
    fetchBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Run once per user login

  // --- FETCH MONTHLY STATS (server-side via Cloud Function) ---
  // Caches stats in localStorage with a 5-minute TTL to avoid redundant Cloud Function reads.
  // Pass forceRefresh=true after mutations (save, auto-correct) to bypass the cache.
  // Initial load defaults to rolling_12 to reduce doc reads; full history only when user selects it.
  const STATS_CACHE_KEY = 'utilitrack_monthly_stats_cache';
  const STATS_CACHE_TS_KEY = 'utilitrack_monthly_stats_ts';
  const STATS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const fetchMonthlyStats = useCallback(async (forceRefresh = false) => {
    if (!user) return;
    setStatsLoading(true);
    try {
      // Check localStorage cache first (unless forced refresh after a mutation)
      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem(STATS_CACHE_KEY);
          const cachedTs = localStorage.getItem(STATS_CACHE_TS_KEY);
          if (cached && cachedTs && (Date.now() - parseInt(cachedTs)) < STATS_CACHE_TTL) {
            const stats = JSON.parse(cached);
            setMonthlyStats(stats);
            logger.log(`[fetchMonthlyStats] Loaded ${stats.length} stats from cache (age: ${Math.round((Date.now() - parseInt(cachedTs)) / 1000)}s)`);
            return;
          }
        } catch { /* cache read failed, proceed with fetch */ }
      }

      // Default to rolling_12 on initial load to reduce doc reads
      try {
        const result = await getMonthlyStatsFunction({ timeRange: 'rolling_12' });
        const stats = result.data.stats || [];
        setMonthlyStats(stats);

        // Persist to localStorage cache
        try {
          localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(stats));
          localStorage.setItem(STATS_CACHE_TS_KEY, String(Date.now()));
        } catch { /* storage full or unavailable, non-critical */ }

        sessionTracker.trackRead('fetchMonthlyStats', 'monthly_stats', stats.length, { via: 'CloudFunction', cached: false });
        logger.log(`[fetchMonthlyStats] Loaded ${stats.length} monthly stats via Cloud Function (rolling 12 months)`);
      } catch (cloudFnError) {
        logger.warn('[fetchMonthlyStats] Cloud Function failed; falling back to direct Firestore read for rolling_12.', cloudFnError);

        const now = new Date();
        const cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        const cutoff = `${cutoffDate.getFullYear()}-${String(cutoffDate.getMonth() + 1).padStart(2, '0')}`;

        const fallbackQuery = query(
          collection(db, 'monthly_stats'),
          where('month', '>=', cutoff)
        );

        const fallbackSnapshot = await getDocs(fallbackQuery);
        const fallbackStats = fallbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMonthlyStats(fallbackStats);

        try {
          localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(fallbackStats));
          localStorage.setItem(STATS_CACHE_TS_KEY, String(Date.now()));
        } catch { /* storage full or unavailable, non-critical */ }

        sessionTracker.trackRead('fetchMonthlyStats', 'monthly_stats', fallbackStats.length, { via: 'FirestoreFallback', cached: false });
        logger.log(`[fetchMonthlyStats] Loaded ${fallbackStats.length} monthly stats via Firestore fallback (rolling 12 months)`);
      }
    } catch (error) {
      logger.error("Error fetching monthly stats:", error);
      setMonthlyStats([]);
    } finally {
      setStatsLoading(false);
    }
  }, [user, STATS_CACHE_TTL, sessionTracker]);

  useEffect(() => {
    fetchMonthlyStats();
  }, [fetchMonthlyStats]);
  // Runs once per user login
  
  // --- LOAD APPROVED LOCATIONS (for auto-matching, cached in localStorage) ---
  const LOCS_CACHE_KEY = 'utilitrack_approved_locations_cache';
  const LOCS_CACHE_TS_KEY = 'utilitrack_approved_locations_ts';
  const LOCS_CACHE_TTL = 10 * 60 * 1000; // 10 minutes (locations change rarely)

  const loadApprovedLocations = useCallback(async (forceRefresh = false) => {
    if (!user) return [];
    setApprovedLocationsLoading(true);
    try {
      // Check localStorage cache first (unless forced refresh)
      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem(LOCS_CACHE_KEY);
          const cachedTs = localStorage.getItem(LOCS_CACHE_TS_KEY);
          if (cached && cachedTs && (Date.now() - parseInt(cachedTs)) < LOCS_CACHE_TTL) {
            const locs = JSON.parse(cached);
            setApprovedLocations(locs);
            logger.log(`[loadApprovedLocations] Loaded ${locs.length} locations from cache (age: ${Math.round((Date.now() - parseInt(cachedTs)) / 1000)}s)`);
            return locs;
          }
        } catch { /* cache read failed, proceed with fetch */ }
      }

      const snapshot = await getDocs(collection(db, "approved_locations"));
      const locs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      sessionTracker.trackRead('loadApprovedLocations', 'approved_locations', locs.length);
      setApprovedLocations(locs);

      // Persist to localStorage cache
      try {
        localStorage.setItem(LOCS_CACHE_KEY, JSON.stringify(locs));
        localStorage.setItem(LOCS_CACHE_TS_KEY, String(Date.now()));
      } catch { /* storage full or unavailable, non-critical */ }

      return locs;
    } catch (error) {
      logger.error("Error loading approved locations:", error);
      return [];
    } finally {
      setApprovedLocationsLoading(false);
    }
  }, [user, LOCS_CACHE_TTL, sessionTracker]);

  useEffect(() => {
    loadApprovedLocations();
  }, [loadApprovedLocations]);
  // Runs once per user login

  // --- CHECK URL PARAMETERS FOR SHARED REPORT LINK ---
  useEffect(() => {
    // Only check URL params on mount (when user is loaded), not on every bill change
    if (user) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('report') === 'missing_bills') {
        const timeframe = urlParams.get('timeframe');
        const facility = urlParams.get('facility');
        const utilities = urlParams.get('utilities');
        const customStart = urlParams.get('customStart');
        const customEnd = urlParams.get('customEnd');
        
        setReportParams({
          timeframe: timeframe || 'rolling_12',
          facility: facility || 'All',
          utilities: utilities ? utilities.split(',') : ['electric', 'gas', 'water'],
          customStart: customStart || '',
          customEnd: customEnd || ''
        });
        setShowMissingBillsModal(true);
        setActiveTab('dashboard');
      }
    }
  }, [user]); // FIXED: Removed bills.length from deps - only check URL params once on login

  // --- 6. AUTO-LOGOUT TIMER (10 Mins) ---
  useEffect(() => {
    if (!user) return;
    const INACTIVITY_LIMIT = 10 * 60 * 1000; 
    let logoutTimer;

    const handleLogout = () => {
      sessionTracker.flushSession(); // Persist session data before auto-logout
      signOut(auth);
      alert("You have been logged out due to inactivity.");
    };

    const resetTimer = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      logoutTimer = setTimeout(handleLogout, INACTIVITY_LIMIT);
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keypress', resetTimer);
    resetTimer();

    return () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
    };
  }, [user, sessionTracker]);

  // --- PLACEHOLDER PDF MEMO GENERATOR ---
  const generatePlaceholderPDF = (billData) => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    
    // --- Header bar ---
    pdf.setFillColor(9, 80, 160); // #0950A0
    pdf.rect(0, 0, pageWidth, 40, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PLACEHOLDER MEMO', pageWidth / 2, 22, { align: 'center' });
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('UtiliTrack — Utility Bill Tracking System', pageWidth / 2, 32, { align: 'center' });
    
    // --- Body ---
    pdf.setTextColor(40, 40, 40);
    let y = 58;
    
    // Facility info section
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Facility Information', 20, y);
    y += 2;
    pdf.setDrawColor(9, 80, 160);
    pdf.setLineWidth(0.5);
    pdf.line(20, y, pageWidth - 20, y);
    y += 10;
    
    pdf.setFontSize(11);
    const addField = (label, value) => {
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${label}:`, 25, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(String(value || 'N/A'), 80, y);
      y += 8;
    };
    
    addField('Facility', billData.site_name);
    addField('Address', billData.site_address);
    addField('Utility Type', (billData.type || 'N/A').charAt(0).toUpperCase() + (billData.type || '').slice(1));
    addField('Provider', billData.provider);
    addField('Bill Date', billData.date);
    addField('Billing Month', billData.billing_month);
    addField('Created By', billData.uploaded_by);
    addField('Created On', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
    
    y += 8;
    
    // Notes section
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Placeholder Notes', 20, y);
    y += 2;
    pdf.line(20, y, pageWidth - 20, y);
    y += 10;
    
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    const noteText = billData.placeholder_note || 'No bill was received for this period. This placeholder was created to indicate zero usage or account for a missing bill.';
    const noteLines = pdf.splitTextToSize(noteText, pageWidth - 50);
    pdf.text(noteLines, 25, y);
    y += noteLines.length * 6 + 10;
    
    // Disclaimer
    y += 10;
    pdf.setFillColor(245, 245, 245);
    pdf.roundedRect(20, y - 4, pageWidth - 40, 24, 3, 3, 'F');
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 120);
    pdf.setFont('helvetica', 'italic');
    pdf.text('This is a system-generated placeholder memo. No actual utility bill was received for this period.', pageWidth / 2, y + 6, { align: 'center' });
    pdf.text('Amount and usage values are recorded as $0.00 / 0 units.', pageWidth / 2, y + 14, { align: 'center' });
    
    // Return as Blob
    return pdf.output('blob');
  };

  // --- 7. SAVE HANDLER (With AI Math & File Upload) ---
  const handleNewBill = async (newEntries) => {
    // Duplicate Check (Skip if editing)
    if (!editingItem) {
      const duplicates = [];
      newEntries.forEach(entry => {
        // Get the facility name we'll actually save (from address lookup or fallback)
        const entryFacilityName = entry.site_address?.value 
          ? (getFacilityNameFromAddress(entry.site_address.value, approvedLocations) || entry.site_address.value.split(',')[0])
          : "New Facility";
        
        const potentialMatch = bills.find(existingBill => {
          return existingBill.date === entry.bill_date?.value &&
                 parseFloat(existingBill.amount) === parseFloat(entry.total_amount?.value) &&
                 (existingBill.site_name || "").toLowerCase() === entryFacilityName.toLowerCase() &&
                 (existingBill.type || "").toLowerCase() === (entry.type || "").toLowerCase() &&
                 (existingBill.provider || "").toLowerCase() === (entry.utility_provider?.value || "").toLowerCase();
        });
        if (potentialMatch) duplicates.push(`${entryFacilityName} - ${entry.type} - ${entry.bill_date?.value}`);
      });

      if (duplicates.length > 0) {
        if (!window.confirm(`⚠️ DUPLICATE WARNING ⚠️\n\nPossible duplicates found:\n${duplicates.join('\n')}\n\nSave anyway?`)) return;
      }
    }

    setIsSaving(true); 
    const billsCollectionRef = collection(db, "bills");
    const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB guard to avoid large uploads/charges
    const ALLOWED_TYPES = ['application/pdf', 'image/'];

    try {
      for (const data of newEntries) {
        let fileUrl = data.imageUrl || null;

        // Upload Logic with compression
        if (data.rawFile) {
           // Basic size/type guard before any upload/processing
           const isAllowedType = ALLOWED_TYPES.some(t => data.rawFile.type.startsWith(t));
           if (!isAllowedType) {
             alert(`Blocked upload: unsupported file type ${data.rawFile.type || 'unknown'}. Only PDFs and images are allowed.`);
             continue;
           }

           if (data.rawFile.size > MAX_FILE_BYTES) {
             alert(`Blocked upload: file is ${(data.rawFile.size / (1024 * 1024)).toFixed(1)} MB. Max allowed is 20 MB to control storage costs.`);
             continue;
           }

           let fileToUpload = data.rawFile;
           
           // Compress image files before uploading
           if (data.rawFile.type.startsWith('image/')) {
             fileToUpload = await compressImageFile(data.rawFile);
             logger.log(`Compressed image from ${data.rawFile.size} to ${fileToUpload.size} bytes`);
           }
           
           // Compress PDF files before uploading
           if (data.rawFile.type.includes('pdf')) {
             fileToUpload = await compressPDF(data.rawFile);
             // compressPDF already logs compression stats
           }
           
           const storageRef = ref(storage, `bill_uploads/${Date.now()}_${fileToUpload.name}`);
           const snapshot = await uploadBytes(storageRef, fileToUpload);
           fileUrl = await getDownloadURL(snapshot.ref);
        }

        // Auto-correct facility name from address if available
        const correctedFacilityName = data.site_address?.value 
          ? getFacilityNameFromAddress(data.site_address.value, approvedLocations)
          : null;

        // Generate placeholder PDF memo if this is a placeholder with no file attached
        if (data.is_placeholder && !fileUrl) {
          const placeholderBillInfo = {
            site_name: correctedFacilityName || data.site_address?.value?.split(',')[0] || 'New Facility',
            site_address: data.site_address?.value || '',
            date: data.bill_date?.value || new Date().toISOString().split('T')[0],
            type: (data.type || 'other').toLowerCase(),
            provider: data.utility_provider?.value || 'Unknown',
            billing_month: data.billing_month?.value || (data.bill_date?.value ? data.bill_date.value.substring(0, 7) : ''),
            uploaded_by: user?.email || 'Unknown',
            placeholder_note: data.placeholder_note || ''
          };
          try {
            const pdfBlob = generatePlaceholderPDF(placeholderBillInfo);
            const pdfFile = new File([pdfBlob], `placeholder_${placeholderBillInfo.site_name.replace(/[^a-zA-Z0-9]/g, '_')}_${placeholderBillInfo.billing_month}.pdf`, { type: 'application/pdf' });
            const storageRef = ref(storage, `bill_uploads/${Date.now()}_${pdfFile.name}`);
            const snapshot = await uploadBytes(storageRef, pdfFile);
            fileUrl = await getDownloadURL(snapshot.ref);
            logger.log('📄 Generated and uploaded placeholder PDF memo');
          } catch (pdfErr) {
            logger.error('Failed to generate placeholder PDF:', pdfErr);
          }
        }

        const billData = {
          site_name: correctedFacilityName || data.site_address?.value?.split(',')[0] || "New Facility",
          site_address: data.site_address?.value || "",
          date: data.bill_date?.value || new Date().toISOString().split('T')[0],
          type: (data.type || "other").toLowerCase(),
          // Trust the AI for units (CCF logic is in the prompt)
          usage: parseFloat(data.usage_total?.value || "0").toFixed(2), 
          unit: data.usage_total?.unit || "units",
          amount: data.total_amount?.value || "0",
          provider: data.utility_provider?.value || "Unknown",
          billing_month: data.billing_month?.value || (data.bill_date?.value ? data.bill_date.value.substring(0, 7) : ''),
          status: 'verified',
          uploaded_by: user?.email || "Unknown",
          fileUrl: fileUrl,
          archived: false, // CRITICAL: Must be explicitly set for Firestore query to match
          search_tokens: [], // Will be populated below
          updatedAt: new Date().toISOString(),
          is_placeholder: data.is_placeholder || false,
          placeholder_note: data.placeholder_note || '',
          manual_entry: data.manual_entry || false
        };
        
        // Only set createdAt for new bills, not edits
        if (!editingItem) {
          billData.createdAt = new Date().toISOString();
        }
        
        // Generate search tokens for quick filtering
        billData.search_tokens = generateSearchTokens(billData);

        logger.log('💾 Saving bill:', { 
          site_name: billData.site_name,
          address: billData.site_address,
          search_tokens: billData.search_tokens
        });

        if (editingItem) {
          // When editing: remove old fingerprint, add new one
          await removeDuplicateFingerprint(db, editingItem);
          await updateDoc(doc(db, "bills", editingItem.id), billData);
          await updateDuplicateFingerprint(db, { id: editingItem.id, ...billData });
        } else {
          // When adding new: create with ID then update fingerprint
          const docRef = await addDoc(billsCollectionRef, billData);
          await updateDuplicateFingerprint(db, { id: docRef.id, ...billData });
        }
      }
      await fetchBills();
      await fetchMonthlyStats(true); // Force refresh (bypass cache) after saving new bill
      setBillListRefreshKey(k => k + 1); // Trigger BillList to re-query from Firestore
      setEditingItem(null);
      setActiveTab('bills');
    } catch (error) {
      logger.error("Error saving bill:", error);
      alert("Error saving. Check console.");
    } finally {
      setIsSaving(false); 
    }
  };

  // --- 8. DELETE HANDLERS ---
  const archiveBill = useCallback(async (bill) => {
    if (!bill) return;
    const confirmArchive = window.confirm(`Archive bill for ${bill.site_name}? It will move to Archives and can be restored.`);
    if (!confirmArchive) return;

    try {
      setArchivingItem(bill);
      await updateDoc(doc(db, "bills", bill.id), {
        archived: true,
        archivedAt: Timestamp.now(),
        updatedAt: new Date().toISOString()
      });
      // Notify ArchiveViewer to refetch
      setArchiveRefreshNonce(n => n + 1);
      // Remove from active list
      setBills(prev => prev.filter(b => b.id !== bill.id));
      // Refresh the paginated bill list so the archived entry disappears
      setBillListRefreshKey(k => k + 1);
      // If this bill was part of duplicates, remove it from groups
      setDuplicateGroups(prev => prev ? prev.map(group => ({
        ...group,
        bills: group.bills.filter(b => b.id !== bill.id)
      })).filter(group => group.bills.length > 1) : prev);
    } catch (error) {
      logger.error("Error archiving bill:", error);
      alert('Failed to archive bill. Please try again.');
    } finally {
      setArchivingItem(null);
    }
  }, []);

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      if (deleteItem.fileUrl) {
        try { await deleteObject(ref(storage, deleteItem.fileUrl)); } catch (e) { logger.warn("File missing"); }
      }
      await removeDuplicateFingerprint(db, deleteItem);
      await deleteDoc(doc(db, "bills", deleteItem.id));
      setBills(prev => prev.filter(b => b.id !== deleteItem.id));
      // Refresh the paginated list so the deleted entry disappears
      setBillListRefreshKey(k => k + 1);
    } catch (error) {
      logger.error("Error deleting bill:", error);
      alert('Failed to delete bill. Please try again.');
    } finally {
      setDeleteItem(null);
    }
  };

  // Show the bulk-delete confirmation modal (matching the single-delete popup style)
  const handleBulkDelete = (idsToDelete) => {
    if (!idsToDelete || idsToDelete.length === 0) return;
    setBulkDeleteIds(idsToDelete);
  };

  // Actually execute the bulk delete after modal confirmation
  const confirmBulkDelete = async () => {
    if (!bulkDeleteIds || bulkDeleteIds.length === 0) return;
    const idsToDelete = [...bulkDeleteIds];
    setBulkDeleteIds(null); // Close modal immediately
    setIsLoading(true);
    let deletedCount = 0;
    let failedCount = 0;
    try {
      // Fetch full docs for the selected IDs so we can clean up storage/fingerprints
      const batchQuery = query(
        collection(db, 'bills'),
        where('__name__', 'in', idsToDelete.slice(0, 30))
      );
      const snap = await getDocs(batchQuery);
      const targets = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Handle any IDs beyond the first 30 (Firestore 'in' limit) by fetching in batches
      if (idsToDelete.length > 30) {
        for (let i = 30; i < idsToDelete.length; i += 30) {
          const batch = idsToDelete.slice(i, i + 30);
          const batchSnap = await getDocs(query(
            collection(db, 'bills'),
            where('__name__', 'in', batch)
          ));
          batchSnap.docs.forEach(d => targets.push({ id: d.id, ...d.data() }));
        }
      }

      for (const bill of targets) {
        try {
          if (bill.fileUrl) {
            try { await deleteObject(ref(storage, bill.fileUrl)); } catch (_e) { /* file missing */ }
          }
          await removeDuplicateFingerprint(db, bill);
          await deleteDoc(doc(db, "bills", bill.id));
          deletedCount++;
        } catch (billError) {
          logger.error(`Failed to delete bill ${bill.id}:`, billError);
          failedCount++;
        }
      }

      // Refresh the paginated list so deleted entries disappear
      setBills(prev => prev.filter(b => !idsToDelete.includes(b.id)));
      setBillListRefreshKey(k => k + 1);

      if (failedCount > 0) {
        alert(`Deleted ${deletedCount} bills. ${failedCount} failed — please retry those.`);
      }
    } catch (error) {
      logger.error("Bulk delete failed:", error);
      alert('Bulk delete encountered an error. Please refresh and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkAutoCorrect = async () => {
    setIsLoading(true);
    try {
      logger.log('[auto-correct] Scanning for corrections via Cloud Function...');
      
      const result = await scanForCorrectionsFunction({
        ignoredBillIds: ignoredCorrections,
        ignoredRuleAddresses: []
      });
      
      const { proposedChanges, skipReasons, totalScanned } = result.data;
      sessionTracker.trackRead('handleBulkAutoCorrect', 'bills', totalScanned, { via: 'CloudFunction:scanForCorrections', proposedChanges: proposedChanges.length });
      
      if (proposedChanges.length === 0) {
        console.info('[auto-correct] No proposed changes', {
          totalScanned,
          ...skipReasons
        });
        alert("No corrections found. You may need to add locations or clear ignored corrections.");
        setIsLoading(false);
        return;
      }

      // Add UI-level properties for the preview modal
      const changesWithUI = proposedChanges.map(c => ({
        ...c,
        ignored: false
      }));

      console.info(`[auto-correct] Found ${changesWithUI.length} corrections to propose (scanned ${totalScanned} bills)`);
      setAutoCorrectPreview(changesWithUI);
    } catch (error) {
      logger.error('[auto-correct] Error scanning:', error);
      alert("Error scanning for corrections. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const updateAutoCorrectSelection = (index, newValue) => {
    setAutoCorrectPreview(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selectedName: newValue };
      return updated;
    });
  };

  const toggleIgnoreCorrection = (index) => {
    setAutoCorrectPreview(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ignored: !updated[index].ignored };
      return updated;
    });
  };

  const applyAutoCorrections = async () => {
    if (!autoCorrectPreview) return;
    
    // Filter out ignored items and save them to localStorage
    const itemsToApply = autoCorrectPreview.filter(change => !change.ignored);
    const itemsToIgnore = autoCorrectPreview.filter(change => change.ignored).map(c => c.id);
    
    if (itemsToIgnore.length > 0) {
      const updatedIgnored = [...new Set([...ignoredCorrections, ...itemsToIgnore])];
      setIgnoredCorrections(updatedIgnored);
      localStorage.setItem('ignoredCorrections', JSON.stringify(updatedIgnored));
    }
    
    // Build ignored rules from marked incorrect suggestions
    const ignoredRules = [];
    const markedIncorrectEntries = Object.entries(markedIncorrect).filter(([, value]) => value);
    if (markedIncorrectEntries.length > 0) {
      for (const [idx, reason] of markedIncorrectEntries) {
        const change = autoCorrectPreview[parseInt(idx)];
        if (change) {
          ignoredRules.push({
            address: change.address,
            suggestedLocation: change.autoSuggested,
            correctLocation: change.selectedName || 'Unknown',
            reason: reason || 'User marked as incorrect',
            billId: change.id
          });
        }
      }
      setMarkedIncorrect({});
    }
    
    if (itemsToApply.length === 0) {
      alert("No changes to apply. All suggestions were ignored.");
      setAutoCorrectPreview(null);
      return;
    }
    
    setIsLoading(true);
    try {
      // Apply corrections server-side via Cloud Function
      // This updates bills, rebuilds monthly_stats, and saves ignored rules
      const result = await applyAutoCorrectionsFunction({
        corrections: itemsToApply.map(c => ({
          id: c.id,
          currentName: c.currentName,
          selectedName: c.selectedName
        })),
        ignoredRules
      });
      
      const { updatedBills, deletedStats, writtenStats } = result.data;
      logger.log(`✅ Server applied ${updatedBills} corrections, rebuilt ${writtenStats} stats (deleted ${deletedStats})`);
      
      // Refresh monthly stats from server (bypass cache after corrections)
      await fetchMonthlyStats(true);
      
      alert(`Successfully updated ${itemsToApply.length} bill(s). Dashboard and filters refreshed.`);
      setAutoCorrectPreview(null);
    } catch (error) {
      logger.error("Auto-correction failed:", error);
      alert("Error during auto-correction. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

// --- SECURE FILE VIEW (With Auto-Fallback) ---
  const handleSecureView = async (fileUrl) => {
    if (!fileUrl) return;
    document.body.style.cursor = 'wait';
    
    try {
      // 1. Try to fetch the secure Blob (Best for privacy)
      const fileRef = ref(storage, fileUrl);
      const blob = await getBlob(fileRef);
      const secureUrl = URL.createObjectURL(blob);
      setViewingPdfUrl(secureUrl);
      
    } catch (error) {
      logger.warn("Secure fetch failed; offering fallback instead of auto-downloading twice.", error);
      
      // Ask user before triggering a second download/egress
      if (window.confirm("Secure view failed. Open via direct link instead? (may consume additional bandwidth)")) {
        setViewingPdfUrl(fileUrl);
      }
      
    } finally {
      document.body.style.cursor = 'default';
    }
  };

  // --- 10. PDF GENERATOR (fetches bill data server-side) ---
  const generatePDF = async () => {
    try {
      const result = await exportBillDataFunction({});
      const exportBills = result.data.bills || [];
      sessionTracker.trackRead('generatePDF', 'bills', exportBills.length, { via: 'CloudFunction:exportBillData' });
      
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text("UtiliTrack EHS Report", 14, 22);
      doc.setFontSize(11);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
      
      const tableColumn = ["Date", "Facility", "Type", "Usage", "Cost ($)"];
      const tableRows = exportBills.map(b => [
        b.date, b.site_name, b.type, `${b.usage} ${b.unit}`, parseFloat(b.amount).toFixed(2)
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 40,
      });
      doc.save(`UtiliTrack_Report.pdf`);
    } catch (error) {
      logger.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // --- 11. DUPLICATE AUDIT FUNCTION (Server-side via Cloud Function) ---
  const runDuplicateAudit = async () => {
    setIsLoading(true);
    try {
      logger.log('[duplicate-audit] Starting server-side audit...');
      
      const result = await runDuplicateAuditFunction({
        ignoredFingerprints: ignoredDuplicates
      });
      
      const { duplicateGroups: groups, totalScanned } = result.data;
      sessionTracker.trackRead('runDuplicateAudit', 'bills', totalScanned, { via: 'CloudFunction', duplicatesFound: groups.length });
      logger.log(`[duplicate-audit] Server scanned ${totalScanned} bills, found ${groups.length} duplicate groups`);
      
      if (groups.length === 0) {
        alert("Great news! No duplicates found in the database.");
        setIsLoading(false);
        return;
      }

      setDuplicateGroups(groups);
      
    } catch (error) {
      logger.error('[duplicate-audit] Error:', error);
      alert('Error checking for duplicates. Please try again. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- 12. DUPLICATE MANAGEMENT FUNCTIONS ---
  const ignoreDuplicateGroup = (fingerprint) => {
    const updated = [...ignoredDuplicates, fingerprint];
    setIgnoredDuplicates(updated);
    localStorage.setItem('ignoredDuplicates', JSON.stringify(updated));
    
    // Remove from current view and check remaining count
    setDuplicateGroups(prev => {
      const remaining = prev.filter(group => group.fingerprint !== fingerprint);
      if (remaining.length === 0) {
        alert("All duplicates have been reviewed.");
        return null;
      }
      return remaining;
    });
  };

  const archiveDuplicate = async (billToArchive, fingerprint) => {
    try {
      // Update the bill in Firestore to mark as archived
      await updateDoc(doc(db, "bills", billToArchive.id), {
        archived: true,
        archivedAt: Timestamp.now(),
        updatedAt: new Date().toISOString()
      });
      
      // Update duplicate fingerprint (remove this bill from the fingerprint)
      await removeDuplicateFingerprint(db, billToArchive);
      // Notify ArchiveViewer to refetch
      setArchiveRefreshNonce(n => n + 1);
      
      // Remove from local state
      setBills(prev => prev.filter(b => b.id !== billToArchive.id));
      
      // Update the duplicate group
      setDuplicateGroups(prev => {
        const updated = prev.map(group => {
          if (group.fingerprint === fingerprint) {
            return {
              ...group,
              bills: group.bills.filter(b => b.id !== billToArchive.id)
            };
          }
          return group;
        }).filter(group => group.bills.length > 1);
        
        return updated.length > 0 ? updated : null;
      });
      
      alert("Bill archived successfully. You can restore it from the Archive view.");
    } catch (error) {
      logger.error("Error archiving bill:", error);
      alert("Error archiving bill. Check console.");
    }
  };

  // --- EDIT HELPERS ---
  const startEdit = (bill) => { setEditingItem(bill); setActiveTab('scan'); };
  const cancelEdit = () => { setEditingItem(null); setActiveTab('bills'); };

  // --- DEV SHORTCUT: Print session summary to console ---
  useEffect(() => {
    if (import.meta.env.DEV && user) {
      window.__sessionTracker = sessionTracker;
      logger.log('[SESSION-TRACK] Type window.__sessionTracker.printSummary() in console to see read stats');
    }
    return () => { if (import.meta.env.DEV) delete window.__sessionTracker; };
  }, [user, sessionTracker]);

  // --- GATEKEEPER ---
  if (authLoading) return <div className="flex justify-center items-center h-screen"><Loader2 className="animate-spin" /></div>;
  if (!user) return <LoginPage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />;

  // --- MAIN RENDER ---
  return (
    <div className={`min-h-screen font-sans relative transition-colors duration-200 ${
      darkMode ? 'bg-[#121212] text-white' : 'bg-white text-gray-900'
    }`}>
      <Header 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onShowLocationManager={() => setShowLocationManager(true)}
        onShowAnomalyAnalysis={() => setShowAnomalyAnalysis(true)}
        onShowEnterpriseAnalytics={() => setShowEnterpriseAnalytics(true)}
        onShowSettings={() => setShowSettings(true)}
      />
      
      <MobileBottomNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        darkMode={darkMode}
        onShowAnomalyAnalysis={() => setShowAnomalyAnalysis(true)}
        onShowEnterpriseAnalytics={() => setShowEnterpriseAnalytics(true)}
      />
      
      <SystemSettings 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)} 
        darkMode={darkMode} 
        appSettings={appSettings} 
        setAppSettings={setAppSettings} 
      />
      
      <main className={`mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8 ${activeTab === 'dashboard' ? 'max-w-[1920px]' : 'max-w-7xl'}`}>
        {activeTab === 'dashboard' && (
          <Dashboard 
            bills={bills} 
            generatePDF={generatePDF}
            showMissingBillsModal={showMissingBillsModal}
            setShowMissingBillsModal={setShowMissingBillsModal}
            reportParams={reportParams}
            darkMode={darkMode}
            monthlyStats={monthlyStats}
            statsLoading={statsLoading}
            approvedLocations={approvedLocations}
          />
        )}
        
        {activeTab === 'scan' && (
          <Scanner 
            onSave={handleNewBill} 
            initialEditData={editingItem} 
            onCancelEdit={cancelEdit}
            darkMode={darkMode}
            approvedLocations={approvedLocations}
          />
        )}
        
        {activeTab === 'bills' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
               <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Utility Bill History
                  </h2>
                  
                  {/* BUTTON GROUP */}
                  <div className="flex gap-3">
                    <button 
                      onClick={() => setShowExportModal(true)}
                      className="btn-dashboard-outline hidden md:inline-flex items-center px-4 py-2 shadow-sm text-sm font-medium focus:outline-none"
                    >
                      <Download className="-ml-1 mr-2 h-5 w-5" />
                      Export as ZIP
                    </button>

                    <button 
                      onClick={runDuplicateAudit}
                      className="btn-dashboard-outline hidden md:inline-flex items-center px-4 py-2 shadow-sm text-sm font-medium focus:outline-none"
                    >
                      <ScanLine className="-ml-1 mr-2 h-5 w-5" />
                      Check for Duplicates
                    </button>

                    <button 
                      onClick={() => { setEditingItem(null); setActiveTab('scan'); }}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 dark:shadow-[0_0_12px_rgba(14,165,233,0.5)] focus:outline-none transition-all"
                    >
                      <UploadCloud className="-ml-1 mr-2 h-5 w-5" />
                      Upload New Bill
                    </button>
                  </div>
               </div>
               
               {isLoading ? (
                 <div className="flex justify-center p-12 text-gray-400 dark:text-gray-500">
                   <Loader2 className="h-8 w-8 animate-spin" />
                 </div>
               ) : (
                 <BillList 
                   bills={bills} 
                   onDeleteRequest={setDeleteItem} 
                   onEditRequest={startEdit}
                   onArchiveRequest={archiveBill}
                   onBulkDelete={handleBulkDelete}
                   onBulkAutoCorrect={handleBulkAutoCorrect}
                   onSecureView={handleSecureView}
                   darkMode={darkMode}
                   isLoading={isLoading}
                   fetchBills={fetchBills}
                   allFacilities={allFacilities}
                   ignoredCorrections={ignoredCorrections}
                   setIgnoredCorrections={setIgnoredCorrections}
                   onShowIgnoredRulesManager={() => setShowIgnoredRulesManager(true)}
                   refreshKey={billListRefreshKey}
                   sessionTracker={sessionTracker}
                 />
               )}
            </div>
        )}

        {activeTab === 'archive' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Archived Bills
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                View and restore archived bills, or permanently delete them.
              </p>
            </div>
            <ArchiveViewer
              bills={bills}
              setBills={setBills}
              currentUser={user}
              darkMode={darkMode}
              setShowToast={setShowToast}
              setToastMessage={setToastMessage}
              rateLimiter={rateLimiter}
              refreshToken={archiveRefreshNonce}
              sessionTracker={sessionTracker}
              onSecureView={handleSecureView}
            />
          </div>
        )}
      </main>

      {/* --- MODALS --- */}
      
      {/* 1. Delete Confirmation (Single) */}
      {deleteItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4 transition-colors duration-200 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className="text-lg font-bold text-red-600 flex gap-2"><AlertTriangle /> Delete Entry?</h3>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-900'}>Delete bill for <strong>{deleteItem.site_name}</strong>? This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setDeleteItem(null)} 
                className={`px-4 py-2 border rounded transition-colors ${
                  darkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete} 
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1b. Delete Confirmation (Bulk) */}
      {bulkDeleteIds && bulkDeleteIds.length > 0 && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4 transition-colors duration-200 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className="text-lg font-bold text-red-600 flex gap-2"><AlertTriangle /> Delete {bulkDeleteIds.length} Entries?</h3>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-900'}>You are about to permanently delete <strong>{bulkDeleteIds.length}</strong> selected bill{bulkDeleteIds.length > 1 ? 's' : ''}. This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setBulkDeleteIds(null)} 
                className={`px-4 py-2 border rounded transition-colors ${
                  darkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button 
                onClick={confirmBulkDelete} 
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Delete {bulkDeleteIds.length} Bill{bulkDeleteIds.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. PDF Viewer */}
      {viewingPdfUrl && (
        <PdfViewer fileUrl={viewingPdfUrl} onClose={() => {
            setViewingPdfUrl(null);
            URL.revokeObjectURL(viewingPdfUrl);
        }} darkMode={darkMode} />
      )}

      {/* 3. Export Bills Modal */}
      {showExportModal && (
        <ExportBillsModal 
          bills={bills}
          onClose={() => { if (!zipExportProgress) setShowExportModal(false); }}
          onExport={handleExportBills}
          darkMode={darkMode}
          allFacilities={allFacilities}
          exportProgress={zipExportProgress}
        />
      )}

      {/* 4. Enhanced Duplicate Audit Results */}
      {duplicateGroups && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col transition-colors duration-200 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`p-6 border-b flex justify-between items-center transition-colors duration-200 ${
              darkMode
                ? 'bg-red-900/30 border-red-800'
                : 'bg-red-50 border-red-200'
            }`}>
              <div>
                <h3 className="text-lg font-bold text-red-600">Potential Duplicates Found ({duplicateGroups.length})</h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Review bills with matching facility, date, utility type, provider, and amount
                </p>
              </div>
              <button 
                onClick={() => setDuplicateGroups(null)}
                className={`p-1 rounded transition-colors ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
              >
                <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {duplicateGroups.map((group, idx) => {
                // Use stored values from fingerprint document (not parsing the encoded fingerprint)
                const { facility, date, type: utilityType, provider, amount } = group;
                return (
                  <div key={idx} className={`border rounded-lg transition-colors duration-200 ${
                    darkMode
                      ? 'bg-gray-700/50 border-gray-600'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`p-4 border-b ${
                      darkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-white/50'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className={`font-bold text-lg ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            {facility || group.bills[0]?.site_name || 'Unknown Facility'}
                          </div>
                          <div className={`text-sm mt-1 space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <div><strong>Matching Criteria:</strong></div>
                            <div className="flex gap-4 flex-wrap">
                              <span>📅 Date: <strong>{date || 'N/A'}</strong></span>
                              <span>⚡ Type: <strong className="capitalize">{utilityType || 'N/A'}</strong></span>
                              <span>🏢 Provider: <strong>{provider || 'N/A'}</strong></span>
                              <span>💰 Amount: <strong>${amount || '0.00'}</strong></span>
                              <span>📍 Facility: <strong>{facility || group.bills[0]?.site_name || 'N/A'}</strong></span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => ignoreDuplicateGroup(group.fingerprint)}
                          className={`px-3 py-1.5 text-sm rounded transition-colors ${
                            darkMode
                              ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Not a Duplicate - Ignore
                        </button>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Found {group.bills.length} entries:
                      </div>
                      {group.bills.map((bill) => (
                        <div key={bill.id} className={`p-3 border rounded transition-colors duration-200 ${
                          darkMode
                            ? 'bg-gray-800 border-gray-600'
                            : 'bg-white border-gray-300'
                        }`}>
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                              <div>
                                <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type: </span>
                                <span className={`capitalize ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{bill.type}</span>
                              </div>
                              <div>
                                <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Provider: </span>
                                <span className={darkMode ? 'text-gray-200' : 'text-gray-900'}>{bill.provider || 'Unknown'}</span>
                              </div>
                              <div>
                                <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Usage: </span>
                                <span className={darkMode ? 'text-gray-200' : 'text-gray-900'}>{bill.usage} {bill.unit}</span>
                              </div>
                              <div className="col-span-full">
                                <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Address: </span>
                                <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{bill.site_address || 'N/A'}</span>
                              </div>
                              <div className="col-span-full">
                                <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>ID: </span>
                                <span className={`text-xs font-mono ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{bill.id}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setComparingDuplicate(group.bills)}
                                className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 transition-colors ${
                                  darkMode
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                              >
                                <Eye className="h-3 w-3" />
                                Compare
                              </button>
                              <button
                                onClick={() => archiveDuplicate(bill, group.fingerprint)}
                                className="px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                              >
                                <Trash2 className="h-3 w-3" />
                                Archive
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={`p-4 border-t flex justify-between items-center transition-colors duration-200 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Tip: Use "Compare" to see side-by-side differences
              </div>
              <button 
                onClick={() => setDuplicateGroups(null)} 
                className={`px-4 py-2 rounded transition-colors ${
                  darkMode
                    ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3b. Side-by-Side Duplicate Comparison Modal */}
      {comparingDuplicate && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col transition-colors duration-200 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`p-6 border-b flex justify-between items-center transition-colors duration-200 ${
              darkMode
                ? 'bg-blue-900/30 border-blue-800'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                Side-by-Side Comparison ({comparingDuplicate.length} entries)
              </h3>
              <button 
                onClick={() => setComparingDuplicate(null)}
                className={`p-1 rounded transition-colors ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
              >
                <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {comparingDuplicate.map((bill, idx) => (
                  <div key={bill.id} className={`border-2 rounded-lg p-4 transition-colors duration-200 ${
                    idx === 0 
                      ? darkMode 
                        ? 'border-green-600 bg-green-900/20' 
                        : 'border-green-500 bg-green-50'
                      : darkMode
                        ? 'border-gray-600 bg-gray-700/50'
                        : 'border-gray-300 bg-gray-50'
                  }`}>
                    <div className={`text-sm font-bold mb-3 pb-2 border-b ${
                      idx === 0 ? 'text-green-600' : darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-200'
                    }`}>
                      {idx === 0 ? '✓ Entry #1 (Keep?)' : `Entry #${idx + 1}`}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Facility:</span>
                        <div className={`mt-1 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{bill.site_name}</div>
                      </div>
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Date:</span>
                        <div className={`mt-1 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{bill.date}</div>
                      </div>
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type:</span>
                        <div className={`mt-1 capitalize ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{bill.type}</div>
                      </div>
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                        <div className={`mt-1 text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          ${parseFloat(bill.amount).toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Usage:</span>
                        <div className={`mt-1 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{bill.usage} {bill.unit}</div>
                      </div>
                      <div>
                        <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Address:</span>
                        <div className={`mt-1 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{bill.site_address || 'N/A'}</div>
                      </div>
                      <div className={`pt-2 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                        <span className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Document ID:</span>
                        <div className={`mt-1 text-xs font-mono ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{bill.id}</div>
                      </div>
                      {bill.fileUrl && (
                        <div>
                          <button
                            onClick={() => handleSecureView(bill.fileUrl)}
                            className={`w-full mt-2 px-3 py-1.5 text-xs rounded inline-flex items-center justify-center gap-1 transition-colors ${
                              darkMode
                                ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            <Eye className="h-3 w-3" />
                            View PDF
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`p-4 border-t transition-colors duration-200 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Compare the details above. Differences in usage, address, or other fields may indicate these are not true duplicates.
              </div>
              <div className="flex justify-end">
                <button 
                  onClick={() => setComparingDuplicate(null)} 
                  className={`px-4 py-2 rounded transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  Close Comparison
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. Auto-Correct Preview Modal */}
      {autoCorrectPreview && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col transition-colors duration-200 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`p-6 border-b flex justify-between items-center transition-colors duration-200 ${
              darkMode
                ? 'bg-blue-900/30 border-blue-800'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div>
                <h3 className={`text-lg font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  Location Auto-Correct Preview
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Review suggestions, select approved locations, or ignore items
                </p>
              </div>
              <button 
                onClick={() => setAutoCorrectPreview(null)}
                className={`p-1 rounded transition-colors ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
              >
                <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {autoCorrectPreview.map((change, idx) => (
                  <div key={idx} className={`border rounded-lg transition-all duration-200 ${
                    change.ignored
                      ? darkMode
                        ? 'bg-gray-800/50 border-gray-700 opacity-50'
                        : 'bg-gray-100 border-gray-300 opacity-60'
                      : darkMode
                        ? 'bg-gray-700/50 border-gray-600'
                        : 'bg-white border-gray-200 shadow-sm'
                  }`}>
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Ignore Checkbox */}
                        <div className="pt-1">
                          <input
                            type="checkbox"
                            checked={change.ignored}
                            onChange={() => toggleIgnoreCorrection(idx)}
                            className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-2 focus:ring-red-500"
                            title={change.ignored ? "Include this change" : "Ignore this change"}
                          />
                        </div>
                        
                        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {/* Current Name */}
                          <div>
                            <div className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Current Name
                            </div>
                            <div className={`font-medium px-3 py-2 rounded ${
                              darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-700'
                            }`}>
                              {change.currentName}
                            </div>
                          </div>
                          
                          {/* Arrow */}
                          <div className="hidden lg:flex items-center justify-center">
                            <ArrowUpDown className={`h-5 w-5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                          </div>
                          
                          {/* Select Approved Location */}
                          <div>
                            <div className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Select Approved Location
                            </div>
                            <select
                              value={change.selectedName}
                              onChange={(e) => updateAutoCorrectSelection(idx, e.target.value)}
                              disabled={change.ignored}
                              className={`w-full px-3 py-2 rounded border font-medium transition-colors ${
                                change.ignored
                                  ? 'cursor-not-allowed opacity-50'
                                  : ''
                              } ${
                                darkMode
                                  ? 'bg-gray-700 border-gray-600 text-green-400 focus:ring-2 focus:ring-green-500'
                                  : 'bg-green-50 border-green-200 text-green-700 focus:ring-2 focus:ring-green-500'
                              }`}
                            >
                              {approvedLocations.map(loc => (
                                <option key={loc.id} value={loc.name}>
                                  {loc.name}
                                </option>
                              ))}
                            </select>
                            {change.selectedName !== change.autoSuggested && (
                              <div className={`text-xs mt-1 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                ⚠️ Modified from suggestion: {change.autoSuggested}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Details and Actions */}
                      <div className={`mt-3 pt-3 border-t flex flex-col gap-3 text-xs ${
                        darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-600'
                      }`}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <div><strong>Address:</strong> {change.address}</div>
                          <div><strong>Date:</strong> {change.date}</div>
                          <div><strong>Type:</strong> {change.type}</div>
                        </div>
                        
                        {markedIncorrect[idx] && (
                          <div className={`px-3 py-2 rounded ${
                            darkMode ? 'bg-orange-900/30 border border-orange-700' : 'bg-orange-50 border border-orange-200'
                          }`}>
                            <strong>⚠️ Marked as Incorrect:</strong> This suggestion will be saved as a rule to prevent future false suggestions.
                          </div>
                        )}
                        
                        {!change.ignored && !markedIncorrect[idx] && (
                          <button
                            onClick={() => setMarkedIncorrect(prev => ({
                              ...prev,
                              [idx]: true
                            }))}
                            className={`px-3 py-1 text-xs rounded border text-left transition-colors w-full ${
                              darkMode
                                ? 'border-orange-700 text-orange-400 hover:bg-orange-900/20'
                                : 'border-orange-300 text-orange-600 hover:bg-orange-50'
                            }`}
                          >
                            💾 Mark as Incorrect (save rule to prevent this suggestion)
                          </button>
                        )}
                        
                        {markedIncorrect[idx] && (
                          <button
                            onClick={() => setMarkedIncorrect(prev => ({
                              ...prev,
                              [idx]: false
                            }))}
                            className={`px-3 py-1 text-xs rounded border transition-colors w-full ${
                              darkMode
                                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Undo Mark as Incorrect
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {change.ignored && (
                      <div className={`px-4 py-2 text-xs font-medium border-t ${
                        darkMode
                          ? 'bg-gray-800 border-gray-700 text-gray-500'
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      }`}>
                        🚫 This change will be ignored
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className={`p-4 border-t flex justify-between items-center transition-colors duration-200 ${
              darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {autoCorrectPreview.filter(c => !c.ignored).length} of {autoCorrectPreview.length} changes will be applied
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={() => setAutoCorrectPreview(null)} 
                  className={`px-4 py-2 border rounded transition-colors ${
                    darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button 
                  onClick={applyAutoCorrections} 
                  disabled={autoCorrectPreview.filter(c => !c.ignored).length === 0}
                  className="px-4 py-2 bg-[#0950A0] text-white rounded hover:bg-[#0950A0]/90 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Apply {autoCorrectPreview.filter(c => !c.ignored).length} Corrections
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. Saving Overlay */}
      {isSaving && (
        <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className={`p-8 rounded-xl shadow-xl flex flex-col items-center gap-4 transition-colors duration-200 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <Loader2 className="h-10 w-10 text-[#0950A0] animate-spin" />
            <h3 className={`font-bold ${
              darkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Saving Records...
            </h3>
          </div>
        </div>
      )}

      {/* 6. Location Manager */}
      {showLocationManager && (
        <LocationManager onClose={() => { setShowLocationManager(false); localStorage.removeItem('utilitrack_approved_locations_cache'); localStorage.removeItem('utilitrack_approved_locations_ts'); loadApprovedLocations(true); }} darkMode={darkMode} monthlyStats={monthlyStats} onRebuildComplete={() => { fetchMonthlyStats(true); setBillListRefreshKey(k => k + 1); localStorage.removeItem('utilitrack_approved_locations_cache'); localStorage.removeItem('utilitrack_approved_locations_ts'); loadApprovedLocations(true); }} />
      )}

      {/* 7. AI Utility Anomaly Analysis */}
      {showAnomalyAnalysis && (
        <UtilityAnomalyAnalysis
          isOpen={showAnomalyAnalysis}
          onClose={() => setShowAnomalyAnalysis(false)}
          darkMode={darkMode}
          approvedLocations={approvedLocations}
          monthlyStats={monthlyStats}
          bills={bills}
          sessionTracker={sessionTracker}
          appSettings={appSettings}
        />
      )}

      {/* 7b. Enterprise Analytics */}
      {showEnterpriseAnalytics && (
        <EnterpriseAnalytics
          isOpen={showEnterpriseAnalytics}
          onClose={() => setShowEnterpriseAnalytics(false)}
          darkMode={darkMode}
        />
      )}

      {/* 8. Ignored Corrections Manager */}
      {showIgnoredRulesManager && (
        <IgnoredCorrectionsManager 
          onClose={() => setShowIgnoredRulesManager(false)} 
          darkMode={darkMode}
        />
      )}

      {/* 9. Toast Notification */}
      {showToast && toastMessage && (
        <div className="fixed bottom-20 md:bottom-4 right-4 z-[300] animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className={`px-6 py-3 rounded-lg shadow-lg text-sm font-medium ${
            darkMode
              ? 'bg-gray-800 text-gray-100 border border-gray-700'
              : 'bg-gray-900 text-white border border-gray-800'
          }`}>
            {toastMessage}
          </div>
        </div>
      )}

      {/* 10. Version Footer */}
      <footer className={`border-t py-3 px-4 text-center text-xs transition-colors duration-200 ${
        darkMode
          ? 'border-gray-800 text-gray-600'
          : 'border-gray-200 text-gray-400'
      }`}>
        UtiliTrack AI v{APP_VERSION}
      </footer>

    </div>
  );
}
